import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  ArrowLeft, Save, Eye, Undo, Redo, Monitor, Tablet, Smartphone,
  Settings, ChevronDown, ChevronUp, X, Check, Plus, Home, FileText,
  Trash2, Rocket, ExternalLink, Copy, Edit2, GripVertical, EyeOff,
  ShoppingBag, Scale, Newspaper, AlertCircle, FolderOpen, ShoppingCart,
  Calendar, Mail, Sparkles, Navigation, Palette, Type, Image,
  Globe, Layout, Layers, Play, Music, MessageSquare, Star, Clock,
  Code, Map, Search, CreditCard, UserPlus, User, Heart, Share2, LayoutDashboard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { fallbackEditorSurfaces, isPanelVisible, operateUrl, crmUrl, ensureCrmInEditorSurfaces } from '../lib/editorSurfaces';
import { applyWidgetBinding, fallbackWidgetBindings, OPERATE_EMPTY_GRID_MSG } from '../lib/widgetBindings';
import {
  applyWidgetInstancesToHtml,
  deactivateWidgetInstance,
  enhanceEditorVideoPreview,
  repairStoredVideoSections,
  extractInstanceIdFromHtml,
  extractGalleryItemsFromDom,
  fetchWidgetInstances,
  findGalleryItemIndex,
  findSliderImageIndex,
  inferWidgetTypeFromSectionHtml,
  isConfigWidget,
  patchGalleryItemImage,
  patchSliderSlideImage,
  registerWidgetInstance,
  syncWidgetInstancesFromPages,
  tagWidgetInstanceHtml,
  patchWidgetInstanceConfig,
  parseWidgetInstanceConfig,
  widgetDashboardUrl,
  widgetInstanceFromSection,
} from '../lib/widgetInstances';
import snagcartCheckoutRuntime from '../lib/snagcartCheckoutRuntime';
import orderConfirmationRuntime from '../lib/orderConfirmationRuntime';
import siteAuthRuntime from '../lib/siteAuthRuntime';
import AIPageAssistant from './TemplateEditorAIPageAssistant';
import SiteBrainPanel from './TemplateEditorSiteBrainPanel';
import ClassSchedulePanel from './ClassSchedulePanel';
import { parseHtmlStructure, parseHtmlToSections, sectionsToHtml } from '../utils/parseTemplateSections';
import { resolveApiBase } from '../lib/http';

// ==================== CONFIGURATION ====================

// Genre dashboard pages are full standalone HTML documents — never wrap them
// in the marketing preview shell (causes nested UI / broken layout).
const STANDALONE_DASHBOARD_SLUGS = new Set(['admin-dashboard', 'my-account', 'sign-in']);

const ECOMMERCE_UTILITY_PAGE_SLUGS = new Set([
  'product-detail', 'account', 'sign-in', 'my-account', 'admin-dashboard',
]);

/** Left panel + header nav — same slugs and labels everywhere. */
const ECOMMERCE_NAV_ITEMS = [
  { slug: 'home', label: 'Home', fallbacks: [] },
  { slug: 'products', label: 'Shop', fallbacks: [] },
  { slug: 'sign-in', label: 'Register', fallbacks: ['account'] },
  { slug: 'cart', label: 'Cart', fallbacks: [] },
];

const ECOMMERCE_STORE_FLOW_SLUGS = new Set([
  'checkout', 'order-confirmation', 'product-detail', 'orders',
]);

const resolvePageBySlugs = (pages, slugs) => {
  for (const slug of slugs) {
    const match = pages.find((p) => p.slug === slug);
    if (match) return match;
  }
  return null;
};

const normalizeEcommerceNavSlug = (slug) => (slug === 'account' ? 'sign-in' : slug);

const buildCanonicalEcommerceNavLinks = (currentSlug = 'home', pages = []) => {
  const activeSlug = normalizeEcommerceNavSlug(currentSlug || 'home');
  const coreSlugs = new Set(ECOMMERCE_NAV_ITEMS.map(({ slug }) => slug));
  const coreLinks = ECOMMERCE_NAV_ITEMS
    .filter(({ slug }) => slug !== 'sign-in' || shouldShowRegisterNav(pages))
    .map(({ slug, label }) => {
      const active = activeSlug === slug ? ' class="is-active"' : '';
      return `<a href="?page=${slug}" data-page="${slug}" data-editor-page="${slug}"${active}>${label}</a>`;
    });
  // Insert merchant-opted pages (incl. legal/SEO) before Cart / Register.
  const extras = (pages || [])
    .filter((p) => {
      const slug = p?.slug;
      if (!slug || coreSlugs.has(slug) || ECOMMERCE_UTILITY_PAGE_SLUGS.has(slug)) return false;
      if (p.nav_visible !== true) return false;
      if (p.resource) return false;
      const cat = p.category || 'main';
      if (['system', 'admin', 'ecommerce', 'account'].includes(cat)) return false;
      return true;
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((page) => {
      const slug = page.slug;
      const label = page.name || page.title || slug;
      const active = activeSlug === slug ? ' class="is-active"' : '';
      return `<a href="?page=${slug}" data-page="${slug}" data-editor-page="${slug}"${active}>${label}</a>`;
    });
  const withoutTrailing = coreLinks.filter((html) => !html.includes('data-page="cart"') && !html.includes('data-page="sign-in"'));
  const trailing = coreLinks.filter((html) => html.includes('data-page="cart"') || html.includes('data-page="sign-in"'));
  return [...withoutTrailing, ...extras, ...trailing].join('');
};

const shouldShowRegisterNav = (pages = []) => {
  const signIn = pages.find((p) => ['sign-in', 'account'].includes(p.slug));
  return Boolean(signIn && signIn.nav_visible === true);
};

const slugifyWebsiteName = (value = '') => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const extractHomeHeaderHtml = (pageList = []) => {
  const homePage = pageList.find((p) => p.is_home || p.slug === 'home');
  const html = homePage?.html_code || '';
  const match = html.match(/<header[^>]*data-section-name=["']Header["'][^>]*>[\s\S]*?<\/header>/i);
  return match ? match[0] : '';
};

function prepareStandaloneDashboardPreview(rawHtml, websiteId) {
  if (!rawHtml || !String(rawHtml).trim()) {
    return '<!DOCTYPE html><html><body style="font-family:Inter,sans-serif;padding:40px;color:#64748b;"><p>Operator dashboard has not been generated for this site yet. Use <strong>Open operator dashboard</strong> or regenerate genre dashboards.</p></body></html>';
  }

  let html = stripPrependedMarketingShell(String(rawHtml));
  html = html.replace("window.location.href = '?page=account';", '/* preview: auth redirect disabled */');
  html = html.replace("window.location.href = '?page=sign-in';", '/* preview: auth redirect disabled */');
  html = html.replace("window.location.href = '?page=home';", '/* preview: logout redirect disabled */');

  const operatorPatch = `
<script>
(function() {
  var platformToken = localStorage.getItem('token');
  if (platformToken && typeof DASH_TOKEN !== 'undefined') DASH_TOKEN = platformToken;
  if (typeof checkDashAuth === 'function') {
    checkDashAuth = function() { return Promise.resolve({ role: 'admin', name: 'Site Owner' }); };
  }
})();
</script>`;

  if (html.includes('</body>')) {
    return html.replace('</body>', `${operatorPatch}\n</body>`);
  }
  return html + operatorPatch;
}

// Currencies supported
const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
];

// Font families
const FONT_FAMILIES = [
  { name: 'Inter', value: 'Inter' },
  { name: 'Poppins', value: 'Poppins' },
  { name: 'Roboto', value: 'Roboto' },
  { name: 'Open Sans', value: 'Open Sans' },
  { name: 'Montserrat', value: 'Montserrat' },
  { name: 'Playfair Display', value: 'Playfair Display' },
  { name: 'Lato', value: 'Lato' },
  { name: 'Nunito', value: 'Nunito' },
  { name: 'Raleway', value: 'Raleway' },
  { name: 'Source Sans Pro', value: 'Source Sans Pro' },
];

// Banner images library
const BANNER_IMAGES = [
  { id: 'tech1', url: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920', category: 'Technology' },
  { id: 'tech2', url: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920', category: 'Technology' },
  { id: 'nature1', url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920', category: 'Nature' },
  { id: 'nature2', url: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1920', category: 'Nature' },
  { id: 'business1', url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920', category: 'Business' },
  { id: 'business2', url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920', category: 'Business' },
  { id: 'food1', url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1920', category: 'Food' },
  { id: 'food2', url: 'https://images.unsplash.com/photo-1493770348161-369560ae357d?w=1920', category: 'Food' },
  { id: 'fashion1', url: 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=1920', category: 'Fashion' },
  { id: 'fashion2', url: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=1920', category: 'Fashion' },
  { id: 'photography1', url: 'https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=1920', category: 'Photography' },
  { id: 'photography2', url: 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=1920', category: 'Photography' },
  { id: 'travel1', url: 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=1920', category: 'Travel' },
  { id: 'travel2', url: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1920', category: 'Travel' },
  { id: 'fitness1', url: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920', category: 'Fitness' },
  { id: 'abstract1', url: 'https://images.unsplash.com/photo-1557683316-973673baf926?w=1920', category: 'Abstract' },
  { id: 'abstract2', url: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1920', category: 'Abstract' },
  { id: 'minimal1', url: 'https://images.unsplash.com/photo-1494438639946-1ebd1d20bf85?w=1920', category: 'Minimal' },
];

// Page categories
const PAGE_CATEGORIES = [
  { id: 'main', name: 'Pages', icon: FolderOpen, color: 'text-violet-400' },
  { id: 'ecommerce', name: 'Store', icon: ShoppingCart, color: 'text-emerald-400' },
  { id: 'blog', name: 'Blog', icon: Newspaper, color: 'text-blue-400' },
  { id: 'legal', name: 'Legal', icon: Scale, color: 'text-amber-400' },
  { id: 'account', name: 'Account', icon: User, color: 'text-indigo-400' },
  { id: 'admin', name: 'Operator', icon: LayoutDashboard, color: 'text-rose-400' },
  { id: 'system', name: 'System Pages', icon: AlertCircle, color: 'text-zinc-400' },
];

// ==================== COMPREHENSIVE WIDGET LIBRARY ====================

const WIDGET_CATEGORIES = [
  { id: 'basic', name: 'Basic', icon: '📦' },
  { id: 'media', name: 'Media', icon: '🖼️' },
  { id: 'lead', name: 'Lead Generation', icon: '📋' },
  { id: 'sales', name: 'Sales', icon: '💰' },
  { id: 'community', name: 'Community', icon: '👥' },
  { id: 'advanced', name: 'Advanced', icon: '🔧' },
  { id: 'other', name: 'Other', icon: '🔧' },
];

const generateId = () => Math.random().toString(36).substring(2, 9);

// Complete widget library with all requested widgets
const ALL_WIDGETS = [
  // ==================== BASIC WIDGETS ====================
  {
    id: 'hero-gradient',
    name: 'Hero Gradient',
    icon: '🌟',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section widget-hero" data-section-id="hero-grad-{{ID}}" data-section-name="Hero" data-show-nav="false" style="background: linear-gradient(-45deg, #667eea, #764ba2, #f093fb, #f5576c); background-size: 400% 400%; animation: gradientShift 15s ease infinite; padding: 120px 20px; text-align: center; color: white; position: relative; overflow: hidden;">
  <div style="max-width: 900px; margin: 0 auto; position: relative; z-index: 1;">
    <span style="display: inline-block; padding: 8px 20px; background: rgba(255,255,255,0.2); border-radius: 30px; font-size: 0.9rem; margin-bottom: 20px; backdrop-filter: blur(10px);">✨ Welcome to the Future</span>
    <h1 style="font-size: 3.5rem; font-weight: 800; margin-bottom: 25px; line-height: 1.2; text-shadow: 0 2px 40px rgba(0,0,0,0.3);">Transform Your Ideas Into Reality</h1>
    <p style="font-size: 1.3rem; margin-bottom: 40px; opacity: 0.95; max-width: 600px; margin-left: auto; margin-right: auto;">Build stunning websites, grow your business, and achieve your dreams with our powerful platform.</p>
    <div style="display: flex; gap: 15px; justify-content: center; flex-wrap: wrap;">
      <a href="#" style="padding: 18px 40px; background: white; color: #764ba2; border-radius: 50px; text-decoration: none; font-weight: 700; box-shadow: 0 10px 30px rgba(0,0,0,0.2);">Get Started Free</a>
      <a href="#" style="padding: 18px 40px; border: 2px solid white; color: white; border-radius: 50px; text-decoration: none; font-weight: 600; backdrop-filter: blur(10px);">Watch Demo →</a>
    </div>
  </div>
</section>`
  },
  {
    id: 'hero-image-bg',
    name: 'Hero with Image',
    icon: '🖼️',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section widget-hero" data-section-id="hero-img-{{ID}}" data-section-name="Hero" data-show-nav="false" style="min-height: 600px; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden;">
  <div style="position: absolute; inset: 0; z-index: 0;">
    <img src="https://images.unsplash.com/photo-1557683316-973673baf926?w=1920" style="width: 100%; height: 100%; object-fit: cover;" alt="Hero Background">
  </div>
  <div style="position: absolute; inset: 0; background: rgba(0,0,0,0.5); z-index: 1;"></div>
  <div style="position: relative; z-index: 2; text-align: center; color: white; max-width: 800px; padding: 40px;">
    <h1 style="font-size: 4rem; font-weight: 800; margin-bottom: 25px; text-shadow: 0 4px 30px rgba(0,0,0,0.3);">Welcome to Our Site</h1>
    <p style="font-size: 1.3rem; margin-bottom: 40px; opacity: 0.9;">Create amazing experiences with our powerful platform</p>
    <div style="display: flex; gap: 15px; justify-content: center;">
      <a href="#" style="padding: 18px 40px; background: var(--primary-color, #8b5cf6); color: white; border-radius: 50px; text-decoration: none; font-weight: 700;">Get Started</a>
      <a href="#" style="padding: 18px 40px; border: 2px solid white; color: white; border-radius: 50px; text-decoration: none; font-weight: 600;">Learn More</a>
    </div>
  </div>
</section>`
  },
  {
    id: 'hero-split',
    name: 'Hero Split Layout',
    icon: '📐',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="hero-split-{{ID}}" data-section-name="Hero" data-show-nav="false" style="display: grid; grid-template-columns: 1fr 1fr; min-height: 90vh; align-items: center;">
  <div style="padding: 60px; background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);">
    <span style="display: inline-block; padding: 6px 16px; background: var(--primary-color, #8b5cf6); color: white; border-radius: 20px; font-size: 0.85rem; font-weight: 600; margin-bottom: 20px;">🚀 New Release</span>
    <h1 style="font-size: 3.2rem; font-weight: 800; color: #1e293b; margin-bottom: 25px; line-height: 1.1;">Build Something Amazing Today</h1>
    <p style="font-size: 1.2rem; color: #64748b; margin-bottom: 35px; line-height: 1.7;">Join thousands of entrepreneurs who have transformed their businesses with our cutting-edge platform.</p>
    <div style="display: flex; gap: 15px; align-items: center; margin-bottom: 40px;">
      <a href="#" style="padding: 16px 35px; background: var(--primary-color, #8b5cf6); color: white; border-radius: 12px; text-decoration: none; font-weight: 600; box-shadow: 0 4px 15px rgba(139,92,246,0.4);">Start Building</a>
    </div>
  </div>
  <div style="height: 100%; position: relative; overflow: hidden;">
    <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800" style="width: 100%; height: 100%; object-fit: cover;" alt="Hero Image">
    <div style="position: absolute; inset: 0; background: linear-gradient(135deg, rgba(139,92,246,0.2), rgba(16,185,129,0.2));"></div>
  </div>
</section>`
  },
  {
    id: 'heading',
    name: 'Heading',
    icon: 'H',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="heading-{{ID}}" data-section-name="Heading" data-show-nav="false" style="padding: 40px 20px; text-align: center;">
  <h1 style="font-size: 3rem; font-weight: 800; color: var(--primary-color, #1e293b); margin: 0;">Your Heading Here</h1>
</section>`
  },
  {
    id: 'text',
    name: 'Text',
    icon: 'T',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="text-{{ID}}" data-section-name="Text Block" data-show-nav="false" style="padding: 40px 20px; max-width: 800px; margin: 0 auto;">
  <p style="font-size: 1.1rem; line-height: 1.8; color: #475569;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.</p>
</section>`
  },
  {
    id: 'button',
    name: 'Button',
    icon: '🔘',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="button-{{ID}}" data-section-name="Button" data-show-nav="false" style="padding: 30px 20px; text-align: center;">
  <a href="#" style="display: inline-block; padding: 16px 40px; background: var(--primary-color, #8b5cf6); color: white; text-decoration: none; border-radius: 50px; font-weight: 600; font-size: 1rem; transition: all 0.3s; box-shadow: 0 4px 15px rgba(139,92,246,0.3);">Click Here</a>
</section>`
  },
  {
    id: 'container',
    name: 'Container',
    icon: '📦',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="container-{{ID}}" data-section-name="Container" data-show-nav="false" style="padding: 60px 20px;">
  <div style="max-width: 1200px; margin: 0 auto; background: #f8fafc; border-radius: 20px; padding: 40px; border: 1px solid #e2e8f0;">
    <h2 style="font-size: 1.5rem; font-weight: 700; margin-bottom: 15px; color: #1e293b;">Container Section</h2>
    <p style="color: #64748b;">Add your content here. This container provides a structured layout for your content.</p>
  </div>
</section>`
  },
  {
    id: 'icon',
    name: 'Icon',
    icon: '⭐',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="icon-{{ID}}" data-section-name="Icon" data-show-nav="false" style="padding: 30px 20px; text-align: center;">
  <div style="width: 80px; height: 80px; background: linear-gradient(135deg, var(--primary-color, #8b5cf6), #a855f7); border-radius: 20px; display: flex; align-items: center; justify-content: center; margin: 0 auto; box-shadow: 0 10px 30px rgba(139,92,246,0.3);">
    <span style="font-size: 2.5rem;">⭐</span>
  </div>
</section>`
  },
  {
    id: 'icon-section',
    name: 'Icon Section',
    icon: '🎯',
    category: 'basic',
    showInNav: true,
    html: `<section class="widget-section" data-operate-widget="icon-section" data-section-id="icon-section-{{ID}}" data-section-name="Features" data-show-nav="true" style="padding: 80px 20px; background: #f8fafc;">
  <div style="max-width: 1200px; margin: 0 auto; text-align: center;">
    <h2 style="font-size: 2.5rem; font-weight: 800; margin-bottom: 50px; color: #1e293b;">Our Features</h2>
    <div data-widget-content-grid style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px;"></div>
  </div>
</section>`
  },
  {
    id: 'auth-login-signup',
    name: 'Auth: Login & Signup',
    icon: '🔐',
    category: 'advanced',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="auth-{{ID}}" data-section-name="Auth" data-show-nav="false" style="padding: 80px 20px; background: #0f172a; color: white;">
  <div style="max-width: 900px; margin: 0 auto; display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 40px; align-items: center;">
    <div>
      <h2 style="font-size: 2.5rem; font-weight: 800; margin-bottom: 16px;">Sign in to your account</h2>
      <p style="color: #9ca3af; margin-bottom: 24px;">Access your saved bookings, orders, and personalized dashboard.</p>
      <ul style="list-style: none; padding: 0; margin: 0; color: #9ca3af; font-size: 0.95rem;">
        <li style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">✅ Secure login</li>
        <li style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">✅ Manage your data in one place</li>
        <li style="display: flex; align-items: center; gap: 8px;">✅ Admin & member roles supported</li>
      </ul>
    </div>
    <div style="background: rgba(15,23,42,0.9); border-radius: 24px; padding: 30px; border: 1px solid rgba(148,163,184,0.4); box-shadow: 0 18px 60px rgba(15,23,42,0.7);">
      <div style="display: flex; margin-bottom: 24px; background: rgba(15,23,42,0.9); border-radius: 999px; padding: 4px; border: 1px solid rgba(148,163,184,0.4);">
        <button type="button" data-auth-toggle="login" style="flex:1; padding: 8px 0; border-radius: 999px; border: none; background: white; color: #0f172a; font-size: 0.85rem; font-weight: 600; cursor: pointer;">Sign in</button>
        <button type="button" data-auth-toggle="signup" style="flex:1; padding: 8px 0; border-radius: 999px; border: none; background: transparent; color: #e5e7eb; font-size: 0.85rem; font-weight: 500; cursor: pointer;">Create account</button>
      </div>
      <form data-site-auth-form="login" style="display: block;">
        <div style="margin-bottom: 16px;">
          <label style="display: block; font-size: 0.85rem; font-weight: 600; margin-bottom: 6px;">Email</label>
          <input type="email" name="email" required style="width: 100%; padding: 10px 12px; border-radius: 10px; border: 1px solid #4b5563; background: #020617; color: white; font-size: 0.9rem;" />
        </div>
        <div style="margin-bottom: 8px;">
          <label style="display: block; font-size: 0.85rem; font-weight: 600; margin-bottom: 6px;">Password</label>
          <input type="password" name="password" required minlength="6" style="width: 100%; padding: 10px 12px; border-radius: 10px; border: 1px solid #4b5563; background: #020617; color: white; font-size: 0.9rem;" />
        </div>
        <button type="submit" style="margin-top: 12px; width: 100%; padding: 10px 14px; border-radius: 999px; border: none; background: var(--primary-color, #6366f1); color: white; font-weight: 600; font-size: 0.95rem; cursor: pointer;">Sign in</button>
        <p style="margin-top: 10px; font-size: 0.8rem; color: #9ca3af;">By signing in you agree to our terms of service and privacy policy.</p>
      </form>
      <form data-site-auth-form="signup" style="display: none;">
        <div style="margin-bottom: 12px;">
          <label style="display: block; font-size: 0.85rem; font-weight: 600; margin-bottom: 6px;">Name</label>
          <input type="text" name="name" required style="width: 100%; padding: 10px 12px; border-radius: 10px; border: 1px solid #4b5563; background: #020617; color: white; font-size: 0.9rem;" />
        </div>
        <div style="margin-bottom: 12px;">
          <label style="display: block; font-size: 0.85rem; font-weight: 600; margin-bottom: 6px;">Email</label>
          <input type="email" name="email" required style="width: 100%; padding: 10px 12px; border-radius: 10px; border: 1px solid #4b5563; background: #020617; color: white; font-size: 0.9rem;" />
        </div>
        <div style="margin-bottom: 8px;">
          <label style="display: block; font-size: 0.85rem; font-weight: 600; margin-bottom: 6px;">Password</label>
          <input type="password" name="password" required minlength="6" style="width: 100%; padding: 10px 12px; border-radius: 10px; border: 1px solid #4b5563; background: #020617; color: white; font-size: 0.9rem;" />
        </div>
        <button type="submit" style="margin-top: 12px; width: 100%; padding: 10px 14px; border-radius: 999px; border: none; background: var(--primary-color, #6366f1); color: white; font-weight: 600; font-size: 0.95rem; cursor: pointer;">Create account</button>
        <p style="margin-top: 10px; font-size: 0.8rem; color: #9ca3af;">We will never share your email with anyone else.</p>
      </form>
      <p data-site-auth-message style="margin-top: 10px; font-size: 0.8rem; color: #f97316;"></p>
    </div>
  </div>
</section>`
  },
  {
    id: 'features-grid',
    name: 'Features Grid',
    icon: '⭐',
    category: 'basic',
    showInNav: true,
    html: `<section class="widget-section" data-operate-widget="features-grid" data-section-id="features-{{ID}}" data-section-name="Features" data-show-nav="true" style="padding: 80px 20px; background: #f8fafc;">
  <div style="max-width: 1200px; margin: 0 auto; text-align: center;">
    <h2 style="font-size: 2.5rem; font-weight: 800; margin-bottom: 50px; color: #1e293b;">Why Choose Us</h2>
    <div data-widget-content-grid style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 40px;"></div>
  </div>
</section>`
  },
  {
    id: 'separator',
    name: 'Separator',
    icon: '➖',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="separator-{{ID}}" data-section-name="Separator" data-show-nav="false" style="padding: 20px;">
  <hr style="border: none; height: 2px; background: linear-gradient(90deg, transparent, #e2e8f0, transparent); max-width: 200px; margin: 0 auto;">
</section>`
  },
  {
    id: 'spacer',
    name: 'Spacer',
    icon: '↕️',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="spacer-{{ID}}" data-section-name="Spacer" data-show-nav="false" style="padding: 40px 20px; min-height: 60px;"></section>`
  },
  {
    id: 'accordion',
    name: 'Accordion',
    icon: '📑',
    category: 'basic',
    showInNav: true,
    html: `<section class="widget-section" data-section-id="accordion-{{ID}}" data-section-name="FAQ" data-show-nav="true" style="padding: 80px 20px; background: white;">
  <div style="max-width: 800px; margin: 0 auto;">
    <h2 style="font-size: 2.5rem; font-weight: 800; text-align: center; margin-bottom: 50px; color: #1e293b;">Frequently Asked Questions</h2>
    <div data-accordion-items style="space-y: 15px;">
      <details style="background: #f8fafc; border-radius: 12px; padding: 20px; margin-bottom: 15px;">
        <summary style="font-weight: 600; cursor: pointer; color: #1e293b;">How do I get started?</summary>
        <p style="margin-top: 15px; color: #64748b; line-height: 1.7;">Simply sign up for an account and follow our easy setup wizard. You'll be up and running in minutes!</p>
      </details>
      <details style="background: #f8fafc; border-radius: 12px; padding: 20px; margin-bottom: 15px;">
        <summary style="font-weight: 600; cursor: pointer; color: #1e293b;">What payment methods do you accept?</summary>
        <p style="margin-top: 15px; color: #64748b; line-height: 1.7;">We accept all major credit cards, PayPal, bank transfers, and multiple local payment methods.</p>
      </details>
      <details style="background: #f8fafc; border-radius: 12px; padding: 20px; margin-bottom: 15px;">
        <summary style="font-weight: 600; cursor: pointer; color: #1e293b;">Can I cancel anytime?</summary>
        <p style="margin-top: 15px; color: #64748b; line-height: 1.7;">Yes! You can cancel your subscription at any time with no hidden fees or penalties.</p>
      </details>
    </div>
  </div>
</section>`
  },

  // ==================== MEDIA WIDGETS ====================
  {
    id: 'image',
    name: 'Image',
    icon: '🖼️',
    category: 'media',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="image-{{ID}}" data-section-name="Image" data-show-nav="false" style="padding: 40px 20px; text-align: center;">
  <img src="https://images.unsplash.com/photo-1557683316-973673baf926?w=1200" alt="Featured Image" style="max-width: 100%; height: auto; border-radius: 20px; box-shadow: 0 20px 60px rgba(0,0,0,0.15);">
</section>`
  },
  {
    id: 'video',
    name: 'Video',
    icon: '🎬',
    category: 'media',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="video-{{ID}}" data-section-name="Video" data-show-nav="false" style="padding: 60px 20px; background: #0f172a;">
  <div style="max-width: 900px; margin: 0 auto; position: relative; aspect-ratio: 16/9; border-radius: 20px; overflow: hidden; box-shadow: 0 25px 80px rgba(0,0,0,0.5);">
    <iframe width="100%" height="100%" src="https://www.youtube.com/embed/dQw4w9WgXcQ" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position: absolute; top: 0; left: 0;"></iframe>
  </div>
</section>`
  },
  {
    id: 'video-playlist',
    name: 'Video Playlist',
    icon: '📺',
    category: 'media',
    showInNav: true,
    html: `<section class="widget-section" data-section-id="playlist-{{ID}}" data-section-name="Video Gallery" data-show-nav="true" style="padding: 80px 20px; background: #f8fafc;">
  <div style="max-width: 1200px; margin: 0 auto;">
    <h2 data-playlist-title style="font-size: 2.5rem; font-weight: 800; text-align: center; margin-bottom: 50px;">Video Gallery</h2>
    <div data-widget-content-grid data-video-playlist-grid style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 25px;"></div>
  </div>
</section>`
  },
  {
    id: 'audio',
    name: 'Audio',
    icon: '🎵',
    category: 'media',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="audio-{{ID}}" data-section-name="Audio Player" data-show-nav="false" style="padding: 60px 20px; background: linear-gradient(135deg, #1e293b, #334155);">
  <div style="max-width: 600px; margin: 0 auto; background: white; border-radius: 20px; padding: 30px; box-shadow: 0 20px 60px rgba(0,0,0,0.3);">
    <div style="display: flex; align-items: center; gap: 20px; margin-bottom: 20px;">
      <div style="width: 80px; height: 80px; background: linear-gradient(135deg, #8b5cf6, #ec4899); border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 2rem;">🎵</div>
      <div><h3 style="font-weight: 700; margin-bottom: 5px;">Track Title</h3><p style="color: #64748b;">Artist Name</p></div>
    </div>
    <audio controls style="width: 100%;"><source src="" type="audio/mpeg">Your browser does not support audio.</audio>
  </div>
</section>`
  },
  {
    id: 'gallery',
    name: 'Gallery',
    icon: '🖼️',
    category: 'media',
    showInNav: true,
    html: `<section class="widget-section" data-section-id="gallery-{{ID}}" data-section-name="Gallery" data-show-nav="true" style="padding: 80px 20px;">
  <div style="max-width: 1200px; margin: 0 auto;">
    <h2 data-gallery-title style="font-size: 2.5rem; font-weight: 800; text-align: center; margin-bottom: 16px;">Photo Gallery</h2>
    <p data-gallery-subtitle data-section-subtitle style="text-align: center; color: #64748b; margin-bottom: 50px; display: none;"></p>
    <div data-widget-content-grid data-gallery-grid style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px;"></div>
  </div>
</section>`
  },
  {
    id: 'slider',
    name: 'Image Slider',
    icon: '🎠',
    category: 'media',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="slider-{{ID}}" data-section-name="Slider" data-show-nav="false" style="position: relative; overflow: hidden; height: 500px;">
  <div data-slider-track data-widget-content-grid style="display: flex; height: 100%; transition: transform 0.5s ease; width: 100%;"></div>
  <button type="button" data-slider-prev aria-label="Previous slide" style="position:absolute;left:16px;top:50%;transform:translateY(-50%);z-index:2;width:44px;height:44px;border-radius:50%;border:none;background:rgba(255,255,255,0.85);cursor:pointer;font-size:1.5rem;line-height:1;">‹</button>
  <button type="button" data-slider-next aria-label="Next slide" style="position:absolute;right:16px;top:50%;transform:translateY(-50%);z-index:2;width:44px;height:44px;border-radius:50%;border:none;background:rgba(255,255,255,0.85);cursor:pointer;font-size:1.5rem;line-height:1;">›</button>
  <div data-slider-dots style="position:absolute;bottom:20px;left:0;right:0;display:flex;justify-content:center;gap:8px;z-index:2;"></div>
</section>`
  },

  // ==================== LEAD GENERATION WIDGETS ====================
  {
    id: 'form',
    name: 'Form',
    icon: '📝',
    category: 'lead',
    showInNav: true,
    html: `<section class="widget-section" data-section-id="form-{{ID}}" data-section-name="Contact" data-show-nav="true" style="padding: 80px 20px; background: linear-gradient(180deg, #f8fafc, #ffffff);">
  <div style="max-width: 600px; margin: 0 auto;">
    <h2 style="font-size: 2.5rem; font-weight: 800; text-align: center; margin-bottom: 40px;">Contact Us</h2>
    <form data-schema-form="true" data-schema-resource="inquiries" style="background: white; padding: 40px; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.08); display: grid; gap: 16px;">
      <input type="text" name="name" placeholder="Your name" required style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
      <input type="email" name="email" placeholder="Email address" required style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
      <input type="text" name="subject" placeholder="Subject" style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
      <textarea name="message" placeholder="Your message" rows="4" required style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem; resize: none;"></textarea>
      <div data-schema-form-message style="font-size: 0.95rem; color: #64748b;"></div>
      <button type="submit" style="width: 100%; padding: 18px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 10px; font-size: 1rem; font-weight: 600; cursor: pointer;">Send Message</button>
    </form>
  </div>
</section>`
  },
  {
    id: 'booking',
    name: 'Booking',
    icon: '📅',
    category: 'lead',
    showInNav: true,
    html: `<section class="widget-section" data-section-id="booking-{{ID}}" data-section-name="Book Now" data-show-nav="true" style="padding: 80px 20px; background: #f8fafc;">
  <div style="max-width: 800px; margin: 0 auto; text-align: center;">
    <h2 style="font-size: 2.5rem; font-weight: 800; margin-bottom: 20px;">Book an Appointment</h2>
    <p style="color: #64748b; margin-bottom: 40px;">Select a date and time that works for you.</p>
    <form data-schema-form="true" data-schema-resource="bookings" style="background: white; padding: 40px; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.08); text-align: left; display: grid; gap: 18px;">
      <div>
        <label style="display: block; font-weight: 600; margin-bottom: 8px;">Your name</label>
        <input type="text" name="guest_name" required style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
      </div>
      <div>
        <label style="display: block; font-weight: 600; margin-bottom: 8px;">Email</label>
        <input type="email" name="email" required style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
      </div>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
        <div>
          <label style="display: block; font-weight: 600; margin-bottom: 8px;">Date & time</label>
          <input type="datetime-local" name="time" required style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
        </div>
        <div>
          <label style="display: block; font-weight: 600; margin-bottom: 8px;">Party size</label>
          <input type="number" name="party_size" min="1" value="1" required style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
        </div>
      </div>
      <div data-schema-form-message style="font-size: 0.95rem; color: #64748b;"></div>
      <button type="submit" style="margin-top: 8px; padding: 18px 50px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 50px; font-size: 1rem; font-weight: 600; cursor: pointer; justify-self: center;">Book Appointment</button>
    </form>
  </div>
</section>`
  },
  {
    id: 'quiz',
    name: 'Quiz/Survey',
    icon: '❓',
    category: 'lead',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="quiz-{{ID}}" data-section-name="Quiz" data-show-nav="false" data-operate-widget="quiz" data-quiz-widget="true" style="padding: 80px 20px; background: linear-gradient(135deg, #667eea, #764ba2); color: white;">
  <div style="max-width: 700px; margin: 0 auto; text-align: center;">
    <h2 data-quiz-title style="font-size: 2.5rem; font-weight: 800; margin-bottom: 20px;">Quick Quiz</h2>
    <p data-quiz-subtitle style="opacity: 0.9; margin-bottom: 40px;">Answer a few questions to get personalized recommendations</p>
    <form data-schema-form="true" data-schema-resource="leads" data-quiz-form="true" style="background: white; color: #1e293b; padding: 40px; border-radius: 20px; display:grid; gap:24px; text-align:left;">
      <div data-quiz-questions>
        <div data-quiz-question-block style="display:grid;gap:12px;">
          <h3 style="font-size:1.1rem;font-weight:700;color:#4f46e5;">Question 1 of 3</h3>
          <p style="font-weight:600;color:#334155;">What best describes your business?</p>
          <div style="display:grid;gap:10px;">
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_1" value="E-commerce / Online Store" required><span style="color:#1e293b;font-weight:600;">E-commerce / Online Store</span></label>
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_1" value="Service Provider"><span style="color:#1e293b;font-weight:600;">Service Provider</span></label>
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_1" value="Portfolio / Personal"><span style="color:#1e293b;font-weight:600;">Portfolio / Personal</span></label>
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_1" value="Blog / Content"><span style="color:#1e293b;font-weight:600;">Blog / Content</span></label>
          </div>
        </div>
        <div data-quiz-question-block style="display:grid;gap:12px;">
          <h3 style="font-size:1.1rem;font-weight:700;color:#4f46e5;">Question 2 of 3</h3>
          <p style="font-weight:600;color:#334155;">What is your main goal?</p>
          <div style="display:grid;gap:10px;">
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_2" value="Get more leads" required><span style="color:#1e293b;font-weight:600;">Get more leads</span></label>
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_2" value="Sell online"><span style="color:#1e293b;font-weight:600;">Sell online</span></label>
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_2" value="Take bookings"><span style="color:#1e293b;font-weight:600;">Take bookings</span></label>
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_2" value="Build trust"><span style="color:#1e293b;font-weight:600;">Build trust</span></label>
          </div>
        </div>
        <div data-quiz-question-block style="display:grid;gap:12px;">
          <h3 style="font-size:1.1rem;font-weight:700;color:#4f46e5;">Question 3 of 3</h3>
          <p style="font-weight:600;color:#334155;">How soon do you want to start?</p>
          <div style="display:grid;gap:10px;">
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_3" value="Today" required><span style="color:#1e293b;font-weight:600;">Today</span></label>
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_3" value="This week"><span style="color:#1e293b;font-weight:600;">This week</span></label>
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_3" value="This month"><span style="color:#1e293b;font-weight:600;">This month</span></label>
            <label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_3" value="Just exploring"><span style="color:#1e293b;font-weight:600;">Just exploring</span></label>
          </div>
        </div>
      </div>
      <input type="text" name="name" placeholder="Your name" style="padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;font-size:1rem;">
      <input type="email" name="email" placeholder="Email address" style="padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;font-size:1rem;">
      <input type="hidden" name="message" data-quiz-message>
      <input type="hidden" name="status" value="new">
      <div data-schema-form-message style="font-size:.95rem;color:#64748b;"></div>
      <button type="submit" data-quiz-submit style="padding:15px;border:0;border-radius:10px;background:var(--primary-color,#8b5cf6);color:white;font-weight:700;cursor:pointer;">Get My Result</button>
    </form>
  </div>
</section>`
  },

  // ==================== SALES & E-COMMERCE WIDGETS ====================
  {
    id: 'featured-products',
    name: 'Featured Products',
    icon: '⭐',
    category: 'sales',
    showInNav: true,
    html: `<section class="widget-section dynamic-products" data-widget="products" data-products data-section-id="featured-{{ID}}" data-section-name="Featured Products" data-show-nav="true" style="padding: 80px 20px; background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);">
  <div style="max-width: 1200px; margin: 0 auto;">
    <div style="text-align: center; margin-bottom: 50px;">
      <span style="display: inline-block; padding: 8px 20px; background: linear-gradient(135deg, #fef3c7, #fde68a); color: #d97706; border-radius: 30px; font-size: 0.85rem; font-weight: 600; margin-bottom: 15px;">⭐ Featured</span>
      <h2 style="font-size: 2.8rem; font-weight: 800; color: #1e293b; margin-bottom: 15px;">Featured Products</h2>
      <p style="color: #64748b; font-size: 1.1rem; max-width: 500px; margin: 0 auto;">Our most popular and best-selling items</p>
    </div>
    <div class="products-grid" style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px;"><!-- Products loaded dynamically --></div>
  </div>
</section>`
  },
  {
    id: 'store',
    name: 'Product Store',
    icon: '🛍️',
    category: 'sales',
    showInNav: true,
    html: `<section class="widget-section dynamic-products" data-widget="products" data-products data-section-id="store-{{ID}}" data-section-name="Products" data-show-nav="true" style="padding: 80px 20px;">
  <div style="max-width: 1200px; margin: 0 auto;">
    <div style="text-align: center; margin-bottom: 50px;">
      <span style="display: inline-block; padding: 8px 20px; background: #f0fdf4; color: #10b981; border-radius: 30px; font-size: 0.85rem; font-weight: 600; margin-bottom: 15px;">🛍️ Shop Now</span>
      <h2 style="font-size: 2.5rem; font-weight: 800; color: #1e293b; margin-bottom: 15px;">Our Products</h2>
      <p style="color: #64748b; max-width: 500px; margin: 0 auto;">Discover our curated collection of premium products</p>
    </div>
    <div class="products-grid" style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px;"><!-- Products loaded dynamically --></div>
  </div>
</section>`
  },
  {
    id: 'shopping-cart',
    name: 'Shopping Cart',
    icon: '🛒',
    category: 'sales',
    showInNav: true,
    html: `<section class="widget-section" data-section-id="cart-widget-{{ID}}" data-section-name="Cart" data-show-nav="true" style="padding: 60px 20px; background: #f8fafc;">
  <div style="max-width: 900px; margin: 0 auto;">
    <h2 style="font-size: 2rem; font-weight: 800; margin-bottom: 30px; display: flex; align-items: center; gap: 15px;">🛒 Shopping Cart</h2>
    <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 30px;">
      <div class="cart-items-display" style="background: white; border-radius: 16px; padding: 25px; box-shadow: 0 4px 15px rgba(0,0,0,0.05);"><p style="color: #94a3b8; text-align: center; padding: 40px;">Your cart is empty. Start shopping!</p></div>
      <div style="background: white; border-radius: 16px; padding: 25px; box-shadow: 0 4px 15px rgba(0,0,0,0.05); height: fit-content;">
        <h3 style="font-weight: 700; margin-bottom: 20px;">Order Summary</h3>
        <div style="margin-bottom: 20px;">
          <div style="display: flex; justify-content: space-between; padding: 10px 0;"><span style="color: #64748b;">Subtotal</span><span class="cart-subtotal-display">$0.00</span></div>
          <div style="display: flex; justify-content: space-between; padding: 10px 0;"><span style="color: #64748b;">Shipping</span><span>$9.99</span></div>
          <div style="display: flex; justify-content: space-between; padding: 15px 0; border-top: 2px solid #e2e8f0; font-weight: 700; font-size: 1.2rem;"><span>Total</span><span class="cart-total-display" style="color: var(--primary-color, #8b5cf6);">$9.99</span></div>
        </div>
        <button type="button" data-snagcart-proceed-checkout style="width: 100%; padding: 16px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 10px; font-weight: 600; cursor: pointer;">Proceed to Checkout</button>
      </div>
    </div>
  </div>
</section>`
  },
  {
    id: 'checkout-form',
    name: 'Checkout Form',
    icon: '💳',
    category: 'sales',
    showInNav: true,
    html: `<section class="widget-section" data-section-id="checkout-{{ID}}" data-section-name="Checkout" data-show-nav="true" style="padding: 60px 20px; background: #f8fafc;">
  <div style="max-width: 1000px; margin: 0 auto;">
    <h2 style="font-size: 2rem; font-weight: 800; margin-bottom: 30px;">Checkout</h2>
    <div style="display: grid; grid-template-columns: 1.5fr 1fr; gap: 30px;">
      <div>
        <div style="background: white; border-radius: 16px; padding: 30px; box-shadow: 0 4px 15px rgba(0,0,0,0.05); margin-bottom: 20px;">
          <h3 style="font-weight: 700; margin-bottom: 20px;">Contact Information</h3>
          <input type="email" placeholder="Email Address" style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 15px; font-size: 1rem;">
          <input type="tel" placeholder="Phone Number" style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
        </div>
        <div style="background: white; border-radius: 16px; padding: 30px; box-shadow: 0 4px 15px rgba(0,0,0,0.05); margin-bottom: 20px;">
          <h3 style="font-weight: 700; margin-bottom: 20px;">Shipping Address</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
            <input type="text" placeholder="First Name" style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
            <input type="text" placeholder="Last Name" style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
          </div>
          <input type="text" placeholder="Address" style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 15px; font-size: 1rem;">
          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px;">
            <input type="text" placeholder="City" style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
            <input type="text" placeholder="State" style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
            <input type="text" placeholder="ZIP Code" style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
          </div>
        </div>
        <div style="background: white; border-radius: 16px; padding: 30px; box-shadow: 0 4px 15px rgba(0,0,0,0.05);">
          <h3 style="font-weight: 700; margin-bottom: 20px;">Payment Method</h3>
          <div style="display: flex; gap: 15px; margin-bottom: 20px;">
            <label style="flex: 1; padding: 20px; border: 2px solid var(--primary-color, #8b5cf6); border-radius: 12px; cursor: pointer; text-align: center; background: #f5f3ff;"><span style="font-size: 1.5rem; display: block; margin-bottom: 5px;">💳</span><span style="font-weight: 600;">Credit Card</span></label>
            <label style="flex: 1; padding: 20px; border: 2px solid #e2e8f0; border-radius: 12px; cursor: pointer; text-align: center;"><span style="font-size: 1.5rem; display: block; margin-bottom: 5px;">🏦</span><span style="font-weight: 600;">Bank</span></label>
            <label style="flex: 1; padding: 20px; border: 2px solid #e2e8f0; border-radius: 12px; cursor: pointer; text-align: center;"><span style="font-size: 1.5rem; display: block; margin-bottom: 5px;">📱</span><span style="font-weight: 600;">UPI</span></label>
          </div>
          <input type="text" placeholder="Card Number" style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 15px; font-size: 1rem;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
            <input type="text" placeholder="MM/YY" style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
            <input type="text" placeholder="CVV" style="padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 1rem;">
          </div>
        </div>
      </div>
      <div style="background: white; border-radius: 16px; padding: 30px; box-shadow: 0 4px 15px rgba(0,0,0,0.05); position: sticky; top: 100px; height: fit-content;">
        <h3 style="font-weight: 700; margin-bottom: 20px;">Order Summary</h3>
        <div class="checkout-items-display" style="max-height: 250px; overflow-y: auto; margin-bottom: 20px;"></div>
        <div style="border-top: 2px solid #e2e8f0; padding-top: 15px;">
          <div style="display: flex; justify-content: space-between; padding: 8px 0;"><span style="color: #64748b;">Subtotal</span><span class="checkout-subtotal">$0.00</span></div>
          <div style="display: flex; justify-content: space-between; padding: 8px 0;"><span style="color: #64748b;">Shipping</span><span>$9.99</span></div>
          <div style="display: flex; justify-content: space-between; padding: 15px 0; border-top: 2px solid #e2e8f0; margin-top: 10px; font-weight: 700; font-size: 1.3rem;"><span>Total</span><span class="checkout-total" style="color: var(--primary-color, #8b5cf6);">$9.99</span></div>
        </div>
        <button type="button" data-snagcart-checkout onclick="snagcartStartCheckout(); return false;" style="width: 100%; padding: 18px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 10px; font-weight: 700; font-size: 1rem; cursor: pointer; margin-top: 15px;">Pay securely</button>
        <p style="text-align: center; color: #94a3b8; font-size: 0.85rem; margin-top: 15px;">🔒 Secure checkout</p>
      </div>
    </div>
  </div>
</section>`
  },
  {
    id: 'product-categories',
    name: 'Product Categories',
    icon: '📁',
    category: 'sales',
    showInNav: true,
    html: `<section class="widget-section" data-section-id="categories-{{ID}}" data-section-name="Categories" data-show-nav="true" data-widget-type="product-categories" data-operate-widget="product-categories" style="padding: 80px 20px;">
  <div style="max-width: 1200px; margin: 0 auto;">
    <div style="text-align: center; margin-bottom: 50px;">
      <h2 data-categories-title style="font-size: 2.5rem; font-weight: 800; color: #1e293b; margin-bottom: 15px;">Shop by Category</h2>
      <p data-categories-subtitle style="color: #64748b; font-size: 1.1rem;">Browse our wide selection of products</p>
    </div>
    <div id="categories-grid" class="categories-grid" style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 25px;">${OPERATE_EMPTY_GRID_MSG}</div>
  </div>
</section>`
  },
  {
    id: 'sale-banner',
    name: 'Sale Banner',
    icon: '🏷️',
    category: 'sales',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="sale-{{ID}}" data-section-name="Sale" data-show-nav="false" data-widget-type="sale-banner" data-operate-widget="sale-banner" style="background: url('https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1920') center/cover fixed; min-height: 400px; display: flex; align-items: center; position: relative;">
  <div data-sale-overlay style="position: absolute; inset: 0; background: linear-gradient(135deg, rgba(239,68,68,0.9), rgba(220,38,38,0.9));"></div>
  <div style="position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; padding: 0 20px; text-align: center; color: white;">
    <span data-sale-badge style="display: inline-block; padding: 8px 20px; background: white; color: #ef4444; border-radius: 30px; font-size: 0.9rem; font-weight: 700; margin-bottom: 20px;">🔥 LIMITED TIME OFFER</span>
    <h2 data-sale-title style="font-size: 4rem; font-weight: 800; margin-bottom: 15px;">MEGA SALE</h2>
    <p data-sale-subtitle style="font-size: 2rem; font-weight: 600; margin-bottom: 30px;">Up to 70% OFF Everything!</p>
    <a data-sale-button href="?page=products" style="display:inline-block;padding: 18px 50px; background: white; color: #ef4444; border: none; border-radius: 50px; font-weight: 700; font-size: 1.1rem; cursor: pointer; text-decoration:none;">Shop Now</a>
  </div>
</section>`
  },
  {
    id: 'order-success',
    name: 'Order Success',
    icon: '✅',
    category: 'sales',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="success-{{ID}}" data-section-name="Order Success" data-show-nav="false" style="padding: 100px 20px; background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%); text-align: center;">
  <div style="max-width: 600px; margin: 0 auto;">
    <div id="confirmation-loading" style="display:none;color:#64748b;">Loading order details…</div>
    <div id="confirmation-error" style="display:none;color:#dc2626;">We could not load this order. Check your confirmation email.</div>
    <div id="confirmation-content">
      <div style="width: 100px; height: 100px; background: #10b981; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 30px; font-size: 3rem;">✓</div>
      <h2 style="font-size: 2.5rem; font-weight: 800; color: #1e293b; margin-bottom: 15px;">Order Confirmed!</h2>
      <p id="order-number-display" style="font-size: 1.1rem; color: #64748b; margin-bottom: 10px;">Thank you for your purchase.</p>
      <p id="payment-status-badge" style="font-size: .95rem; color: #16a34a; margin-bottom: 30px;">Payment received</p>
      <div style="background: white; border-radius: 16px; padding: 30px; box-shadow: 0 4px 15px rgba(0,0,0,0.05); text-align: left; margin-bottom: 30px;">
        <h3 style="font-weight: 700; margin-bottom: 20px;">Order Details</h3>
        <div id="order-items-list"></div>
        <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 1px solid #e2e8f0; margin-top: 12px;"><span style="color: #64748b;">Subtotal</span><span id="conf-subtotal" style="font-weight: 600;">—</span></div>
        <div style="display: flex; justify-content: space-between; padding: 12px 0;"><span style="color: #64748b;">Shipping</span><span id="conf-delivery" style="font-weight: 600;">—</span></div>
        <div style="display: flex; justify-content: space-between; padding: 12px 0;"><span style="color: #64748b;">Tax</span><span id="conf-tax" style="font-weight: 600;">—</span></div>
        <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 2px solid #e2e8f0; margin-top: 8px;"><span style="font-weight: 700;">Total</span><span id="conf-total" style="font-weight: 700; color: var(--primary-color, #8b5cf6); font-size: 1.2rem;">—</span></div>
        <div id="conf-address" style="margin-top: 16px; color: #64748b; font-size: .9rem;"></div>
      </div>
      <a href="?page=products" style="display:inline-block;padding: 16px 40px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 50px; font-weight: 600; cursor: pointer; text-decoration:none;">Continue Shopping</a>
    </div>
  </div>
</section>`
  },

  // ==================== COMMUNITY WIDGETS ====================
  {
    id: 'reviews',
    name: 'Reviews',
    icon: '⭐',
    category: 'community',
    showInNav: true,
    html: `<section class="widget-section" data-operate-widget="reviews" data-section-id="reviews-{{ID}}" data-section-name="Reviews" data-show-nav="true" style="padding: 80px 20px; background: #f8fafc;">
  <div style="max-width: 1200px; margin: 0 auto;">
    <h2 style="font-size: 2.5rem; font-weight: 800; text-align: center; margin-bottom: 50px;">Customer Reviews</h2>
    <div class="operate-resource-grid" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px;">${OPERATE_EMPTY_GRID_MSG}
    </div>
  </div>
</section>`
  },
  {
    id: 'comment-area',
    name: 'Comment Area',
    icon: '💬',
    category: 'community',
    showInNav: false,
    html: `<section class="widget-section" data-operate-widget="comment-area" data-section-id="comments-{{ID}}" data-section-name="Comments" data-show-nav="false" data-comment-widget="true" style="padding: 60px 20px;">
  <div style="max-width: 800px; margin: 0 auto;">
    <h3 data-comment-title style="font-size: 1.5rem; font-weight: 700; margin-bottom: 25px;">Comments</h3>
    <form data-schema-form="true" data-schema-resource="comments" data-comment-form="true" style="margin-bottom: 25px; padding: 20px; background: #f8fafc; border-radius: 12px;">
      <input type="text" name="name" placeholder="Your name" required style="width: 100%; padding: 12px 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 12px;">
      <input type="email" name="email" placeholder="Email (optional)" style="width: 100%; padding: 12px 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 12px;">
      <textarea name="message" placeholder="Write a comment..." rows="3" required style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; resize: none; margin-bottom: 15px;"></textarea>
      <div data-schema-form-message style="font-size:.9rem;color:#64748b;margin-bottom:10px;"></div>
      <button type="submit" style="padding: 12px 25px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer;">Post Comment</button>
    </form>
    <div data-comment-list style="border-top: 1px solid #e2e8f0; padding-top: 20px;">
      <p style="color:#94a3b8;font-size:.9rem;">Comments from your site visitors will appear here.</p>
    </div>
  </div>
</section>`
  },
  {
    id: 'member-login',
    name: 'Member Login / Registration',
    icon: '👤',
    category: 'community',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="login-{{ID}}" data-section-name="Login" data-show-nav="false" style="padding: 80px 20px; background: linear-gradient(135deg, #f8fafc, #e2e8f0);">
  <div style="max-width: 420px; margin: 0 auto; background: white; padding: 40px; border-radius: 20px; box-shadow: 0 20px 60px rgba(0,0,0,0.1);">
    <h2 data-auth-title style="font-size: 1.8rem; font-weight: 700; text-align: center; margin-bottom: 10px;">Welcome Back</h2>
    <p data-auth-subtitle style="text-align:center;color:#64748b;margin-bottom:24px;font-size:.95rem;">Sign in to access your account</p>
    <div style="display: flex; margin-bottom: 24px; background: #f1f5f9; border-radius: 999px; padding: 4px;">
      <button type="button" data-auth-toggle="login" style="flex:1;padding:8px 0;border-radius:999px;border:none;background:white;color:#0f172a;font-size:.85rem;font-weight:600;cursor:pointer;">Sign in</button>
      <button type="button" data-auth-toggle="signup" style="flex:1;padding:8px 0;border-radius:999px;border:none;background:transparent;color:#64748b;font-size:.85rem;font-weight:500;cursor:pointer;">Create account</button>
    </div>
    <form data-site-auth-form="login" data-auth-redirect="?page=my-account">
      <input type="email" name="email" placeholder="Email" required style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 15px;">
      <input type="password" name="password" placeholder="Password" required minlength="6" style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 20px;">
      <button type="submit" style="width: 100%; padding: 16px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 10px; font-weight: 600; cursor: pointer;">Sign In</button>
    </form>
    <form data-site-auth-form="signup" style="display:none;" data-auth-redirect="?page=my-account">
      <input type="text" name="name" placeholder="Name" required style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 15px;">
      <input type="email" name="email" placeholder="Email" required style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 15px;">
      <input type="password" name="password" placeholder="Password" required minlength="6" style="width: 100%; padding: 15px; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 20px;">
      <button type="submit" style="width: 100%; padding: 16px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 10px; font-weight: 600; cursor: pointer;">Create Account</button>
    </form>
    <p data-site-auth-message style="margin-top: 12px; font-size: .85rem; color: #f97316; text-align:center;"></p>
  </div>
</section>`
  },

  // ==================== OTHER WIDGETS ====================
  {
    id: 'donation',
    name: 'Donation',
    icon: '❤️',
    category: 'other',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="donation-{{ID}}" data-section-name="Donation" data-show-nav="false" style="padding: 80px 20px; background: linear-gradient(135deg, #fef3c7, #fde68a);">
  <div style="max-width: 500px; margin: 0 auto; text-align: center; background: white; padding: 50px; border-radius: 20px; box-shadow: 0 20px 60px rgba(0,0,0,0.1);">
    <div style="font-size: 4rem; margin-bottom: 20px;">❤️</div>
    <h2 style="font-size: 2rem; font-weight: 800; margin-bottom: 15px;">Support Our Cause</h2>
    <p style="color: #64748b; margin-bottom: 30px;">Your donation helps us make a difference.</p>
    <div style="display: flex; gap: 10px; justify-content: center; margin-bottom: 25px;">
      <button style="padding: 15px 25px; border: 2px solid #e2e8f0; border-radius: 10px; background: white; cursor: pointer; font-weight: 600;">$10</button>
      <button style="padding: 15px 25px; border: 2px solid var(--primary-color, #8b5cf6); border-radius: 10px; background: #ede9fe; cursor: pointer; font-weight: 600; color: var(--primary-color, #8b5cf6);">$25</button>
      <button style="padding: 15px 25px; border: 2px solid #e2e8f0; border-radius: 10px; background: white; cursor: pointer; font-weight: 600;">$50</button>
      <button style="padding: 15px 25px; border: 2px solid #e2e8f0; border-radius: 10px; background: white; cursor: pointer; font-weight: 600;">$100</button>
    </div>
    <button style="width: 100%; padding: 18px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 10px; font-size: 1rem; font-weight: 600; cursor: pointer;">Donate Now</button>
  </div>
</section>`
  },
  {
    id: 'social-media',
    name: 'Social Media',
    icon: '📱',
    category: 'other',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="social-{{ID}}" data-section-name="Social" data-show-nav="false" style="padding: 60px 20px; background: #f8fafc;">
  <div style="max-width: 600px; margin: 0 auto; text-align: center;">
    <h3 style="font-size: 1.5rem; font-weight: 700; margin-bottom: 25px;">Follow Us</h3>
    <div style="display: flex; justify-content: center; gap: 15px;">
      <a href="#" style="width: 50px; height: 50px; background: #1877f2; color: white; display: flex; align-items: center; justify-content: center; border-radius: 50%; text-decoration: none; font-size: 1.3rem;">f</a>
      <a href="#" style="width: 50px; height: 50px; background: #1da1f2; color: white; display: flex; align-items: center; justify-content: center; border-radius: 50%; text-decoration: none; font-size: 1.3rem;">𝕏</a>
      <a href="#" style="width: 50px; height: 50px; background: linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888); color: white; display: flex; align-items: center; justify-content: center; border-radius: 50%; text-decoration: none; font-size: 1.3rem;">📷</a>
      <a href="#" style="width: 50px; height: 50px; background: #0077b5; color: white; display: flex; align-items: center; justify-content: center; border-radius: 50%; text-decoration: none; font-size: 1.3rem;">in</a>
      <a href="#" style="width: 50px; height: 50px; background: #ff0000; color: white; display: flex; align-items: center; justify-content: center; border-radius: 50%; text-decoration: none; font-size: 1.3rem;">▶</a>
    </div>
  </div>
</section>`
  },
  {
    id: 'blog',
    name: 'Blog',
    icon: '📰',
    category: 'other',
    showInNav: true,
    html: `<section class="widget-section" data-operate-widget="blog" data-section-id="blog-{{ID}}" data-section-name="Blog" data-show-nav="true" style="padding: 80px 20px;">
  <div style="max-width: 1200px; margin: 0 auto;">
    <h2 style="font-size: 2.5rem; font-weight: 800; text-align: center; margin-bottom: 50px;">Latest Articles</h2>
    <div class="operate-resource-grid" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px;">${OPERATE_EMPTY_GRID_MSG}
    </div>
  </div>
</section>`
  },
  {
    id: 'code',
    name: 'Code',
    icon: '💻',
    category: 'other',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="code-{{ID}}" data-section-name="Code" data-show-nav="false" style="padding: 60px 20px; background: #1e293b;">
  <div style="max-width: 800px; margin: 0 auto;">
    <pre style="background: #0f172a; padding: 30px; border-radius: 12px; overflow-x: auto; color: #e2e8f0; font-family: 'Fira Code', monospace; font-size: 0.9rem; line-height: 1.8;">
<code><span style="color: #7c3aed;">const</span> <span style="color: #22d3ee;">greeting</span> = (<span style="color: #fbbf24;">name</span>) => {
  <span style="color: #7c3aed;">return</span> <span style="color: #a3e635;">\`Hello, \${name}!\`</span>;
};

<span style="color: #22d3ee;">console</span>.<span style="color: #fbbf24;">log</span>(<span style="color: #22d3ee;">greeting</span>(<span style="color: #a3e635;">'World'</span>));</code>
    </pre>
  </div>
</section>`
  },
  {
    id: 'countdown',
    name: 'Countdown',
    icon: '⏰',
    category: 'other',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="countdown-{{ID}}" data-section-name="Countdown" data-show-nav="false" style="padding: 80px 20px; background: linear-gradient(135deg, #667eea, #764ba2); color: white; text-align: center;">
  <h2 style="font-size: 2rem; font-weight: 800; margin-bottom: 30px;">Coming Soon!</h2>
  <div style="display: flex; justify-content: center; gap: 20px;">
    <div style="background: rgba(255,255,255,0.2); padding: 25px 35px; border-radius: 15px; backdrop-filter: blur(10px);">
      <div style="font-size: 3rem; font-weight: 800;">15</div>
      <div style="font-size: 0.9rem; opacity: 0.8;">Days</div>
    </div>
    <div style="background: rgba(255,255,255,0.2); padding: 25px 35px; border-radius: 15px; backdrop-filter: blur(10px);">
      <div style="font-size: 3rem; font-weight: 800;">08</div>
      <div style="font-size: 0.9rem; opacity: 0.8;">Hours</div>
    </div>
    <div style="background: rgba(255,255,255,0.2); padding: 25px 35px; border-radius: 15px; backdrop-filter: blur(10px);">
      <div style="font-size: 3rem; font-weight: 800;">42</div>
      <div style="font-size: 0.9rem; opacity: 0.8;">Minutes</div>
    </div>
    <div style="background: rgba(255,255,255,0.2); padding: 25px 35px; border-radius: 15px; backdrop-filter: blur(10px);">
      <div style="font-size: 3rem; font-weight: 800;">30</div>
      <div style="font-size: 0.9rem; opacity: 0.8;">Seconds</div>
    </div>
  </div>
</section>`
  },
  {
    id: 'map',
    name: 'Map',
    icon: '🗺️',
    category: 'other',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="map-{{ID}}" data-section-name="Map" data-show-nav="false" style="padding: 0; height: 400px;">
  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d387193.30591910525!2d-74.25986432970718!3d40.697149422113014!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY!5e0!3m2!1sen!2sus!4v1625764841234" width="100%" height="100%" style="border:0;" allowfullscreen="" loading="lazy"></iframe>
</section>`
  },
  {
    id: 'menu',
    name: 'Menu',
    icon: '🍽️',
    category: 'other',
    showInNav: true,
    html: `<section class="widget-section" data-operate-widget="menu" data-section-id="menu-{{ID}}" data-section-name="Menu" data-show-nav="true" style="padding: 80px 20px; background: #f8fafc;">
  <div style="max-width: 900px; margin: 0 auto;">
    <h2 style="font-size: 2.5rem; font-weight: 800; text-align: center; margin-bottom: 50px;">Our Menu</h2>
    <div class="operate-resource-grid" style="display: grid; gap: 16px;">${OPERATE_EMPTY_GRID_MSG}
    </div>
  </div>
</section>`
  },
  {
    id: 'portfolio',
    name: 'Portfolio',
    icon: '💼',
    category: 'other',
    showInNav: true,
    html: `<section class="widget-section" data-operate-widget="portfolio" data-section-id="portfolio-{{ID}}" data-section-name="Portfolio" data-show-nav="true" style="padding: 80px 20px;">
  <div style="max-width: 1200px; margin: 0 auto;">
    <h2 style="font-size: 2.5rem; font-weight: 800; text-align: center; margin-bottom: 50px;">Our Work</h2>
    <div class="operate-resource-grid" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 25px;">${OPERATE_EMPTY_GRID_MSG}
    </div>
  </div>
</section>`
  },
  {
    id: 'progress-bar',
    name: 'Progress Bar',
    icon: '📊',
    category: 'other',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="progress-{{ID}}" data-section-name="Skills" data-show-nav="false" style="padding: 60px 20px;">
  <div style="max-width: 600px; margin: 0 auto;">
    <h3 style="font-size: 1.5rem; font-weight: 700; text-align: center; margin-bottom: 40px;">Our Skills</h3>
    <div style="margin-bottom: 25px;">
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px;"><span style="font-weight: 600;">Web Design</span><span>90%</span></div>
      <div style="height: 10px; background: #e2e8f0; border-radius: 10px; overflow: hidden;"><div style="width: 90%; height: 100%; background: linear-gradient(90deg, #8b5cf6, #a855f7); border-radius: 10px;"></div></div>
    </div>
    <div style="margin-bottom: 25px;">
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px;"><span style="font-weight: 600;">Development</span><span>85%</span></div>
      <div style="height: 10px; background: #e2e8f0; border-radius: 10px; overflow: hidden;"><div style="width: 85%; height: 100%; background: linear-gradient(90deg, #10b981, #34d399); border-radius: 10px;"></div></div>
    </div>
    <div style="margin-bottom: 25px;">
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px;"><span style="font-weight: 600;">Marketing</span><span>75%</span></div>
      <div style="height: 10px; background: #e2e8f0; border-radius: 10px; overflow: hidden;"><div style="width: 75%; height: 100%; background: linear-gradient(90deg, #f59e0b, #fbbf24); border-radius: 10px;"></div></div>
    </div>
  </div>
</section>`
  },
  {
    id: 'search',
    name: 'Search',
    icon: '🔍',
    category: 'other',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="search-{{ID}}" data-section-name="Search" data-show-nav="false" style="padding: 60px 20px; text-align: center;">
  <div style="max-width: 600px; margin: 0 auto;">
    <h3 style="font-size: 1.5rem; font-weight: 700; margin-bottom: 20px;">Search</h3>
    <div style="display: flex; gap: 10px;">
      <input type="text" placeholder="Search..." style="flex: 1; padding: 15px 20px; border: 2px solid #e2e8f0; border-radius: 50px; font-size: 1rem;">
      <button style="padding: 15px 30px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 50px; font-weight: 600; cursor: pointer;">Search</button>
    </div>
  </div>
</section>`
  },
  {
    id: 'pricing',
    name: 'Pricing',
    icon: '💰',
    category: 'sales',
    showInNav: true,
    html: `<section class="widget-section" data-operate-widget="pricing" data-section-id="pricing-{{ID}}" data-section-name="Pricing" data-show-nav="true" style="padding: 100px 20px; background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%); color: white;">
  <div style="max-width: 1200px; margin: 0 auto; text-align: center;">
    <span style="display: inline-block; padding: 8px 20px; background: rgba(255,255,255,0.1); border-radius: 30px; font-size: 0.85rem; font-weight: 600; margin-bottom: 20px;">💎 Pricing</span>
    <h2 style="font-size: 2.8rem; font-weight: 800; margin-bottom: 60px;">Simple, Transparent Pricing</h2>
    <div class="operate-resource-grid" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; align-items: stretch;">${OPERATE_EMPTY_GRID_MSG}
    </div>
  </div>
</section>`
  },
  {
    id: 'hero-banner',
    name: 'Hero Banner',
    icon: '🎨',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section widget-hero" data-section-id="hero-{{ID}}" data-section-name="Hero" data-show-nav="false" style="background-image: url('https://images.unsplash.com/photo-1557683316-973673baf926?w=1920'); background-size: cover; background-position: center; min-height: 600px; display: flex; align-items: center; justify-content: center; position: relative;">
  <div style="position: absolute; inset: 0; background: rgba(0,0,0,0.5);"></div>
  <div style="position: relative; z-index: 1; text-align: center; color: white; max-width: 800px; padding: 40px;">
    <h1 style="font-size: 4rem; font-weight: 800; margin-bottom: 25px; text-shadow: 0 4px 30px rgba(0,0,0,0.3);">Welcome to Our Site</h1>
    <p style="font-size: 1.3rem; margin-bottom: 40px; opacity: 0.9;">Create amazing experiences with our powerful platform</p>
    <div style="display: flex; gap: 15px; justify-content: center;">
      <a href="#" style="padding: 18px 40px; background: var(--primary-color, #8b5cf6); color: white; border-radius: 50px; text-decoration: none; font-weight: 700;">Get Started</a>
      <a href="#" style="padding: 18px 40px; border: 2px solid white; color: white; border-radius: 50px; text-decoration: none; font-weight: 600;">Learn More</a>
    </div>
  </div>
</section>`
  },
  {
    id: 'footer',
    name: 'Footer',
    icon: '📌',
    category: 'basic',
    showInNav: false,
    html: `<footer class="widget-section" data-section-id="footer-{{ID}}" data-section-name="Footer" data-show-nav="false" style="padding: 60px 20px 30px; background: #0f172a; color: white;">
  <div class="footer-grid" style="max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 40px;">
    <div class="footer-brand">
      <h3 style="font-size: 1.5rem; font-weight: 800; margin-bottom: 20px;">Your Brand</h3>
      <p style="color: #94a3b8; line-height: 1.7;">Building amazing digital experiences for businesses worldwide.</p>
    </div>
    <div class="footer-col">
      <h4 style="font-weight: 600; margin-bottom: 20px;">Quick Links</h4>
      <ul style="list-style: none; padding: 0;">
        <li style="margin-bottom: 12px;"><a href="#" style="color: #94a3b8; text-decoration: none;">Home</a></li>
        <li style="margin-bottom: 12px;"><a href="#" style="color: #94a3b8; text-decoration: none;">About</a></li>
        <li style="margin-bottom: 12px;"><a href="#" style="color: #94a3b8; text-decoration: none;">Services</a></li>
        <li style="margin-bottom: 12px;"><a href="#" style="color: #94a3b8; text-decoration: none;">Contact</a></li>
      </ul>
    </div>
    <div class="footer-col">
      <h4 style="font-weight: 600; margin-bottom: 20px;">Legal</h4>
      <ul style="list-style: none; padding: 0;">
        <li style="margin-bottom: 12px;"><a href="#" style="color: #94a3b8; text-decoration: none;">Privacy Policy</a></li>
        <li style="margin-bottom: 12px;"><a href="#" style="color: #94a3b8; text-decoration: none;">Terms of Service</a></li>
      </ul>
    </div>
    <div class="footer-col">
      <h4 style="font-weight: 600; margin-bottom: 20px;">Contact</h4>
      <ul style="list-style: none; padding: 0; color: #94a3b8;">
        <li style="margin-bottom: 12px;">📧 hello@example.com</li>
        <li style="margin-bottom: 12px;">📞 +1 (555) 123-4567</li>
        <li style="margin-bottom: 12px;">📍 New York, NY</li>
      </ul>
    </div>
  </div>
  <div class="footer-bottom" style="border-top: 1px solid #1e293b; margin-top: 40px; padding-top: 25px; text-align: center; color: #64748b;">
    <span data-footer-copyright="true">© 2025 Snagcart. All rights reserved.</span>
  </div>
</footer>`
  },
  {
    id: 'cta',
    name: 'Call to Action',
    icon: '🎯',
    category: 'basic',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="cta-{{ID}}" data-section-name="CTA" data-show-nav="false" style="padding: 100px 20px; background: linear-gradient(135deg, var(--primary-color, #667eea) 0%, #764ba2 100%); color: white; text-align: center;">
  <div style="max-width: 700px; margin: 0 auto;">
    <h2 style="font-size: 2.8rem; font-weight: 800; margin-bottom: 20px;">Ready to Get Started?</h2>
    <p style="font-size: 1.2rem; margin-bottom: 40px; opacity: 0.9;">Join thousands of satisfied customers and transform your business today.</p>
    <div style="display: flex; gap: 15px; justify-content: center;">
      <a href="#" style="padding: 18px 45px; background: white; color: #764ba2; border-radius: 50px; text-decoration: none; font-weight: 700; box-shadow: 0 10px 30px rgba(0,0,0,0.2);">Start Free Trial</a>
      <a href="#" style="padding: 18px 45px; border: 2px solid white; color: white; border-radius: 50px; text-decoration: none; font-weight: 600;">Contact Sales</a>
    </div>
  </div>
</section>`
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    icon: '📬',
    category: 'lead',
    showInNav: false,
    html: `<section class="widget-section" data-section-id="newsletter-{{ID}}" data-section-name="Newsletter" data-show-nav="false" style="padding: 80px 20px; background: #f8fafc;">
  <div style="max-width: 600px; margin: 0 auto; text-align: center;">
    <span style="font-size: 3rem; margin-bottom: 20px; display: block;">📬</span>
    <h2 style="font-size: 2rem; font-weight: 800; margin-bottom: 15px;">Subscribe to Our Newsletter</h2>
    <p style="color: #64748b; margin-bottom: 30px;">Get the latest updates and offers directly in your inbox.</p>
    <form data-schema-form="true" data-schema-resource="subscribers" style="display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;">
      <input type="email" name="email" placeholder="Enter your email" required style="flex: 1; min-width: 220px; padding: 16px 20px; border: 2px solid #e2e8f0; border-radius: 50px; font-size: 1rem;">
      <button type="submit" style="padding: 16px 35px; background: var(--primary-color, #8b5cf6); color: white; border: none; border-radius: 50px; font-weight: 600; cursor: pointer;">Subscribe</button>
      <div data-schema-form-message style="width: 100%; font-size: 0.95rem; color: #64748b; margin-top: 8px;"></div>
    </form>
  </div>
</section>`
  },
  {
    id: 'team',
    name: 'Team',
    icon: '👥',
    category: 'other',
    showInNav: true,
    html: `<section class="widget-section" data-operate-widget="team" data-section-id="team-{{ID}}" data-section-name="Team" data-show-nav="true" style="padding: 80px 20px;">
  <div style="max-width: 1200px; margin: 0 auto; text-align: center;">
    <h2 style="font-size: 2.5rem; font-weight: 800; margin-bottom: 50px;">Meet Our Team</h2>
    <div class="operate-resource-grid" style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px;">${OPERATE_EMPTY_GRID_MSG}
    </div>
  </div>
</section>`
  },
  {
    id: 'services',
    name: 'Services',
    icon: '🔧',
    category: 'basic',
    showInNav: true,
    html: `<section class="widget-section" data-operate-widget="services" data-widget-type="services" data-section-id="services-{{ID}}" data-section-name="Services" data-show-nav="true" style="padding: 80px 20px; background: #f8fafc;">
  <div style="max-width: 1200px; margin: 0 auto; text-align: center;">
    <h2 style="font-size: 2.5rem; font-weight: 800; margin-bottom: 50px; color: #1e293b;">Our Services</h2>
    <div data-widget-content-grid style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px;"></div>
  </div>
</section>`
  },
];

/** Whether an Operate resource row should appear in editor/storefront previews. */
const isOperateResourceActive = (item) => {
  if (!item || typeof item !== 'object') return false;
  if (item.is_active === false || item.is_available === false) return false;
  const status = String(item.status || 'active').toLowerCase();
  return !['inactive', 'draft', 'archived', 'disabled', 'cancelled', 'removed'].includes(status);
};

const normalizeSchemaProduct = (item) => {
  const images = Array.isArray(item?.images) ? item.images : [];
  const primaryImage = images.find((img) => img?.is_primary) || images[0];
  return {
    ...item,
    id: item?.id,
    name: item?.name || '',
    price: Number(item?.price || 0),
    description: item?.description || item?.short_description || '',
    image_url: item?.image_url || item?.image || primaryImage?.url || '',
    is_active: isOperateResourceActive(item),
    is_featured: !!item?.is_featured,
    status: item?.status || 'active',
  };
};

// ==================== HELPER FUNCTIONS ====================

/** Remove inline product-loader scripts without crossing other nodes (regex would swallow sections). */
const stripEmbeddedProductLoaders = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  const markers = [
    "getElementById('featured-products')",
    'getElementById("featured-products")',
    "getElementById('products-grid')",
    'getElementById("products-grid")',
  ];
  try {
    const doc = new DOMParser().parseFromString(`<div id="strip-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('strip-root');
    if (!root) return html;
    root.querySelectorAll('script').forEach((node) => {
      const text = node.textContent || '';
      if (markers.some((marker) => text.includes(marker))) {
        node.remove();
      }
    });
    return root.innerHTML;
  } catch {
    return html;
  }
};

/** E-commerce and nav-pack pages store full <!DOCTYPE html> documents; preview needs body fragment only. */
const extractPageFragmentForPreview = (html) => {
  if (!html || typeof html !== 'string') {
    return { html: html || '', extraStyles: '', extraLinks: '' };
  }
  const trimmed = html.trim();
  if (!/<html[\s>]/i.test(trimmed) && !/<!DOCTYPE/i.test(trimmed)) {
    return { html: trimmed, extraStyles: '', extraLinks: '' };
  }
  try {
    const doc = new DOMParser().parseFromString(trimmed, 'text/html');
    const extraStyles = Array.from(doc.querySelectorAll('head style'))
      .map((node) => node.textContent || '')
      .filter(Boolean)
      .join('\n');
    const extraLinks = Array.from(doc.querySelectorAll('head link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('\n');
    return {
      html: doc.body?.innerHTML || trimmed,
      extraStyles,
      extraLinks,
    };
  } catch {
    return { html: trimmed, extraStyles: '', extraLinks: '' };
  }
};

const normalizeImageUrl = (url) => {
  if (!url) return '';
  return String(url).replace(/&amp;/g, '&').trim();
};

const imageUrlMatches = (a, b) => {
  const na = normalizeImageUrl(a);
  const nb = normalizeImageUrl(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const sa = na.split('?')[0];
  const sb = nb.split('?')[0];
  if (sa === sb) return true;
  try {
    const pathA = sa.startsWith('http') ? new URL(sa).pathname : sa;
    const pathB = sb.startsWith('http') ? new URL(sb).pathname : sb;
    if (pathA && pathB && pathA === pathB) return true;
  } catch { /* ignore malformed URLs */ }
  const idA = sa.match(/photo-([\w-]+)/)?.[1];
  const idB = sb.match(/photo-([\w-]+)/)?.[1];
  return Boolean(idA && idB && idA === idB);
};

const patchThemeTokensInCss = (css, primary, secondary, font) => {
  let next = css || '';
  const upsert = (name, value) => {
    if (!value) return;
    const re = new RegExp(`(${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*)[^;]+`, 'i');
    if (re.test(next)) next = next.replace(re, `$1${value}`);
    else next = `:root { ${name}: ${value}; }\n${next}`;
  };
  upsert('--primary', primary);
  upsert('--primary-color', primary);
  upsert('--primary-dark', secondary);
  upsert('--secondary', secondary);
  upsert('--secondary-color', secondary);
  if (font) {
    upsert('--font-heading', `'${font}', sans-serif`);
    upsert('--font-body', `'${font}', sans-serif`);
  }
  return next;
};

const BROKEN_UNSPLASH_REPLACEMENTS = {
  '1527515633865-4fc7b7b9c508': '1581578731548-c64695cc6952',
  '1556911223-dc2376019893': '1504307651254-356700af0cc2',
};

const repairBrokenUnsplashUrls = (html) => {
  if (!html) return html;
  let next = html;
  Object.entries(BROKEN_UNSPLASH_REPLACEMENTS).forEach(([bad, good]) => {
    next = next.split(bad).join(good);
  });
  return next;
};

const cssColorToHex = (color) => {
  if (!color) return '#111827';
  const trimmed = String(color).trim();
  if (trimmed.startsWith('#')) return trimmed.length === 4
    ? `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
    : trimmed.slice(0, 7);
  const rgb = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!rgb) return '#111827';
  const hex = (n) => Number(n).toString(16).padStart(2, '0');
  return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
};

const buildEditorGoogleFontsHref = () => {
  const families = FONT_FAMILIES
    .map((f) => `family=${encodeURIComponent(f.value).replace(/%20/g, '+')}:wght@300;400;500;600;700;800`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
};

const formatToolbarFontFamily = (font) => {
  const trimmed = String(font || '').trim();
  if (!trimmed) return '';
  if (trimmed.includes(',')) return trimmed;
  if (trimmed.startsWith('"') || trimmed.startsWith("'")) return trimmed;
  return `'${trimmed}', sans-serif`;
};

const matchToolbarFontFamily = (computed) => {
  const raw = String(computed || '').toLowerCase();
  const hit = FONT_FAMILIES.find((f) => raw.includes(f.value.toLowerCase()));
  return hit?.value || '';
};

const normalizeToolbarFontSize = (computedSize) => {
  if (!computedSize) return '';
  const px = parseFloat(computedSize);
  if (!Number.isFinite(px)) return '';
  const rounded = Math.round(px);
  const options = [14, 16, 18, 20, 24, 28];
  const match = options.find((n) => Math.abs(n - rounded) <= 1);
  return match ? `${match}px` : `${rounded}px`;
};

const resolveMediaUrl = (url, apiBase = '') => {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (trimmed.startsWith('data:') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  if (trimmed.startsWith('//')) return `${window.location.protocol}${trimmed}`;
  if (trimmed.startsWith('/')) {
    const origin = apiBase.replace(/\/api\/?$/, '') || window.location.origin;
    return `${origin}${trimmed}`;
  }
  return trimmed;
};

/** Sync or insert ``<nav class="header-nav">`` from the site's page list. */
const injectOrSyncHeaderNav = (html, linksHtml) => {
  if (!html || !linksHtml) return html || '';
  const navBlock = `<nav class="header-nav">${linksHtml}</nav>`;
  if (/<nav class="header-nav"[^>]*>/i.test(html)) {
    return html.replace(/<nav class="header-nav"[^>]*>[\s\S]*?<\/nav>/i, navBlock);
  }
  if (!/data-section-name=["']Header["']/i.test(html)) return html;
  const afterLogo = html.replace(
    /(<div class="header-logo"[^>]*>[\s\S]*?<\/div>)(\s*(?=<button class="header-cta"|<button class="mobile-toggle))/i,
    `$1\n    ${navBlock}\n    `,
  );
  if (afterLogo !== html) return afterLogo;
  return html.replace(
    /(<header[^>]*data-section-name=["']Header["'][^>]*>[\s\S]*?<div class="header-inner"[^>]*>[\s\S]*?)(\s*<button class="header-cta")/i,
    `$1\n    ${navBlock}\n    $2`,
  );
};

/** Strip preview-only nodes while preserving template header navigation. */
const cloneEditorPreviewBody = (bodyEl) => {
  const clone = bodyEl.cloneNode(true);
  clone.querySelectorAll('[contenteditable]').forEach((el) => {
    el.removeAttribute('contenteditable');
    el.removeAttribute('data-editable');
  });
  clone.querySelectorAll('script,.cart-sidebar,.cart-overlay,.editor-brand-chip').forEach((el) => el.remove());
  // Remove editor-injected unified top nav only — never strip `.header-nav` inside the site header.
  clone.querySelectorAll('body > nav').forEach((el) => {
    if (!el.classList.contains('header-nav')) el.remove();
  });
  return clone.innerHTML.trim();
};

/** Normalize a live preview section node back into stored section HTML. */
const cleanSectionHtmlForStorage = (html, widgetInstances = []) => {
  if (!html) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.body.firstElementChild;
    if (!el) return html;
    el.querySelectorAll('[contenteditable]').forEach((node) => {
      node.removeAttribute('contenteditable');
      node.removeAttribute('data-editable');
      node.removeAttribute('data-editor-id');
    });
    return repairStoredVideoSections(el.outerHTML, widgetInstances);
  } catch {
    return html;
  }
};

/** Merge iframe section DOM back into stored HTML without re-parsing preview chrome. */
const mergeIframeSectionsIntoHtml = (baseHtml, iframeDoc, widgetInstances = []) => {
  const safeBase = repairBrokenUnsplashUrls(baseHtml || '');
  const { sections: currentSections, envelope } = parseHtmlStructure(safeBase);
  if (!iframeDoc || !currentSections.length) {
    return safeBase;
  }
  const contentRoot = iframeDoc.getElementById('editor-page-content')
    || iframeDoc.querySelector('[data-editor-page-root]')
    || iframeDoc.body;
  if (!contentRoot) {
    return safeBase;
  }
  const updatedSections = currentSections.map((section) => {
    const sectionId = String(section.id || '').replace(/"/g, '\\"');
    const liveEl = contentRoot.querySelector(`[data-section-id="${sectionId}"]`)
      || (sectionId ? contentRoot.querySelector(`#${sectionId}`) : null);
    if (!liveEl) return section;
    const cleaned = cleanSectionHtmlForStorage(liveEl.outerHTML, widgetInstances);
    if (!cleaned || !cleaned.trim()) return section;
    return { ...section, html: cleaned };
  });
  const merged = repairBrokenUnsplashUrls(sectionsToHtml(updatedSections, envelope));
  return merged && merged.trim() ? merged : safeBase;
};

/** Prefer parsed sections; fall back to editor state when HTML is not section-marked (AI templates). */
const resolveSectionsForEdit = (baseHtml, sectionsState, envelopeRef) => {
  const safeBase = repairBrokenUnsplashUrls(baseHtml || '');
  const parsed = parseHtmlStructure(safeBase);
  if (parsed.sections.length > 0) {
    return parsed;
  }
  if (Array.isArray(sectionsState) && sectionsState.length > 0) {
    return {
      sections: sectionsState,
      envelope: envelopeRef?.current || parsed.envelope,
    };
  }
  return parsed;
};

/** Always append a new section at the end without disturbing existing layout. */
const appendSectionToPageHtml = (baseHtml, sectionsState, newSection, envelopeRef, composeFn) => {
  const { sections: currentSections, envelope } = resolveSectionsForEdit(baseHtml, sectionsState, envelopeRef);
  if (envelopeRef) envelopeRef.current = envelope;
  if (!currentSections.length) {
    const trimmed = (baseHtml || '').trim();
    return trimmed ? `${trimmed}\n${newSection.html}` : newSection.html;
  }
  return composeFn([...currentSections, newSection]);
};

const buildBlankSectionHtml = (sectionType) => {
  const id = `section-${generateId()}`;
  const name = sectionType.name;
  const typeKey = sectionType.name.toLowerCase().replace(' section', '').replace(/\s+/g, '-');
  const baseAttrs = `class="widget-section" data-section-id="${id}" data-section-name="${name}" data-show-nav="false"`;
  const templates = {
    hero: `<section ${baseAttrs} style="padding:120px 20px;text-align:center;background:linear-gradient(135deg,var(--primary-color,#8b5cf6),var(--secondary-color,#10b981));color:white;"><h1>${name}</h1><p>Add your hero message here</p><a href="#" style="display:inline-block;margin-top:20px;padding:14px 32px;background:white;color:#1e293b;border-radius:999px;text-decoration:none;font-weight:700;">Get Started</a></section>`,
    features: `<section ${baseAttrs} style="padding:80px 20px;"><h2 style="text-align:center;margin-bottom:32px;">${name}</h2><div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:20px;max-width:1100px;margin:0 auto;"><div style="padding:24px;border-radius:16px;background:#f8fafc;"><h3>Feature One</h3><p>Describe your first feature here.</p></div><div style="padding:24px;border-radius:16px;background:#f8fafc;"><h3>Feature Two</h3><p>Describe your second feature here.</p></div><div style="padding:24px;border-radius:16px;background:#f8fafc;"><h3>Feature Three</h3><p>Describe your third feature here.</p></div></div></section>`,
    testimonials: `<section ${baseAttrs} style="padding:80px 20px;background:#f8fafc;"><h2 style="text-align:center;margin-bottom:32px;">${name}</h2><div style="max-width:760px;margin:0 auto;padding:32px;border-radius:20px;background:white;box-shadow:0 10px 30px rgba(15,23,42,0.08);"><p style="font-size:1.1rem;line-height:1.7;">"Add a customer quote here."</p><p style="margin-top:16px;font-weight:700;">Customer Name</p></div></section>`,
    pricing: `<section ${baseAttrs} style="padding:80px 20px;"><h2 style="text-align:center;margin-bottom:32px;">${name}</h2><div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:20px;max-width:980px;margin:0 auto;"><div style="padding:28px;border-radius:18px;border:1px solid #e2e8f0;"><h3>Basic</h3><p class="price-amount" style="font-size:2rem;font-weight:800;">$49</p><p>Perfect to get started.</p></div><div style="padding:28px;border-radius:18px;border:2px solid var(--primary-color,#8b5cf6);"><h3>Pro</h3><p class="price-amount" style="font-size:2rem;font-weight:800;">$99</p><p>Best for growing teams.</p></div></div></section>`,
    faq: `<section ${baseAttrs} style="padding:80px 20px;max-width:860px;margin:0 auto;"><h2 style="margin-bottom:24px;">${name}</h2><div><h3>Question one?</h3><p>Answer one goes here.</p><h3 style="margin-top:20px;">Question two?</h3><p>Answer two goes here.</p></div></section>`,
    team: `<section ${baseAttrs} style="padding:80px 20px;"><h2 style="text-align:center;margin-bottom:32px;">${name}</h2><div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:20px;max-width:980px;margin:0 auto;"><div style="text-align:center;"><img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400" alt="Team member" style="width:120px;height:120px;border-radius:999px;object-fit:cover;margin-bottom:12px;"><h3>Team Member</h3><p>Role title</p></div></div></section>`,
    gallery: `<section ${baseAttrs} style="padding:80px 20px;"><h2 style="text-align:center;margin-bottom:32px;">${name}</h2><div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:16px;max-width:1100px;margin:0 auto;"><img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600" alt="Gallery image" style="width:100%;height:180px;object-fit:cover;border-radius:16px;"><img src="https://images.unsplash.com/photo-1504307651254-356700af0cc2?w=600" alt="Gallery image" style="width:100%;height:180px;object-fit:cover;border-radius:16px;"><img src="https://images.unsplash.com/photo-1563453392219-991e4588f989?w=600" alt="Gallery image" style="width:100%;height:180px;object-fit:cover;border-radius:16px;"></div></section>`,
    stats: `<section ${baseAttrs} style="padding:80px 20px;background:#0f172a;color:white;"><div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:20px;max-width:980px;margin:0 auto;text-align:center;"><div><p style="font-size:2.5rem;font-weight:800;">500+</p><p>Happy Clients</p></div><div><p style="font-size:2.5rem;font-weight:800;">24/7</p><p>Support</p></div><div><p style="font-size:2.5rem;font-weight:800;">10+</p><p>Years Experience</p></div></div></section>`,
    cta: `<section ${baseAttrs} style="padding:90px 20px;text-align:center;background:var(--primary-color,#8b5cf6);color:white;"><h2>${name}</h2><p style="margin:16px auto 24px;max-width:620px;">Encourage visitors to take the next step.</p><a href="#" style="display:inline-block;padding:14px 32px;background:white;color:#1e293b;border-radius:999px;text-decoration:none;font-weight:700;">Contact Us</a></section>`,
    video: `<section ${baseAttrs} style="padding:80px 20px;"><h2 style="text-align:center;margin-bottom:24px;">${name}</h2><div style="max-width:900px;margin:0 auto;aspect-ratio:16/9;background:#0f172a;border-radius:20px;display:flex;align-items:center;justify-content:center;color:white;">Video embed area</div></section>`,
    map: `<section ${baseAttrs} style="padding:80px 20px;"><h2 style="text-align:center;margin-bottom:24px;">${name}</h2><div style="max-width:900px;margin:0 auto;height:320px;border-radius:20px;background:#e2e8f0;display:flex;align-items:center;justify-content:center;color:#475569;">Map embed area</div></section>`,
    custom: `<section ${baseAttrs} style="padding:80px 20px;"><h2>${name}</h2><p>Customize this section with your own content.</p></section>`,
  };
  return templates[typeKey] || templates.custom;
};

const HIDDEN_NAV_SLUGS = new Set(['account', 'order-history', 'order-confirmation', 'product-detail', 'cart', 'checkout']);

const isMarketingNavPage = (page) => {
  if (!page?.slug) return false;
  if (page.resource) return false;
  if (page.nav_visible === false) return false;
  // Legal is allowed when Show in Navigation is on (default packs ship nav_visible=false).
  if (['system', 'admin', 'ecommerce', 'account'].includes(page.category)) return false;
  if (HIDDEN_NAV_SLUGS.has(page.slug)) return false;
  if (!page.name && !page.title && !page.category) return false;
  return true;
};

const slugFromPageHref = (href) => {
  const m = String(href || '').match(/[?&]page=([^&#]+)/);
  return m ? decodeURIComponent(m[1]) : '';
};

const resolveMarketingNavPages = (pageList) => {
  const primary = pageList.filter(isMarketingNavPage);
  if (primary.length) return primary;
  const utility = new Set(['admin-dashboard', 'sign-in', 'my-account', 'checkout', 'cart', 'order-confirmation', 'product-detail']);
  return pageList
    .filter((p) => p?.slug && !p.resource && p.nav_visible !== false && !utility.has(p.slug))
    .filter((p) => !(p.category && ['system', 'admin'].includes(p.category)))
    .filter((p) => p.name || p.title || p.slug)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const buildTemplateNavLinksHtml = (pageList, currentSlug = '', { includeRegister = true } = {}) => {
  const visiblePages = resolveMarketingNavPages(pageList);
  const links = visiblePages.map((page) => {
    const isActive = currentSlug && page.slug === currentSlug;
    const label = page.name || page.title || (page.slug ? page.slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Page');
    return `<a href="?page=${page.slug}" data-editor-page="${page.slug}" data-page="${page.slug}"${isActive ? ' class="is-active"' : ''}>${label}</a>`;
  }).join('');
  if (!includeRegister || !shouldShowRegisterNav(pageList)) return links;
  const registerActive = currentSlug === 'sign-in' ? ' class="is-active"' : '';
  return `${links}<a href="?page=sign-in" data-editor-page="sign-in" data-page="sign-in"${registerActive}>Register</a>`;
};

const shouldSkipMarketingHeaderRepair = (page) => {
  if (!page) return true;
  if (STANDALONE_DASHBOARD_SLUGS.has(page.slug)) return true;
  if (['system', 'admin', 'account'].includes(page.category)) return true;
  const html = page.html_code || '';
  if (html.includes('dash-layout') && /<!DOCTYPE/i.test(html)) return true;
  return false;
};

const stripPrependedMarketingShell = (html) => {
  if (!html) return html;
  let cleaned = html;

  const docMatch = cleaned.match(/<!DOCTYPE\s+html|<html[\s>]/i);
  if (docMatch && docMatch.index > 0) {
    const prefix = cleaned.slice(0, docMatch.index);
    if (prefix.trim() && /site-marketing-header|data-section-name=["']Header["']|class="header-nav"|header-cta/i.test(prefix)) {
      cleaned = cleaned.slice(docMatch.index);
    }
  }

  if (!cleaned.includes('dash-layout')) return cleaned;

  const chromeMatch = cleaned.match(/(<div class="dash-sidebar-overlay"|<div class="dash-layout"|<header class="dash-header")/i);
  if (!chromeMatch || chromeMatch.index === undefined) return cleaned;

  const prefix = cleaned.slice(0, chromeMatch.index);
  const suffix = cleaned.slice(chromeMatch.index);
  const marketingHeaderRe = /<header(?=[^>]*(?:site-marketing-header|header-section(?:\s+widget-section|\s+[^"']*widget-section)))[^>]*>[\s\S]*?<\/header>\s*/gi;
  const newPrefix = prefix
    .replace(marketingHeaderRe, '')
    .replace(/<style[^>]*>[\s\S]*?site-marketing-header[\s\S]*?<\/style>\s*/gi, '');

  return newPrefix !== prefix ? newPrefix + suffix : cleaned;
};

const repairPagesHeaderNav = (pageList) => {
  const homeHeader = extractHomeHeaderHtml(pageList);
  return pageList.map((page) => {
    if (shouldSkipMarketingHeaderRepair(page)) {
      const cleaned = stripPrependedMarketingShell(page.html_code || '');
      return cleaned === (page.html_code || '') ? page : { ...page, html_code: cleaned };
    }
    const html = page.html_code || '';
    if (!/data-section-name=["']Header["']/i.test(html) && !homeHeader) return page;
    let nextHtml = html;
    if (homeHeader && !(page.is_home || page.slug === 'home')) {
      if (/<header[^>]*data-section-name=["']Header["'][^>]*>[\s\S]*?<\/header>/i.test(nextHtml)) {
        nextHtml = nextHtml.replace(/<header[^>]*data-section-name=["']Header["'][^>]*>[\s\S]*?<\/header>/i, homeHeader);
      } else {
        nextHtml = `${homeHeader}${nextHtml}`;
      }
    }
    const links = buildTemplateNavLinksHtml(pageList, page.slug || '');
    if (!links) return { ...page, html_code: nextHtml };
    if (/<nav class="header-nav"[^>]*>[\s\S]*?<\/nav>/i.test(nextHtml) && nextHtml.includes('data-page=')) {
      return { ...page, html_code: injectOrSyncHeaderNav(nextHtml, links) };
    }
    return { ...page, html_code: injectOrSyncHeaderNav(nextHtml, links) };
  });
};

/** Locate the copyright line inside footer HTML (ecom/store templates use a bare span). */
const findFooterCopyrightEl = (root) => {
  if (!root) return null;
  const footer = root.tagName === 'FOOTER' ? root : root.querySelector('footer') || root;
  const byAttr = footer.querySelector('[data-footer-copyright]');
  if (byAttr) return byAttr;
  const bottom = footer.querySelector('.footer-bottom');
  if (bottom) {
    const inner = bottom.querySelector('[data-footer-copyright], span, p');
    if (inner && /©|&copy;|all rights reserved/i.test(inner.textContent || '')) return inner;
    if (/©|&copy;|all rights reserved/i.test(bottom.textContent || '')) return bottom;
  }
  for (const el of footer.querySelectorAll('span, p')) {
    const text = (el.textContent || '').trim();
    if (!text || text.length > 180) continue;
    if (!/©|&copy;|all rights reserved/i.test(text)) continue;
    if (el.querySelector('a')) continue;
    return el;
  }
  return null;
};

const getSectionBadgeMeta = (section) => {
  const source = `${section?.name || ''} ${section?.id || ''} ${section?.html || ''}`.toLowerCase();

  if (source.includes('hero')) return { label: 'Hero', classes: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/20' };
  if (source.includes('contact') || source.includes('form') || source.includes('booking')) return { label: 'Lead', classes: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/20' };
  if (source.includes('gallery') || source.includes('image') || source.includes('video') || source.includes('slider')) return { label: 'Media', classes: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/20' };
  if (source.includes('product') || source.includes('pricing') || source.includes('checkout') || source.includes('cart') || source.includes('store')) return { label: 'Sales', classes: 'bg-amber-500/15 text-amber-300 border-amber-400/20' };
  if (source.includes('faq') || source.includes('accordion')) return { label: 'FAQ', classes: 'bg-blue-500/15 text-blue-300 border-blue-400/20' };
  if (source.includes('testimonial') || source.includes('review')) return { label: 'Social Proof', classes: 'bg-pink-500/15 text-pink-300 border-pink-400/20' };
  if (source.includes('footer')) return { label: 'Footer', classes: 'bg-zinc-500/15 text-zinc-300 border-zinc-400/20' };
  if (source.includes('feature')) return { label: 'Features', classes: 'bg-violet-500/15 text-violet-300 border-violet-400/20' };

  return { label: 'Content', classes: 'bg-zinc-700/50 text-zinc-300 border-zinc-600/40' };
};

// ==================== MAIN COMPONENT ====================

const TemplateEditor = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const operatorOpenedRef = useRef(false);
  const iframeRef = useRef(null);
  const previewBlobUrlRef = useRef('');
  const videoHtmlRepairedRef = useRef(false);
  const handleImageUpdateRef = useRef(null);
  const htmlEnvelopeRef = useRef({ prefix: '', wrapperClass: null });
  const composeSectionHtml = useCallback(
    (sectionList) => sectionsToHtml(sectionList, htmlEnvelopeRef.current),
    [],
  );
  // Ref tracks latest HTML without triggering iframe reloads (used by toolbar)
  const htmlContentRef = useRef('');

  // Editor state (must be declared before useEffect that uses it)
  const [htmlContent, setHtmlContent] = useState('');
  const [cssContent, setCssContent] = useState('');
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [ecomSettings, setEcomSettings] = useState(null);
  const [editorSurfaces, setEditorSurfaces] = useState(null);

  // Keep ref in sync with state changes (page load, undo/redo, etc.)
  useEffect(() => {
    htmlContentRef.current = htmlContent;
  }, [htmlContent]);
  
  // Core state
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // Pages state
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [showAddPage, setShowAddPage] = useState(false);
  const [newPageName, setNewPageName] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [newPageCategory, setNewPageCategory] = useState('main');
  const [expandedCategories, setExpandedCategories] = useState(['main']);
  
  // Page settings modal
  const [showPageSettings, setShowPageSettings] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [editPageName, setEditPageName] = useState('');
  const [editPageSlug, setEditPageSlug] = useState('');
  const [editPageNavVisible, setEditPageNavVisible] = useState(true);
  const [editPageCategory, setEditPageCategory] = useState('main');
  
  // Products state
  const [products, setProducts] = useState([]);
  const [liveServices, setLiveServices] = useState([]);
  const [widgetConfigs, setWidgetConfigs] = useState([]);
  const [widgetInstances, setWidgetInstances] = useState([]);
  
  // Design customization
  const [primaryColor, setPrimaryColor] = useState('#8b5cf6');
  const [secondaryColor, setSecondaryColor] = useState('#10b981');
  const [fontFamily, setFontFamily] = useState('Inter');
  
  // Banner images modal
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [bannerCategory, setBannerCategory] = useState('all');
  const [editingImageSection, setEditingImageSection] = useState(null);
  
  // Publish settings
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [customSubdomain, setCustomSubdomain] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [availableDomains, setAvailableDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  
  // History for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // UI state
  const [previewMode, setPreviewMode] = useState('desktop');
  const [activePanel, setActivePanel] = useState('pages');
  const [widgetFilter, setWidgetFilter] = useState('all');
  const [showDesignPanel, setShowDesignPanel] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [selectedTextElement, setSelectedTextElement] = useState(null);
  const [selectedEditorId, setSelectedEditorId] = useState('');
  const selectedEditorIdRef = useRef('');
  const htmlHistoryTimerRef = useRef(null);
  const [toolbarColor, setToolbarColor] = useState('#111827');
  const [toolbarBgColor, setToolbarBgColor] = useState('#8b5cf6');
  const [toolbarLinkUrl, setToolbarLinkUrl] = useState('');
  const [toolbarPageSlug, setToolbarPageSlug] = useState('');
  const [toolbarIsButton, setToolbarIsButton] = useState(false);
  const [toolbarFontSize, setToolbarFontSize] = useState('');
  const [toolbarFontFamily, setToolbarFontFamily] = useState('');
  const [toolbarPosition, setToolbarPosition] = useState({ visible: false, top: 0, left: 0 });
  const [showAITextModal, setShowAITextModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [imageAiPrompt, setImageAiPrompt] = useState('');
  const [showSectionEditModal, setShowSectionEditModal] = useState(false);
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const imageUploadRef = useRef(null);
  const [imageAiGenerating, setImageAiGenerating] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [logoGenerating, setLogoGenerating] = useState(false);
  const [showBrandPopover, setShowBrandPopover] = useState(false);
  const [brandDraftName, setBrandDraftName] = useState('');
  const [brandSaving, setBrandSaving] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(800);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const getPreviewHtmlRef = useRef(() => '');

  const token = localStorage.getItem('token');
  const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
    ? process.env.REACT_APP_BACKEND_URL
    : '';
  const editorApiBase = useMemo(() => resolveApiBase(), [API_URL]);
  const brandName = website?.name || 'snaglms';
  const brandLogoFallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(brandName)}&size=128&background=${(website?.primary_color || '#8b5cf6').replace('#', '')}&color=fff&bold=true&format=svg&rounded=true`;

  useEffect(() => {
    setBrandDraftName(brandName);
  }, [brandName]);

  // ==================== APP SCHEMA (schema-driven architecture) ====================
  const [appSchema, setAppSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState(null);
  const [showSchemaPanel, setShowSchemaPanel] = useState(false);

  const fetchAppSchema = useCallback(async () => {
    setSchemaLoading(true);
    setSchemaError(null);
    try {
      const response = await fetch(`${API_URL}/api/projects/${websiteId}/schema`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to load schema (${response.status})`);
      }
      const data = await response.json();
      setAppSchema(data.schema || null);
    } catch (e) {
      setSchemaError(e?.message || 'Failed to load schema');
      setAppSchema(null);
    } finally {
      setSchemaLoading(false);
    }
  }, [API_URL, token, websiteId]);


  // ==================== SCHEMA WIDGET RENDERING (preview-only) ====================
  const renderSchemaWidgetsIntoHtml = useCallback((baseHtml) => {
    if (!appSchema || !Array.isArray(appSchema.pages)) return baseHtml;

    // Find a schema page matching the currently selected page slug.
    // Current templates use slugs, schema uses route.
    const currentSlug = currentPage?.slug;
    if (!currentSlug) return baseHtml;

    const normalizedSlug = currentSlug.startsWith('/') ? currentSlug : `/${currentSlug}`;
    const schemaPage =
      appSchema.pages.find((p) => (p.route || '') === normalizedSlug) ||
      appSchema.pages.find((p) => (p.route || '') === currentSlug) ||
      appSchema.pages.find((p) => `/${(p.route || '').replace(/^\//, '')}` === normalizedSlug);

    if (!schemaPage) return baseHtml;

    // v2 shape: sections -> widgets (preferred)
    const sections = Array.isArray(schemaPage.sections) ? schemaPage.sections : [];

    // v1 fallback: page.widgets
    const pageWidgets = Array.isArray(schemaPage.widgets) ? schemaPage.widgets : [];

    const widgets = sections.length
      ? sections.flatMap((s) => (Array.isArray(s.widgets) ? s.widgets : []))
      : pageWidgets;

    if (!widgets.length) return baseHtml;

    let widgetsHtml = '';

    widgets.forEach((w) => {
      if (!w || !w.type) return;

      if (w.type === 'form') {
        const props = w.props || {};
        const fields = Array.isArray(props.fields) ? props.fields : [];
        const resourceName = props.resource || 'contacts';
        const title = props.title || 'Contact Us';
        const submitText = props.submit_text || 'Send message';

        const inputsHtml = fields
          .map((f) => {
            const name = f?.name;
            if (!name) return '';
            const label = f?.label || (name.charAt(0).toUpperCase() + name.slice(1));
            const required = f?.required ? 'required' : '';
            const type = (f?.type || 'string').toLowerCase();

            if (type === 'text' || type === 'textarea' || name === 'message') {
              return `
                <label style="display:block; font-weight:600; margin-bottom:6px;">${label}${required ? ' *' : ''}</label>
                <textarea name="${name}" ${required} style="width:100%; padding:12px 14px; border:1px solid #e5e7eb; border-radius:12px; min-height:120px; resize:vertical; font-family: inherit;"></textarea>
              `;
            }

            const inputType = name === 'email' ? 'email' : 'text';
            return `
              <label style="display:block; font-weight:600; margin-bottom:6px;">${label}${required ? ' *' : ''}</label>
              <input type="${inputType}" name="${name}" ${required} style="width:100%; padding:12px 14px; border:1px solid #e5e7eb; border-radius:12px; font-family: inherit;" />
            `;
          })
          .join('');

        widgetsHtml += `
          <section class="widget-section" data-schema-widget="form" style="padding: 60px 20px; background: #f8fafc;">
            <div style="max-width: 720px; margin: 0 auto; background: white; border-radius: 24px; padding: 28px; box-shadow: 0 10px 30px rgba(0,0,0,0.08);">
              <h2 style="font-size: 1.8rem; font-weight: 800; margin-bottom: 6px; color: #0f172a;">${title}</h2>
              <p style="color: #64748b; margin-bottom: 18px;">We’ll get back to you shortly.</p>

              <form data-schema-form="true" data-schema-resource="${resourceName}" style="display:grid; gap: 14px;">
                ${inputsHtml}
                <button type="submit" style="margin-top:8px; padding: 12px 16px; border: none; border-radius: 999px; background: linear-gradient(135deg, var(--primary-color), var(--secondary-color)); color: white; font-weight: 700; cursor: pointer;">${submitText}</button>
                <div data-schema-form-message style="font-size: 0.95rem;"></div>
              </form>
            </div>
          </section>
        `;
      }
    });

    if (!widgetsHtml) return baseHtml;

    // Insert widgets at end of page content for now
    return `${baseHtml}\n${widgetsHtml}`;
  }, [appSchema, currentPage?.slug]);

  // AI Page Assistant state (used to force refresh after apply)
  const handleAIApply = () => {
    fetchPages();
    fetchAppSchema();
  };

  const previewWidths = { desktop: '100%', tablet: '768px', mobile: '375px' };

  // Expose API URL and website ID to the iframe for site-level widgets (auth, forms, etc.)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.API_URL = API_URL;
      window.WEBSITE_ID = websiteId;
    }
  }, [API_URL, websiteId]);

  useEffect(() => {
    fetchWebsite();
  }, [websiteId]);

  useEffect(() => {
    fetchAppSchema();
  }, [fetchAppSchema]);

  const fetchWebsite = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load');
      const data = await response.json();
      setWebsite(data);
      setCssContent(data.css_code || '');
      setSeoTitle(data.seo_title || data.name || '');
      setSeoDescription(data.seo_description || '');
      setPrimaryColor(data.primary_color || '#8b5cf6');
      setSecondaryColor(data.secondary_color || '#10b981');
      setFontFamily(data.font_family || 'Inter');
      setCustomSubdomain(data.slug || slugifyWebsiteName(data.name) || '');
      setCustomDomain(data.custom_domain || '');
      try {
        const surfacesRes = await fetch(`${API_URL}/api/websites/${websiteId}/editor-surfaces`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (surfacesRes.ok) {
          setEditorSurfaces(ensureCrmInEditorSurfaces(await surfacesRes.json(), data));
        } else {
          setEditorSurfaces(fallbackEditorSurfaces(data));
        }
      } catch {
        setEditorSurfaces(fallbackEditorSurfaces(data));
      }
      await Promise.all([fetchPages(), fetchProducts(), fetchEcommerceSettings(), fetchLiveServices(), fetchWidgetConfigs(), fetchWidgetInstancesList()]);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load website');
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const pagesData = repairPagesHeaderNav(
          (await response.json()).map((p) => ({
            ...p,
            html_code: repairBrokenUnsplashUrls(p.html_code || ''),
          })),
        );
        setPages(pagesData);
        const hasStoreNav = pagesData.some((p) => p.slug === 'products' || p.slug === 'home');
        if (hasStoreNav) {
          setExpandedCategories(['navigation', 'store-flows']);
        }
        const operatorPage = location.state?.openOperatorDashboard
          ? pagesData.find((p) => p.slug === 'admin-dashboard')
          : null;
        const urlPageSlug = new URLSearchParams(window.location.search).get('page');
        const urlPage = urlPageSlug
          ? pagesData.find((p) => p.slug === urlPageSlug)
          : null;
        const initialPage = operatorPage
          || urlPage
          || pagesData.find((p) => p.is_home && p.name)
          || pagesData.find((p) => p.is_home && !p.resource)
          || pagesData.find((p) => p.slug === 'home' && p.name)
          || pagesData[0];
        if (initialPage) {
          const repairedHtml = repairBrokenUnsplashUrls(initialPage.html_code || '');
          setCurrentPage(initialPage);
          htmlContentRef.current = repairedHtml;
          const { sections: parsed, envelope } = parseHtmlStructure(repairedHtml);
          htmlEnvelopeRef.current = envelope;
          setHtmlContent(repairedHtml);
          setSections(parsed);
          setSelectedSectionId(parsed[0]?.id || null);
          setHistory([{ html: repairedHtml, css: '' }]);
          setHistoryIndex(0);
          setPreviewRefreshKey((key) => key + 1);
          if (operatorPage) {
            operatorOpenedRef.current = true;
            setActivePanel('pages');
            setExpandedCategories((prev) => (prev.includes('admin') ? prev : [...prev, 'admin']));
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch pages:', error);
    }
  };

  const fetchProducts = useCallback(async () => {
    if (!websiteId || !token) return [];
    const merged = [];
    let schemaLoaded = false;
    try {
      const schemaRes = await fetch(`${API_URL}/api/sites/${websiteId}/resources/products?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (schemaRes.ok) {
        schemaLoaded = true;
        const data = await schemaRes.json();
        if (Array.isArray(data.items)) {
          merged.push(...data.items.map(normalizeSchemaProduct));
        }
      }
    } catch (error) {
      console.error('Failed to fetch schema products:', error);
    }
    if (!schemaLoaded) {
      try {
        const response = await fetch(`${API_URL}/api/websites/${websiteId}/products`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const productsData = await response.json();
          if (Array.isArray(productsData)) {
            merged.push(...productsData.map(normalizeSchemaProduct));
          }
        }
      } catch (error) {
        console.error('Failed to fetch legacy products:', error);
      }
    }
    const active = merged.filter(isOperateResourceActive);
    setProducts(active);
    return active;
  }, [API_URL, token, websiteId]);

  const fetchLiveServices = useCallback(async () => {
    if (!websiteId || !token) return [];
    const merged = [];
    let schemaLoaded = false;
    try {
      const schemaRes = await fetch(`${API_URL}/api/sites/${websiteId}/resources/services?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (schemaRes.ok) {
        schemaLoaded = true;
        const data = await schemaRes.json();
        if (Array.isArray(data.items)) {
          merged.push(...data.items.filter(isOperateResourceActive));
        }
      }
    } catch (error) {
      console.error('Failed to fetch schema services:', error);
    }
    if (!schemaLoaded) {
      try {
        const legacyRes = await fetch(`${API_URL}/api/websites/${websiteId}/services`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (legacyRes.ok) {
          const legacy = await legacyRes.json();
          if (Array.isArray(legacy)) merged.push(...legacy.filter(isOperateResourceActive));
        }
      } catch (error) {
        console.error('Failed to fetch booking services:', error);
      }
    }
    setLiveServices(merged);
    return merged;
  }, [API_URL, token, websiteId]);

  const fetchWidgetConfigs = useCallback(async () => {
    if (!websiteId) return [];
    try {
      const response = await fetch(`${API_URL}/api/sites/${websiteId}/resources/widget_configs?limit=100`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        const items = Array.isArray(data.items) ? data.items : [];
        setWidgetConfigs(items);
        return items;
      }
    } catch (error) {
      console.error('Failed to fetch widget configs:', error);
    }
    return [];
  }, [API_URL, token, websiteId]);

  const fetchWidgetInstancesList = useCallback(async () => {
    if (!websiteId) return [];
    try {
      await syncWidgetInstancesFromPages(API_URL, token, websiteId);
    } catch {
      /* sync is best-effort */
    }
    const items = await fetchWidgetInstances(API_URL, token, websiteId);
    setWidgetInstances(items);
    return items;
  }, [API_URL, token, websiteId]);

  const pushOperatePreviewRefresh = useCallback((payload) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return false;
    try {
      iframe.contentWindow.postMessage({ type: 'operate-preview-refresh', ...payload }, '*');
      return true;
    } catch {
      return false;
    }
  }, []);

  // Refresh Operate-backed catalog data when returning from another tab (e.g. Operate).
  useEffect(() => {
    const operateChannel = typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('snagcart-operate-sync')
      : null;
    const refreshOperateData = async ({ reloadPreview = false, resource = '' } = {}) => {
      const [products, services, widgetConfigs, widgetInstances] = await Promise.all([
        fetchProducts(),
        fetchLiveServices(),
        fetchWidgetConfigs(),
        fetchWidgetInstancesList(),
      ]);
      const pushed = pushOperatePreviewRefresh({
        resource,
        products,
        services,
        widgetConfigs,
        widgetInstances,
      });
      if (reloadPreview && !pushed) {
        setPreviewRefreshKey((key) => key + 1);
      }
    };
    const onLiveSync = (event) => {
      const resource = event?.data?.resource || '';
      refreshOperateData({ reloadPreview: false, resource });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshOperateData({ reloadPreview: true });
    };
    const onMessage = (event) => {
      if (event.data?.type === 'operator-data-changed') onLiveSync(event);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('message', onMessage);
    window.addEventListener('focus', onVisible);
    operateChannel?.addEventListener('message', (event) => {
      if (event.data?.type === 'operator-data-changed') onLiveSync(event);
    });
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('focus', onVisible);
      operateChannel?.close();
    };
  }, [fetchLiveServices, fetchProducts, fetchWidgetConfigs, fetchWidgetInstancesList, pushOperatePreviewRefresh]);


  const fetchEcommerceSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/ecommerce-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setEcomSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch ecommerce settings:', error);
    }
  };

  // Page CRUD operations
  const createPage = async () => {
    if (!newPageName.trim()) {
      toast.error('Page name required');
      return;
    }

    const slug =
      newPageSlug.trim() ||
      newPageName
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');

    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newPageName,
          slug,
          is_home: pages.length === 0,
          category: newPageCategory,
          nav_visible: true,
          html_code: `<section class="widget-section" data-section-id="${slug}" data-section-name="${newPageName}" data-show-nav="true" style="padding:120px 24px;text-align:center;background:#fff;color:#111827;"><h1 style="font-size:3rem;margin-bottom:16px;">${newPageName}</h1><p style="font-size:1.1rem;color:#64748b;max-width:680px;margin:0 auto;">Add content, widgets, and synced operator data to this page.</p></section>`,
          css_code: '',
          js_code: '',
        }),
      });

      if (!response.ok) {
        let detail = 'Failed to create page';
        try {
          const err = await response.json();
          if (err && (err.detail || err.message)) {
            detail = err.detail || err.message;
          }
        } catch {
          // ignore parse error
        }
        console.error('Create page failed', response.status, detail);
        toast.error(detail);
        return;
      }

      const newPage = await response.json();
      setPages([...pages, newPage]);
      setCurrentPage(newPage);
      commitPageHtml(repairBrokenUnsplashUrls(newPage.html_code || ''), { refreshPreview: true });
      setNewPageName('');
      setNewPageSlug('');
      setShowAddPage(false);
      toast.success(`Page "${newPageName}" created!`);
    } catch (error) {
      console.error('Create page error', error);
      toast.error('Failed to create page');
    }
  };

  const handleGenerateLogo = async () => {
    if (!website || !token) return;
    try {
      setLogoGenerating(true);
      const prompt = (brandDraftName || brandName || 'Store').trim();
      const res = await fetch(`${API_URL}/api/ai/generate-logo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt,
          style: 'modern minimalist logo mark',
          colors: [primaryColor || '#8b5cf6', secondaryColor || '#10b981'],
          website_id: websiteId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.logo_url) {
        // Save logo URL into website settings so it persists
        await fetch(`${API_URL}/api/websites/${websiteId}/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ logo_url: data.logo_url }),
        });
        setWebsite((prev) => (prev ? { ...prev, logo_url: data.logo_url } : prev));
        toast.success('AI logo generated');
      } else {
        toast.error((data && (data.detail || data.message)) || 'Failed to generate logo');
      }
    } catch (err) {
      console.error('AI logo error', err);
      toast.error('Error generating logo');
    } finally {
      setLogoGenerating(false);
    }
  };

  const handleSaveBrandSettings = async () => {
    if (!website || !token) return;
    try {
      setBrandSaving(true);
      const nextName = (brandDraftName || '').trim() || brandName || 'Website';
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: nextName }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Failed to save brand settings');
      setWebsite(data);
      const previousName = (website?.name || brandName || '').trim();
      if (previousName && nextName && previousName !== nextName) {
        const escaped = previousName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const updatedHtml = getCurrentPageHtml().replace(new RegExp(escaped, 'g'), nextName);
        commitPageHtml(updatedHtml, { saveHistory: true, refreshPreview: true });
        if (currentPage) {
          await fetch(`${API_URL}/api/websites/${websiteId}/pages/${currentPage.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ html_code: updatedHtml }),
          });
        }
        await fetch(`${API_URL}/api/websites/${websiteId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ html_code: updatedHtml }),
        });
        saveToHistory(updatedHtml, cssContent);
      }
      setShowBrandPopover(false);
      toast.success('Brand updated');
    } catch (error) {
      console.error('Save brand settings error', error);
      toast.error(error.message || 'Failed to save brand settings');
    } finally {
      setBrandSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!website || !token) return;
    try {
      setBrandSaving(true);
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ logo_url: '' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Failed to remove logo');
      setWebsite(data);
      toast.success('Logo removed');
    } catch (error) {
      console.error('Remove logo error', error);
      toast.error(error.message || 'Failed to remove logo');
    } finally {
      setBrandSaving(false);
    }
  };

  const deletePage = async (pageId) => {
    if (pages.length <= 1) { toast.error("Cannot delete last page"); return; }
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/pages/${pageId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      const updated = pages.filter(p => p.id !== pageId);
      setPages(updated);
      if (currentPage?.id === pageId) {
        setCurrentPage(updated[0]);
        commitPageHtml(repairBrokenUnsplashUrls(updated[0]?.html_code || ''), { refreshPreview: true });
      }
      toast.success('Page deleted');
    } catch (error) { toast.error('Failed to delete'); }
  };

  const switchPage = useCallback((page, options = {}) => {
    const previous = currentPage;
    const previousHtml = htmlContentRef.current || htmlContent;
    if (previous && previous.id !== page.id && !options.skipSave) {
      const pageId = previous.id;
      const htmlToSave = previousHtml;
      (async () => {
        try {
          await fetch(`${API_URL}/api/websites/${websiteId}/pages/${pageId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ html_code: htmlToSave }),
          });
          setPages((prev) => prev.map((p) => (p.id === pageId ? { ...p, html_code: htmlToSave } : p)));
        } catch (error) {
          console.error('Background save failed:', error);
        }
      })();
    }
    setCurrentPage(page);
    const pageHtml = repairBrokenUnsplashUrls(page.html_code || '');
    htmlContentRef.current = pageHtml;
    const { sections: parsed, envelope } = parseHtmlStructure(pageHtml);
    htmlEnvelopeRef.current = envelope;
    setHtmlContent(pageHtml);
    setSections(parsed);
    setSelectedSectionId(parsed[0]?.id || null);
    setHistory([{ html: pageHtml, css: cssContent }]);
    setHistoryIndex(0);
    setIframeHeight(800);
    setPreviewRefreshKey((key) => key + 1);
  }, [API_URL, websiteId, token, currentPage, htmlContent, cssContent]);

  const saveCurrentPage = async () => {
    if (!currentPage) return;
    try {
      // Use ref if it has toolbar edits that haven't triggered a re-render yet
      const latestHtml = repairBrokenUnsplashUrls(htmlContentRef.current || htmlContent);
      await fetch(`${API_URL}/api/websites/${websiteId}/pages/${currentPage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ html_code: latestHtml })
      });
      setPages(pages.map(p => p.id === currentPage.id ? { ...p, html_code: latestHtml } : p));

      // Sync to db.websites.html_code so WebsiteEditor / live preview stay in sync
      await fetch(`${API_URL}/api/websites/${websiteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ html_code: latestHtml })
      });
    } catch (error) { console.error('Save failed:', error); }
  };

  const openPageSettings = (page) => {
    setEditingPage(page);
    setEditPageName(page.name);
    setEditPageSlug(page.slug);
    setEditPageNavVisible(page.nav_visible !== false);
    setEditPageCategory(page.category || 'main');
    setShowPageSettings(true);
  };

  const savePageSettings = async () => {
    if (!editingPage) return;
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/pages/${editingPage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: editPageName,
          slug: editPageSlug,
          nav_visible: editPageNavVisible,
          category: editPageCategory
        })
      });
      if (response.ok) {
        const updatedPage = await response.json();
        const nextPages = repairPagesHeaderNav(
          pages.map((p) => (p.id === editingPage.id ? { ...p, ...updatedPage } : p)),
        );
        setPages(nextPages);
        const refreshedCurrent = nextPages.find((p) => p.id === currentPage?.id);
        if (refreshedCurrent) {
          setCurrentPage(refreshedCurrent);
          // Keep preview source in sync when header links were rewritten.
          if (refreshedCurrent.id === currentPage?.id && refreshedCurrent.html_code != null) {
            setHtmlContent(refreshedCurrent.html_code);
            htmlContentRef.current = refreshedCurrent.html_code;
          }
        }
        setShowPageSettings(false);
        setPreviewRefreshKey((key) => key + 1);
        toast.success('Page settings saved!');
      }
    } catch (error) { toast.error('Failed to save page settings'); }
  };

  const setAsHomePage = async (pageId) => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/pages/${pageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ is_home: true })
      });
      if (response.ok) {
        setPages(pages.map(p => ({ ...p, is_home: p.id === pageId })));
        toast.success('Home page updated!');
      }
    } catch (error) { toast.error('Failed to set home page'); }
  };

  // History management
  const saveToHistory = useCallback((html, css) => {
    const newHistory = history.slice(0, historyIndex + 1);
    if (newHistory.length && newHistory[newHistory.length - 1].html === html && newHistory[newHistory.length - 1].css === css) {
      return;
    }
    newHistory.push({ html, css });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const getCurrentPageHtml = useCallback(() => {
    const baseHtml = htmlContentRef.current || htmlContent;
    const iframeDoc = iframeRef.current?.contentDocument;
    if (!iframeDoc) return repairBrokenUnsplashUrls(baseHtml || '');
    try {
      return mergeIframeSectionsIntoHtml(baseHtml, iframeDoc, widgetInstances);
    } catch (error) {
      console.error('Failed to merge iframe HTML', error);
      return repairBrokenUnsplashUrls(baseHtml || '');
    }
  }, [htmlContent, widgetInstances]);

  /** Keep page HTML, section list, and envelope in sync (single source of truth). */
  const commitPageHtml = useCallback((rawHtml, options = {}) => {
    const {
      saveHistory: shouldSave = false,
      refreshPreview = false,
      selectSectionId = null,
    } = options;
    const html = repairBrokenUnsplashUrls(rawHtml || '');
    const { sections: parsed, envelope } = parseHtmlStructure(html);
    htmlEnvelopeRef.current = envelope;
    htmlContentRef.current = html;
    setHtmlContent(html);
    setSections(parsed);
    setSelectedSectionId((prev) => {
      if (selectSectionId) return selectSectionId;
      if (!parsed.length) return null;
      if (prev && parsed.some((s) => s.id === prev)) return prev;
      return parsed[0].id;
    });
    if (shouldSave) saveToHistory(html, cssContent);
    if (refreshPreview) setPreviewRefreshKey((key) => key + 1);
    return { html, sections: parsed };
  }, [cssContent, saveToHistory]);

  useEffect(() => {
    if (videoHtmlRepairedRef.current || !widgetInstances.length || !htmlContent) return;
    if (!htmlContent.includes('data-editor-video-host')) return;
    const repaired = repairStoredVideoSections(htmlContent, widgetInstances);
    if (repaired === htmlContent) return;
    videoHtmlRepairedRef.current = true;
    commitPageHtml(repaired, { refreshPreview: true });
  }, [widgetInstances, htmlContent, commitPageHtml]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      const nextIndex = historyIndex - 1;
      setHistoryIndex(nextIndex);
      setCssContent(history[nextIndex].css || cssContent);
      commitPageHtml(history[nextIndex].html, { refreshPreview: true });
      toast.info('Undone');
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setCssContent(history[nextIndex].css || cssContent);
      commitPageHtml(history[nextIndex].html, { refreshPreview: true });
      toast.info('Redone');
    }
  };

  const applyQuizConfigToHtml = useCallback((html) => {
    const config = widgetConfigs.find((item) => (item.widget_key || '').toLowerCase() === 'quiz' || (item.title || '').toLowerCase().includes('quiz'));
    if (!html || !config || typeof document === 'undefined') return html;
    const splitOptions = (value) => String(value || '').split(/\n|\|/).map((v) => v.trim()).filter(Boolean);
    const esc = (value) => String(value ?? '').replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
    const questionHtml = [1, 2, 3].map((num) => {
      const question = config[`question_${num}`];
      const options = splitOptions(config[`options_${num}`]);
      if (!question || !options.length) return '';
      return `<div data-quiz-question-block style="display:grid;gap:12px;">
        <h3 style="font-size:1.1rem;font-weight:700;color:#4f46e5;">Question ${num} of 3</h3>
        <p style="font-weight:600;color:#334155;">${esc(question)}</p>
        <div style="display:grid;gap:10px;">
          ${options.map((opt, idx) => `<label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;"><input type="radio" name="answer_${num}" value="${esc(opt)}" ${idx === 0 ? 'required' : ''}><span style="color:#1e293b;font-weight:600;">${esc(opt)}</span></label>`).join('')}
        </div>
      </div>`;
    }).join('');
    if (!questionHtml) return html;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    wrapper.querySelectorAll('[data-quiz-widget="true"]').forEach((section) => {
      const title = section.querySelector('[data-quiz-title]');
      const subtitle = section.querySelector('[data-quiz-subtitle]');
      const questions = section.querySelector('[data-quiz-questions]');
      const submit = section.querySelector('[data-quiz-submit]');
      if (title) title.textContent = config.title || 'Quick Quiz';
      if (subtitle) subtitle.textContent = config.subtitle || '';
      if (questions) questions.innerHTML = questionHtml;
      if (submit) submit.textContent = config.submit_label || 'Submit';
    });
    return wrapper.innerHTML;
  }, [widgetConfigs]);

  // Widget operations
  const addWidget = (widget) => {
    let widgetHtml = widget.html.replace(/\{\{ID\}\}/g, generateId());
    const widgetBindings = editorSurfaces?.widget_bindings
      || fallbackWidgetBindings(editorSurfaces?.genre_id || website?.genre_id || website?.category);

    if (widget.id === 'form') {
      const resource = editorSurfaces?.contact_resource || 'inquiries';
      widgetHtml = widgetHtml.replace(
        'data-schema-resource="inquiries"',
        `data-schema-resource="${resource}"`
      );
    } else if (widgetBindings[widget.id]) {
      widgetHtml = applyWidgetBinding(widgetHtml, widget.id, widgetBindings[widget.id]);
    }

    const instanceId = extractInstanceIdFromHtml(widgetHtml);
    if (instanceId && isConfigWidget(widget.id)) {
      widgetHtml = tagWidgetInstanceHtml(widgetHtml, widget.id, instanceId);
    }

    // Turn the widget HTML into a section object so the sections state stays the source of truth
    const parsedNewSections = parseHtmlToSections(widgetHtml);
    const newSection = parsedNewSections[0] || {
      id: `section-${generateId()}`,
      name: widget.name,
      html: widgetHtml,
      type: 'section',
      showInNav: !!widget.showInNav,
    };

    const baseHtml = getCurrentPageHtml();
    let newHtml = appendSectionToPageHtml(baseHtml, sections, newSection, htmlEnvelopeRef, composeSectionHtml);
    if (widget.id === 'quiz') {
      newHtml = applyQuizConfigToHtml(newHtml);
    }
    commitPageHtml(newHtml, { saveHistory: true, refreshPreview: true, selectSectionId: newSection.id });

    const pageSlug = pages.find((p) => p.id === currentPage)?.slug || '';
    if (instanceId && isConfigWidget(widget.id)) {
      registerWidgetInstance(API_URL, token, websiteId, {
        instance_id: instanceId,
        widget_type: widget.id,
        widget_name: widget.name,
        page_slug: pageSlug,
      }).then(async (item) => {
        if (item) {
          setWidgetInstances((prev) => {
            const next = [item, ...prev.filter((row) => row.instance_id !== instanceId)];
            return next;
          });
          setPreviewRefreshKey((k) => k + 1);
        } else {
          const synced = await syncWidgetInstancesFromPages(API_URL, token, websiteId);
          if (synced.length) setWidgetInstances(synced);
          else fetchWidgetInstancesList();
          setPreviewRefreshKey((k) => k + 1);
        }
      });
    }

    const widgetResourceMap = {
      'comment-area': 'comments',
      newsletter: 'subscribers',
      booking: 'bookings',
      quiz: 'widgets',
    };
    const widgetResource = widget.id === 'form'
      ? (editorSurfaces?.contact_resource || 'inquiries')
      : (widgetResourceMap[widget.id] || widgetBindings[widget.id]?.configResource || widgetBindings[widget.id]?.resource || widget.id);
    try {
      const existing = JSON.parse(localStorage.getItem(`operator_widget_sections_${websiteId}`) || '[]');
      const next = [
        { id: newSection.id, widgetId: widget.id, name: widget.name, resource: widget.id === 'quiz' ? 'widgets' : widgetResource, configResource: widgetResource, createdAt: new Date().toISOString() },
        ...existing.filter((entry) => entry.id !== newSection.id),
      ].slice(0, 30);
      localStorage.setItem(`operator_widget_sections_${websiteId}`, JSON.stringify(next));
    } catch { /* optional widget/operator metadata */ }
    toast.success(`${widget.name} added! Configure it in Widget Dashboard.`);
  };

  // Section operations
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;

    const { sections: currentSections, envelope } = resolveSectionsForEdit(
      getCurrentPageHtml(),
      sections,
      htmlEnvelopeRef,
    );
    htmlEnvelopeRef.current = envelope;
    const newSections = Array.from(currentSections);
    const [removed] = newSections.splice(sourceIndex, 1);
    newSections.splice(destIndex, 0, removed);
    
    commitPageHtml(composeSectionHtml(newSections), { saveHistory: true, refreshPreview: true });
    toast.success('Section reordered!');
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const { sections: currentSections, envelope } = resolveSectionsForEdit(
      getCurrentPageHtml(),
      sections,
      htmlEnvelopeRef,
    );
    htmlEnvelopeRef.current = envelope;
    if (newIndex < 0 || newIndex >= currentSections.length) return;
    
    const newSections = Array.from(currentSections);
    const [removed] = newSections.splice(index, 1);
    newSections.splice(newIndex, 0, removed);
    
    commitPageHtml(composeSectionHtml(newSections), { saveHistory: true, refreshPreview: true });
    toast.success('Section moved!');
  };

  const deleteSection = (index) => {
    const { sections: currentSections, envelope } = parseHtmlStructure(getCurrentPageHtml());
    htmlEnvelopeRef.current = envelope;
    const removedSection = currentSections[index];
    const removedId = removedSection?.id;
    const instanceId = extractInstanceIdFromHtml(removedSection?.html || '');
    const newSections = currentSections.filter((_, i) => i !== index);
    commitPageHtml(composeSectionHtml(newSections), {
      saveHistory: true,
      refreshPreview: true,
      selectSectionId: removedId === selectedSectionId ? (newSections[0]?.id || null) : null,
    });
    if (instanceId) {
      deactivateWidgetInstance(API_URL, token, websiteId, instanceId).then(() => {
        setWidgetInstances((prev) => prev.filter((row) => row.instance_id !== instanceId));
      });
    }
    toast.success('Section deleted!');
  };

  const selectSectionById = (id) => {
    setSelectedSectionId(id);
  };

  const toggleSectionNav = (index) => {
    const { sections: currentSections, envelope } = parseHtmlStructure(getCurrentPageHtml());
    htmlEnvelopeRef.current = envelope;
    const newSections = [...currentSections];
    const section = newSections[index];
    if (!section) return;
    const newShowNav = !section.showInNav;
    
    // Update the HTML with the new data-show-nav value
    const parser = new DOMParser();
    const doc = parser.parseFromString(section.html, 'text/html');
    const element = doc.body.firstChild;
    if (element) {
      element.setAttribute('data-show-nav', newShowNav.toString());
      section.html = element.outerHTML;
      section.showInNav = newShowNav;
    }
    
    commitPageHtml(composeSectionHtml(newSections), { saveHistory: true, refreshPreview: true });
    toast.success(newShowNav ? 'Section will show in navigation' : 'Section hidden from navigation');
  };

  const updateSectionDisplayName = (index, newName) => {
    setSections((prev) => prev.map((section, i) => (
      i === index ? { ...section, name: newName } : section
    )));
  };

  const commitSectionRename = (index) => {
    const { sections: currentSections, envelope } = parseHtmlStructure(getCurrentPageHtml());
    htmlEnvelopeRef.current = envelope;
    const newSections = [...currentSections];
    const section = newSections[index];
    if (!section) return;

    const trimmed = (sections[index]?.name || section.name || '').trim();
    const nameToUse = trimmed || section.name || `Section ${index + 1}`;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(section.html, 'text/html');
      const el = doc.body.firstChild;
      if (el) {
        el.setAttribute('data-section-name', nameToUse);
        section.html = el.outerHTML;
      }
    } catch (e) {
      // Fallback: just update the name in state
    }

    section.name = nameToUse;
    commitPageHtml(composeSectionHtml(newSections), { saveHistory: true });
  };

  const addBlankSection = (sectionType) => {
    const sectionHtml = buildBlankSectionHtml(sectionType);
    const parsedNewSections = parseHtmlToSections(sectionHtml);
    const newSection = parsedNewSections[0] || {
      id: `section-${generateId()}`,
      name: sectionType.name,
      html: sectionHtml,
      type: 'section',
      showInNav: false,
    };

    const newHtml = appendSectionToPageHtml(
      getCurrentPageHtml(),
      sections,
      newSection,
      htmlEnvelopeRef,
      composeSectionHtml,
    );
    commitPageHtml(newHtml, {
      saveHistory: true,
      refreshPreview: true,
      selectSectionId: newSection.id,
    });
    setShowAddSectionModal(false);
    toast.success(`Added ${sectionType.name}`);
  };


  const getSectionConfigWidgetType = (section) => {
    const html = section?.html || '';
    const typeMatch = html.match(/data-widget-type="([^"]+)"/);
    if (typeMatch && isConfigWidget(typeMatch[1])) return typeMatch[1];
    const inferred = inferWidgetTypeFromSectionHtml(html);
    if (inferred && isConfigWidget(inferred)) return inferred;
    return null;
  };

  const openWidgetDashboardForSection = (section) => {
    const payload = widgetInstanceFromSection(section, pages.find((p) => p.id === currentPage)?.slug || '');
    const instanceId = payload?.instance_id || extractInstanceIdFromHtml(section?.html || '');
    if (payload && isConfigWidget(payload.widget_type)) {
      registerWidgetInstance(API_URL, token, websiteId, payload);
    }
    navigate(widgetDashboardUrl(websiteId, instanceId));
  };

  // Section edit helpers
  const getSectionEditType = (section) => {
    const configType = getSectionConfigWidgetType(section);
    if (configType === 'gallery') return 'gallery';
    if (configType === 'slider') return 'slider';
    if (configType) return null;
    const source = `${section?.name || ''} ${section?.id || ''} ${section?.html || ''}`.toLowerCase();
    if (source.includes('faq') || source.includes('accordion')) return 'faq';
    if (source.includes('video') && (source.includes('playlist') || source.includes('gallery'))) return 'video-gallery';
    if (source.includes('video') || source.includes('iframe')) return 'video';
    if (source.includes('audio')) return 'audio';
    if (source.includes('slider')) return 'slider';
    if (
      section?.type === 'footer'
      || section?.name?.toLowerCase() === 'footer'
      || source.includes('footer-section')
      || source.includes('class="footer')
      || /<footer[\s>]/i.test(section?.html || '')
      || /data-section-name=["']footer["']/i.test(section?.html || '')
    ) return 'footer';
    return null;
  };

  const updateSectionContent = (index, newHtml) => {
    const { sections: currentSections, envelope } = parseHtmlStructure(getCurrentPageHtml());
    htmlEnvelopeRef.current = envelope;
    const newSections = [...currentSections];
    newSections[index] = { ...newSections[index], html: newHtml };
    commitPageHtml(composeSectionHtml(newSections), { saveHistory: true, refreshPreview: true });
  };

  // Replace a specific image in section HTML (by edit-id / old URL — never blindly the first <img>)
  const applyImageToSectionHtml = (sectionHtml, imageUrl, opts = {}) => {
    const { matchSrc = '', imageEditId = '', mode = 'img' } = opts;
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="section-root">${sectionHtml}</div>`, 'text/html');
    const root = doc.getElementById('section-root');
    if (!root) return { html: sectionHtml, changed: false };

    const setBackgroundUrl = (el) => {
      if (!el) return false;
      const style = el.getAttribute('style') || '';
      const nextStyle = /url\(/i.test(style)
        ? style.replace(/url\(['"]?[^'")]+['"]?\)/gi, `url('${imageUrl}')`)
        : `${style}${style.trim().endsWith(';') || !style ? '' : ';'}background-image:url('${imageUrl}');background-size:cover;background-position:center;background-repeat:no-repeat;`;
      if (nextStyle === style) return false;
      el.setAttribute('style', nextStyle);
      return true;
    };

    if (mode !== 'background') {
      let target = null;
      if (imageEditId) {
        target = root.querySelector(`[data-image-edit-id="${imageEditId}"]`);
      }
      if (!target && matchSrc) {
        target = [...root.querySelectorAll('img')].find((img) => {
          const attr = img.getAttribute('src') || '';
          return imageUrlMatches(attr, matchSrc) || imageUrlMatches(img.src, matchSrc);
        });
      }
      if (!target) {
        // Only fall back to first img when section has exactly one image
        const imgs = root.querySelectorAll('img');
        if (imgs.length === 1) target = imgs[0];
      }
      if (target && target.tagName === 'IMG') {
        const prev = target.getAttribute('src') || '';
        target.setAttribute('src', imageUrl);
        target.removeAttribute('srcset');
        return { html: root.innerHTML, changed: prev !== imageUrl };
      }
    }

    // Prefer dedicated hero media layers (common SiteBrain / template pattern)
    let preferredBg = root.querySelector(
      '.hero-bg, [data-hero-image], [data-bg-image], .hero-media, .banner-bg',
    );
    if (!preferredBg && matchSrc) {
      preferredBg = [...root.querySelectorAll('[style*="url("], [style*="background"]')].find((el) =>
        imageUrlMatches(el.getAttribute('style') || '', matchSrc),
      );
    }
    if (!preferredBg) {
      preferredBg = [...root.querySelectorAll('[style*="url("]')].find((el) =>
        /url\(/i.test(el.getAttribute('style') || ''),
      );
    }
    if (!preferredBg) {
      preferredBg = [...root.querySelectorAll('section, header, article, .hero-section, .widget-hero')].find((el) =>
        /background(?:-image)?\s*:|url\(/i.test(el.getAttribute('style') || ''),
      );
    }
    if (!preferredBg) {
      preferredBg = root.querySelector('section, header, article') || root.firstElementChild;
    }

    if (preferredBg && setBackgroundUrl(preferredBg)) {
      return { html: root.innerHTML, changed: true };
    }

    return { html: sectionHtml, changed: false };
  };

  const selectBannerImage = async (imageUrlValue) => {
    const resolved = resolveMediaUrl(imageUrlValue, API_URL);
    if (!resolved) {
      toast.error('Choose or upload an image first');
      return;
    }
    if (editingImage) {
      await handleImageUpdateRef.current?.(resolved);
      setShowBannerModal(false);
      setImageAiPrompt('');
      return;
    }
    if (editingImageSection !== null) {
      const { sections: currentSections, envelope } = resolveSectionsForEdit(
        getCurrentPageHtml(),
        sections,
        htmlEnvelopeRef,
      );
      htmlEnvelopeRef.current = envelope;
      const sectionId = sections[editingImageSection]?.id;
      let sectionIndex = sectionId
        ? currentSections.findIndex((s) => s.id === sectionId)
        : editingImageSection;
      if (sectionIndex < 0 || sectionIndex >= currentSections.length) {
        sectionIndex = editingImageSection;
      }
      if (sectionIndex < 0 || sectionIndex >= currentSections.length) {
        toast.error('Section not found — refresh and try again');
        return;
      }
      const newSections = [...currentSections];
      const sectionHtml = newSections[sectionIndex].html;
      const { html: nextHtml, changed } = applyImageToSectionHtml(sectionHtml, resolved);
      if (!changed) {
        toast.error('This section has no image/banner layer to update. Click the image in the preview, or use a Hero/banner section.');
        return;
      }
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        html: nextHtml,
      };
      commitPageHtml(composeSectionHtml(newSections), { saveHistory: true, refreshPreview: true });
      setShowBannerModal(false);
      setEditingImageSection(null);
      setImageAiPrompt('');
      try {
        await saveCurrentPage();
        toast.success('Image updated and saved');
      } catch {
        toast.success('Image updated — click Save if it does not persist');
      }
      return;
    }
    setShowBannerModal(false);
    setEditingImageSection(null);
    setImageAiPrompt('');
  };

  const uploadBannerImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl === 'string') selectBannerImage(dataUrl);
    };
    reader.onerror = () => toast.error('Could not read image file');
    reader.readAsDataURL(file);
  };

  // Get currency symbol from website default currency (backend)
  const getCurrencySymbol = useCallback(() => {
    const code = ecomSettings?.currency || website?.currency || 'USD';
    return CURRENCIES.find(c => c.code === code)?.symbol || '$';
  }, [ecomSettings?.currency, website?.currency]);

  // Generate full HTML with products and customization
  const contactResource = editorSurfaces?.contact_resource || 'inquiries';
  const widgetBindings = editorSurfaces?.widget_bindings
    || fallbackWidgetBindings(editorSurfaces?.genre_id || website?.genre_id);

  const getFullHtml = useCallback(() => {
    // Match live nav: nav_visible pages, excluding operator/utility categories
    const hiddenNavSlugs = HIDDEN_NAV_SLUGS;
    const visiblePages = resolveMarketingNavPages(pages);

    const buildEditorNavLink = (page) => {
      const isActive = currentPage?.id === page.id;
      const label = page.name || page.title || page.slug;
      return `<a href="#" data-editor-page="${page.slug}" data-page="${page.slug}" class="nav-link${isActive ? ' is-active' : ''}" style="color: white; text-decoration: none; padding: 10px 16px; font-weight: 500; transition: all 0.2s; border-radius: 8px; ${isActive ? 'background: rgba(255,255,255,0.15);' : ''}">${label}</a>`;
    };

    const buildTemplateNavLink = (page) => {
      const isActive = currentPage?.id === page.id;
      const label = page.name || page.title || (page.slug ? page.slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Page');
      return `<a href="?page=${page.slug}" data-editor-page="${page.slug}" data-page="${page.slug}"${isActive ? ' class="is-active"' : ''}>${label}</a>`;
    };
    
    // Get sections that should show in nav (single-page sites only — avoid duplicating page nav)
    const navSections = visiblePages.length <= 1 ? sections.filter(s => s.showInNav) : [];
    
    // Build navigation links for the unified preview nav bar
    let navLinks = visiblePages.map(buildEditorNavLink).join('');
    
    // Add section-based nav items
    if (navSections.length > 0) {
      navLinks += navSections.map(s => 
        `<a href="#${s.id}" class="nav-link" style="color: white; text-decoration: none; padding: 10px 16px; font-weight: 500; transition: all 0.2s; border-radius: 8px;">${s.name}</a>`
      ).join('');
    }

    const isEcommerceSite = editorSurfaces?.genre_id === 'ecommerce'
      || website?.category === 'ecommerce'
      || website?.genre_id === 'ecommerce';

    const registerAllowed = shouldShowRegisterNav(pages);
    let templateNavLinks = isEcommerceSite
      ? buildCanonicalEcommerceNavLinks(currentPage?.slug || 'home', pages)
      : buildTemplateNavLinksHtml(pages, currentPage?.slug || '', { includeRegister: registerAllowed });
    if (visiblePages.length <= 1 && navSections.length > 0) {
      templateNavLinks += navSections.map((s) => `<a href="#${s.id}" data-page="${s.id}">${s.name}</a>`).join('');
    }
    
    // E-commerce links (cart + auth only; product sections are added as widgets)
    const registerNavLink = registerAllowed ? `<a href="?page=sign-in" class="nav-link" style="color: white; text-decoration: none; padding: 10px 16px; font-weight: 500; border-radius: 8px;">Register</a>` : '';
    const ecommerceLinks = products.length > 0 ? `
      <a href="#" onclick="toggleCart(); return false;" class="nav-link" style="color: white; text-decoration: none; padding: 10px 16px; font-weight: 500; border-radius: 8px; display: flex; align-items: center; gap: 6px;">🛒 Cart <span id="cart-count" style="background: ${primaryColor}; padding: 2px 8px; border-radius: 10px; font-size: 0.8rem;">0</span></a>
      ${registerNavLink}
    ` : registerNavLink;
    
    // Generate products HTML with multi-currency
    const currencySymbol = getCurrencySymbol();
    const productsHtml = products.length > 0 ? products.map(p => {
      // Get the best available image
      const productImage = p.images?.[0] || p.image || p.image_url || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400';
      return `
      <div class="product-card" style="background: white; border-radius: 20px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); transition: all 0.3s;">
        <div style="position: relative; overflow: hidden;">
          <img src="${productImage}" style="width: 100%; height: 220px; object-fit: cover; transition: transform 0.3s;" alt="${p.name}" onerror="this.src='https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400'">
          ${p.sale_price ? `<span style="position: absolute; top: 10px; right: 10px; background: #ef4444; color: white; padding: 4px 12px; border-radius: 20px; font-size: 0.8rem; font-weight: 600;">SALE</span>` : ''}
        </div>
        <div style="padding: 20px;">
          <h3 style="font-size: 1.1rem; font-weight: 600; margin-bottom: 8px; color: #1e293b;">${p.name}</h3>
          <p style="color: #64748b; font-size: 0.9rem; margin-bottom: 15px; line-height: 1.5; height: 40px; overflow: hidden;">${p.description?.substring(0, 60) || 'Quality product'}...</p>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              ${p.sale_price ? `<span style="text-decoration: line-through; color: #94a3b8; margin-right: 8px;">${currencySymbol}${(p.price || 0).toFixed(2)}</span>` : ''}
              <span style="font-weight: 700; font-size: 1.3rem; color: ${primaryColor};">${currencySymbol}${(p.sale_price || p.price || 0).toFixed(2)}</span>
            </div>
            <button onclick="addToCart('${p.id}', '${p.name.replace(/'/g, "\\'")}', ${p.sale_price || p.price})" style="padding: 10px 20px; background: linear-gradient(135deg, ${primaryColor}, ${secondaryColor}); color: white; border: none; border-radius: 25px; cursor: pointer; font-weight: 600; font-size: 0.9rem; transition: transform 0.2s;" onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">Add to Cart</button>
          </div>
        </div>
      </div>`;
    }).join('') : `
      <div style="grid-column: span 4; text-align: center; padding: 60px 20px; background: #f8fafc; border-radius: 20px;">
        <div style="font-size: 3rem; margin-bottom: 15px;">🛒</div>
        <p style="color: #64748b; margin-bottom: 10px;">No products yet</p>
        <p style="color: ${primaryColor}; font-size: 0.9rem; font-weight: 600;">Go to Store panel to add products →</p>
      </div>
    `;
    
    const escapeServiceText = (value) => String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const servicesHtml = liveServices.length > 0 ? liveServices.map((s) => {
      const price = Number(s.price);
      const priceLabel = Number.isFinite(price) ? `${currencySymbol}${price.toFixed(2)}` : (s.price ? `${currencySymbol}${s.price}` : '');
      const duration = s.duration ? `${s.duration} min` : '';
      return `
      <div class="service-card feature-card" style="background: white; border-radius: 16px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); transition: all 0.3s ease;">
        <div style="width: 60px; height: 60px; background: linear-gradient(135deg, ${primaryColor}, ${secondaryColor}); border-radius: 15px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px;">
          <span style="font-size: 1.5rem;">${s.icon || '⏰'}</span>
        </div>
        <h3 style="font-size: 1.2rem; margin-bottom: 10px; font-weight: 600;">${escapeServiceText(s.name)}</h3>
        <p style="color: #666; font-size: 0.9rem; margin-bottom: 15px;">${escapeServiceText(s.description || '')}</p>
        ${priceLabel || duration ? `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;"><span style="color: #888;">${escapeServiceText(duration)}</span>${priceLabel ? `<span style="font-weight: bold; font-size: 1.1rem; color: ${primaryColor};">${priceLabel}</span>` : ''}</div>` : ''}
      </div>`;
    }).join('') : `
      <div style="grid-column: span 3; text-align: center; padding: 40px 20px; background: #f8fafc; border-radius: 20px;">
        <div style="font-size: 2.5rem; margin-bottom: 12px;">🔧</div>
        <p style="color: #64748b; margin-bottom: 8px;">No services yet</p>
        <p style="color: ${primaryColor}; font-size: 0.9rem; font-weight: 600;">Add services in Operate → Services</p>
      </div>
    `;
    
    // Process content - inject products and remove duplicate nav/headers
    const liveHtml = htmlContentRef.current || htmlContent;
    const { html: pageFragment, extraStyles: pageInlineStyles, extraLinks: pageExtraLinks } =
      extractPageFragmentForPreview(liveHtml);
    let processedContent = pageFragment;

    // Inject schema-driven widgets into preview HTML (preview-only)
    processedContent = renderSchemaWidgetsIntoHtml(processedContent);
    processedContent = applyWidgetInstancesToHtml(processedContent, widgetInstances);
    processedContent = enhanceEditorVideoPreview(processedContent, widgetInstances);

    if (contactResource !== 'inquiries') {
      processedContent = processedContent.replace(
        /data-schema-resource=(["'])inquiries\1/g,
        `data-schema-resource=$1${contactResource}$1`
      );
    }
    // Remove standalone headers that are NOT widget-sections and NOT the site header section.
    processedContent = processedContent.replace(
      /<header(?![^>]*(?:widget-section|data-section-name=(["'])Header\1))[^>]*>[\s\S]*?<\/header>/gi,
      ''
    );
    
    // Upgrade legacy .hero sections from older ecommerce templates to full-width image banners
    try {
      if (processedContent.includes('class="hero"')) {
        const imgMatch = processedContent.match(/<section class="products"[\s\S]*?<img src="([^\"]+)"/);
        const heroImage = imgMatch ? imgMatch[1] : null;
        if (heroImage) {
          // Wrap existing hero content with overlay + content container
          processedContent = processedContent.replace(
            /<section class="hero">([\s\S]*?)<\/section>/,
            '<section class="hero"><div class="hero-overlay"></div><div class="hero-content">$1</div></section>'
          );
          // Ensure hero has background-image inline style
          processedContent = processedContent.replace(
            /<section class="hero">/,
            `<section class="hero" style="background-image:url('${heroImage}')">`
          );
        }
      }
    } catch (e) {
      console.error('Hero upgrade error', e);
    }
    
    // Strip page-embedded product loaders (preview uses window.PRODUCTS + loadProducts())
    processedContent = stripEmbeddedProductLoaders(processedContent);

    // Inject Operate-backed services into static Home Services sections and legacy grids.
    if (liveServices.length > 0 && servicesHtml) {
      if (processedContent.includes('data-services') || processedContent.includes('services-grid')) {
        processedContent = processedContent.replace(
          /(<(?:div|section)[^>]*(?:data-services|class="[^"]*services-grid)[^>]*>)[\s\S]*?(<\/div>)/i,
          `$1${servicesHtml}$2`
        );
      }
      if (/data-section-name=["']Services["']/i.test(processedContent)) {
        processedContent = processedContent.replace(
          /(<section[^>]*data-section-name=["']Services["'][^>]*>[\s\S]*?<div[^>]*class=["'][^"']*(?:features-grid|grid-3|services-grid)[^"']*["'][^>]*>)[\s\S]*?(<\/div>)/i,
          `$1${servicesHtml}$2`
        );
      }
    }
    
    // Extract any <link> tags from processedContent (e.g. Google Fonts from AI-generated HTML)
    const linkTagMatches = processedContent.match(/<link[^>]+>/gi) || [];
    const extractedLinks = linkTagMatches.join('\n');
    // Remove link tags from processedContent to avoid duplicates in body
    processedContent = processedContent.replace(/<link[^>]+>/gi, '');

    const homeHeaderHtml = extractHomeHeaderHtml(pages);
    if (homeHeaderHtml && currentPage?.slug !== 'home' && !currentPage?.is_home) {
      if (/<header[^>]*data-section-name=["']Header["'][^>]*>[\s\S]*?<\/header>/i.test(processedContent)) {
        processedContent = processedContent.replace(
          /<header[^>]*data-section-name=["']Header["'][^>]*>[\s\S]*?<\/header>/i,
          homeHeaderHtml,
        );
      }
    }

    // Keep embedded template headers aligned with nav-pack pages (sidebar + Operate).
    if (templateNavLinks) {
      if (processedContent.includes('header-nav') || /data-section-name=["']Header["']/i.test(processedContent)) {
        processedContent = injectOrSyncHeaderNav(processedContent, templateNavLinks);
      } else if (!processedContent.includes('data-section-name="Header"')) {
        processedContent = `
<header class="site-marketing-header header-section widget-section" data-section-id="site-header" data-section-name="Header" data-show-nav="false" style="position:sticky;top:0;z-index:1000;background:#fff;border-bottom:1px solid #e2e8f0;">
  <div style="max-width:1100px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;padding:0 20px;height:72px;gap:16px;">
    <div style="font-size:1.05rem;font-weight:800;color:${primaryColor};white-space:nowrap;">${brandName}</div>
    <nav class="header-nav" style="display:flex;gap:24px;align-items:center;flex-wrap:wrap;">${templateNavLinks}</nav>
  </div>
</header>
${processedContent}`;
      }
    }

    const hasHeaderSection = processedContent.includes('data-section-name="Header"') || processedContent.includes("data-section-name='Header'");
    // Blob-URL preview iframes cannot resolve relative /api/media paths — always use absolute.
    // editorApiBase is already `…/api`, so paths that start with `/api/` must use origin only
    // (otherwise we produce broken `/api/api/media/…` URLs).
    const _rawLogo = String(website?.logo_url || '').trim();
    const _logoSrc = (() => {
      if (!_rawLogo) {
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(brandName)}&size=128&background=${(primaryColor || '#8b5cf6').replace('#', '')}&color=fff&bold=true&format=svg&rounded=true`;
      }
      if (_rawLogo.startsWith('http') || _rawLogo.startsWith('data:') || _rawLogo.startsWith('blob:')) {
        return _rawLogo;
      }
      if (_rawLogo.startsWith('/api/')) {
        const origin = (typeof window !== 'undefined' && window.location?.origin)
          || String(API_URL || '').replace(/\/$/, '')
          || 'http://127.0.0.1:8002';
        return `${origin.replace(/\/$/, '')}${_rawLogo}`;
      }
      const base = String(editorApiBase || API_URL || '').replace(/\/$/, '');
      return `${base}/${_rawLogo.replace(/^\//, '')}`;
    })();
    const _logoImgStyle = 'display:block;flex-shrink:0;width:32px;height:32px;border-radius:10px;object-fit:cover;box-shadow:0 8px 24px rgba(0,0,0,0.18);';
    const previewBrandLogo = `<img src="${_logoSrc}" alt="${brandName}" style="${_logoImgStyle}" />`;
    // Sync brand name + logo image only — never wipe user color/font styles on the mark.
    const syncHeaderBrandPreservingStyles = (html) => {
      if (!html) return html;
      try {
        const doc = new DOMParser().parseFromString(
          `<div id="__snag-brand-root">${html}</div>`,
          'text/html',
        );
        const root = doc.getElementById('__snag-brand-root');
        if (!root) return html;

        const ensureLogoRowAlign = (el) => {
          if (!el) return;
          const style = el.getAttribute('style') || '';
          const parts = [];
          if (!/display\s*:/i.test(style)) parts.push('display:flex');
          if (!/align-items\s*:/i.test(style)) parts.push('align-items:center');
          if (!/gap\s*:/i.test(style)) parts.push('gap:10px');
          if (!/line-height\s*:/i.test(style)) parts.push('line-height:1');
          if (parts.length) {
            el.setAttribute('style', `${style}${style && !style.trim().endsWith(';') ? ';' : ''}${parts.join(';')}`);
          }
        };

        const syncContainer = (container) => {
          if (!container) return;
          ensureLogoRowAlign(container);
          let img = container.querySelector('img');
          if (!img) {
            img = doc.createElement('img');
            img.setAttribute('style', _logoImgStyle);
            container.insertBefore(img, container.firstChild);
          }
          img.setAttribute('src', _logoSrc);
          img.setAttribute('alt', brandName);
          img.setAttribute('data-site-logo', '1');
          // Keep avatar vertically centered with brand text (inline img baseline otherwise drifts).
          const imgStyle = img.getAttribute('style') || '';
          if (!/display\s*:/i.test(imgStyle)) {
            img.setAttribute('style', `display:block;flex-shrink:0;${imgStyle}`);
          }

          let textEl = Array.from(
            container.querySelectorAll('span, a, strong, h1, h2, h3, p'),
          ).find((node) => (node.textContent || '').trim().length > 0);
          // Direct bare text nodes: brand rendered as plain text, e.g.
          // <div class="header-logo">Brand</div>. Appending a new label span
          // alongside these caused the brand to render twice ("BrandBrand").
          const bareTextNodes = Array.from(container.childNodes).filter(
            (n) => n.nodeType === 3 && (n.textContent || '').trim().length > 0,
          );
          if (!textEl) {
            if (bareTextNodes.length) {
              // Reuse the first bare text node; clear any extras.
              bareTextNodes.forEach((n, i) => {
                n.textContent = i === 0 ? brandName : '';
              });
            } else {
              textEl = doc.createElement('span');
              textEl.style.fontWeight = '800';
              textEl.textContent = brandName;
              container.appendChild(textEl);
            }
          } else {
            // Update label text only; keep style/class/data-* (editor color saves).
            if ((textEl.textContent || '').trim() !== brandName) {
              textEl.textContent = brandName;
            }
            // Drop duplicate bare text nodes so the brand isn't shown twice.
            bareTextNodes.forEach((n) => {
              n.textContent = '';
            });
          }
        };

        root.querySelectorAll('.header-logo, .site-logo').forEach(syncContainer);

        // Fallback for AI headers that used an inline brand div (no .header-logo).
        if (!root.querySelector('.header-logo, .site-logo')) {
          const inlineBrand = root.querySelector(
            'header div[style*="font-size:1.05rem"], header div[style*="font-size: 1.05rem"]',
          );
          if (inlineBrand) {
            ensureLogoRowAlign(inlineBrand);
            const custom = inlineBrand.getAttribute('data-custom-color') === 'true'
              || Boolean(inlineBrand.getAttribute('style')?.includes('color'));
            if (!custom) {
              inlineBrand.innerHTML = `${previewBrandLogo}<span style="font-weight:800;line-height:1;">${brandName}</span>`;
            } else if ((inlineBrand.textContent || '').trim() !== brandName) {
              // Keep nested markup/styles; rewrite only plain text nodes when possible.
              const textNode = Array.from(inlineBrand.childNodes).find(
                (n) => n.nodeType === 3 && n.textContent.trim(),
              );
              if (textNode) textNode.textContent = brandName;
              else {
                const span = inlineBrand.querySelector('span');
                if (span) span.textContent = brandName;
              }
            }
          }
        }

        return root.innerHTML;
      } catch (err) {
        console.error('syncHeaderBrandPreservingStyles failed', err);
        return html;
      }
    };
    processedContent = hasHeaderSection
      ? syncHeaderBrandPreservingStyles(processedContent)
      : processedContent;

    const { wrapperClass } = htmlEnvelopeRef.current || {};
    if (wrapperClass && !/site-theme[\s-]/.test(processedContent)) {
      processedContent = `<div class="${wrapperClass}">\n${processedContent}\n</div>`;
    }

    return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="referrer" content="no-referrer">
<title>${website?.seo_title || website?.name || 'Website'}</title>
<link href="${buildEditorGoogleFontsHref()}" rel="stylesheet">
${pageExtraLinks}
${extractedLinks}
${website?.custom_head_code || ''}
<style>
:root {
  --primary-color: ${primaryColor};
  --secondary-color: ${secondaryColor};
  --font-family: '${fontFamily}', -apple-system, sans-serif;
  --text-color: #333;
  --text-muted: #666;
  --bg-light: #f8fafc;
  --shadow-sm: 0 2px 10px rgba(0,0,0,0.05);
  --shadow-md: 0 4px 20px rgba(0,0,0,0.1);
  --shadow-lg: 0 10px 40px rgba(0,0,0,0.15);
  /* Template design-system fallbacks (overridden by site css_code below) */
  --text: #1f2937;
  --text-light: #6b7280;
  --bg: #f9fafb;
  --surface: #ffffff;
  --border: rgba(0,0,0,0.08);
  --shadow: 0 4px 20px rgba(0,0,0,0.08);
  --radius-card: 12px;
  --primary: ${primaryColor};
  --primary-dark: ${secondaryColor};
  --font-heading: '${fontFamily}', Georgia, serif;
  --font-body: '${fontFamily}', -apple-system, sans-serif;
}

* { margin: 0; padding: 0; box-sizing: border-box; }
html { scroll-behavior: smooth; }
body { font-family: var(--font-family); line-height: 1.6; color: #333; overflow-x: hidden; }

/* =====================================================================
   PREVIEW-ONLY: Fix 100vh so sections don't overflow the iframe panel.
   background-attachment:fixed is disabled in iframes (browser limitation)
   so we also neutralise it to avoid blank/broken hero backgrounds.
   ===================================================================== */
[style*="min-height: 100vh"], [style*="min-height:100vh"] { min-height: 500px !important; }
[style*="min-height: 90vh"], [style*="min-height:90vh"] { min-height: 450px !important; }
[style*="min-height: 80vh"], [style*="min-height:80vh"] { min-height: 400px !important; }
[style*="height: 100vh"], [style*="height:100vh"] { height: 500px !important; }
/* Disable background-attachment:fixed inside iframe (causes blank hero) */
[style*="background-attachment: fixed"], [style*="background-attachment:fixed"] { background-attachment: scroll !important; }
/* Hero height cap — skip split/accent variants so template heroes are not clipped */
.hero-section:not(.hero-variant-split):not(.hero-variant-accent):not(.hero-variant-photo):not(.hero-variant-bottom),
.hero:not(.hero-variant-split), .widget-hero {
  min-height: 480px !important;
  max-height: 560px !important;
  overflow: hidden;
}
.hero-variant-split,
.hero-section.hero-variant-split {
  min-height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  display: block !important;
}
.hero-variant-split .hero-split {
  min-height: auto !important;
  max-height: none !important;
}

/* Editor preview: keep header navigation visible (template CSS hides it below 768px) */
header .header-nav,
.header-section .header-nav { display: flex !important; }
header .mobile-toggle,
.header-section .mobile-toggle { display: none !important; }
/* Keep logo avatar + brand name on one horizontal centerline */
.header-logo, .site-logo {
  display: flex !important;
  align-items: center !important;
  gap: 10px;
  line-height: 1;
}
.header-logo img, .site-logo img {
  display: block;
  flex-shrink: 0;
  vertical-align: middle;
}

.hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; z-index: 0; }
.hero-overlay { position: absolute; inset: 0; z-index: 1; }
.hero-content { position: relative; z-index: 2; }
.hero-section .hero-title { line-height: 1.1; }
.hero-section .btn-group { display: flex; gap: 16px; flex-wrap: wrap; }
.hero-variant-photo .hero-content,
.hero-variant-bottom .hero-content,
.hero-section:not(.hero-variant-split) .hero-content { color: #fff; }

/* =====================================================================
   SECTION STACKING — prevent widget sections from overlapping each other.
   All sections are block-level; position:relative only, never absolute.
   ===================================================================== */
.widget-section { position: relative; display: block; clear: both; }
.widget-section:hover { outline: 2px dashed rgba(139, 92, 246, 0.5); }

/* Nav-pack marketing fragments (contact forms, etc.) when page-level <style> was stripped */
.section { padding: 80px 20px; }
.container { max-width: 1100px; margin: 0 auto; }
.h1 { font-size: clamp(2.2rem, 5vw, 3.5rem); font-weight: 800; line-height: 1.1; margin-bottom: 18px; }
.lead { font-size: 1.1rem; color: var(--muted, #64748b); line-height: 1.7; max-width: 640px; margin-bottom: 28px; }
.form { background: #fff; border-radius: 20px; padding: 32px; border: 1px solid #e2e8f0; display: flex; flex-direction: column; }
.form label { display: block; font-size: .85rem; font-weight: 600; margin-bottom: 6px; }
.form input, .form textarea, .form select { display: block; width: 100%; padding: 12px 14px; border: 1px solid #cbd5e1; border-radius: 10px; margin-bottom: 14px; font: inherit; box-sizing: border-box; }
.form .form-group { margin-bottom: 14px; }
.form .form-group label { margin-bottom: 6px; }
.form .form-group input, .form .form-group textarea, .form .form-group select { margin-bottom: 0; }
.form [data-schema-form-message] { margin-bottom: 8px; }
.form button[type="submit"], .form .btn-primary { width: 100%; margin-top: 4px; }
@media (max-width: 768px) {
  .container[style*="grid-template-columns:1fr 1fr"],
  .container[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
}

/* Inline-edit helpers */
[data-editable]:hover { outline: 2px dashed ${primaryColor} !important; cursor: text; }
[data-editable]:focus { outline: 2px solid ${primaryColor} !important; }
[data-editable][data-custom-font-size] { font-size: var(--editor-custom-font-size) !important; }
[data-editable][data-custom-font-family] { font-family: var(--editor-custom-font-family) !important; }

/* =====================================================================
   SHARED DESIGN SYSTEM — mirrors the live-view CSS from the backend
   so preview and live look identical.
   ===================================================================== */

/* Animations */
@keyframes fadeIn { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slideIn { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
@keyframes scaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
@keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
@keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@keyframes gradientShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }

.animate-fade-in { animation: fadeIn 0.8s ease-out forwards; }
.animate-slide-in { animation: slideIn 0.6s ease-out forwards; }
.animate-float { animation: float 3s ease-in-out infinite; }
.animate-pulse { animation: pulse 2s ease-in-out infinite; }

/* Gradient text */
.gradient-text {
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Animated gradient background */
.gradient-bg {
  background: linear-gradient(-45deg, ${primaryColor}, ${secondaryColor}, #667eea, #764ba2);
  background-size: 400% 400%;
  animation: gradientShift 15s ease infinite;
}

/* Button styles */
.btn-primary {
  background: var(--primary-color);
  color: white;
  padding: 12px 28px;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(139, 92, 246, 0.4); }

/* Card hover effects */
.card-hover { transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
.card-hover:hover { transform: translateY(-10px); box-shadow: var(--shadow-lg); }

/* Image zoom */
.img-zoom { overflow: hidden; }
.img-zoom img { transition: transform 0.5s ease; }
.img-zoom:hover img { transform: scale(1.1); }

/* Skeleton loader */
.skeleton { background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }

/* Product cards */
.product-card:hover { transform: translateY(-5px); box-shadow: 0 15px 40px rgba(0,0,0,0.12); }
.product-card:hover img { transform: scale(1.05); }

/* Nav links */
.nav-link:hover { background: rgba(255,255,255,0.1) !important; }

/* Legacy .hero sections (older ecom templates) */
.hero { position: relative; padding: 120px 20px; text-align: center; background-size: cover; background-position: center; color: #ffffff; }
.hero-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.4); }
.hero-content { position: relative; z-index: 1; max-width: 800px; margin: 0 auto; }

/* Responsive grid */
@media (max-width: 1024px) {
  [style*="grid-template-columns: repeat(4"] { grid-template-columns: repeat(3, 1fr) !important; }
}
@media (max-width: 768px) {
  [style*="grid-template-columns: repeat(3"] { grid-template-columns: repeat(2, 1fr) !important; }
  [style*="grid-template-columns: repeat(4"] { grid-template-columns: repeat(2, 1fr) !important; }
}
@media (max-width: 480px) {
  [style*="grid-template-columns: repeat(2"] { grid-template-columns: 1fr !important; }
}

/* Cart Sidebar */
.cart-sidebar { position: fixed; right: -400px; top: 0; width: 400px; height: 100vh; background: white; box-shadow: -5px 0 30px rgba(0,0,0,0.15); z-index: 10000; transition: right 0.3s; padding: 30px; overflow-y: auto; }
.cart-sidebar.open { right: 0; }
.cart-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 9999; display: none; }
.cart-overlay.open { display: block; }
${pageInlineStyles}
${cssContent}
${website?.custom_css || ''}

/* ============================================================
   Newsletter / Subscribe form structure fix.
   Fields may be direct children of .newsletter-form OR nested in a
   schema <form> wrapper (editor hydrate). Target both so email,
   phone, and Subscribe share one centered full-width column.
   ============================================================ */
.newsletter-form,
.newsletter-form form[data-schema-form],
.newsletter-section form[data-schema-resource="subscribers"] {
  display: flex !important;
  flex-direction: column !important;
  align-items: stretch !important;
  gap: 14px !important;
  width: 100% !important;
  max-width: 420px !important;
  margin: 0 auto !important;
  text-align: left !important;
  box-sizing: border-box !important;
}
.newsletter-form > form[data-schema-form] {
  max-width: none !important;
  margin: 0 !important;
}
.newsletter-form > input,
.newsletter-form > .form-group,
.newsletter-form > button,
.newsletter-form form > input,
.newsletter-form form > .form-group,
.newsletter-form form > button,
.newsletter-section form[data-schema-resource="subscribers"] > input,
.newsletter-section form[data-schema-resource="subscribers"] > .form-group,
.newsletter-section form[data-schema-resource="subscribers"] > button {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  margin: 0 !important;
  flex: none !important;
}
.newsletter-form input,
.newsletter-section form[data-schema-resource="subscribers"] input {
  height: 52px !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}
.newsletter-form .form-group,
.newsletter-section form[data-schema-resource="subscribers"] .form-group {
  display: flex !important;
  flex-direction: column !important;
  gap: 6px !important;
  width: 100% !important;
}
.newsletter-form .form-group label,
.newsletter-section form[data-schema-resource="subscribers"] .form-group label {
  /* Newsletter uses placeholder + aria-label; hide injected "Phone" text */
  display: none !important;
}
.newsletter-form .form-group input,
.newsletter-section form[data-schema-resource="subscribers"] .form-group input {
  width: 100% !important;
  margin: 0 !important;
}
.newsletter-form button,
.newsletter-section form[data-schema-resource="subscribers"] button {
  display: block !important;
  width: 100% !important;
  height: 52px !important;
  padding: 0 32px !important;
  white-space: nowrap !important;
  align-self: stretch !important;
}
.newsletter-form [data-schema-form-message],
.newsletter-section form[data-schema-resource="subscribers"] [data-schema-form-message] {
  width: 100% !important;
  text-align: center !important;
  margin: 2px 0 0 !important;
}
/* Force logical order regardless of DOM order: email -> phone -> button -> message */
.newsletter-form > input,
.newsletter-form form > input[type="email"],
.newsletter-section form[data-schema-resource="subscribers"] > input[type="email"] { order: 1 !important; }
.newsletter-form > .form-group,
.newsletter-form form > .form-group,
.newsletter-section form[data-schema-resource="subscribers"] > .form-group { order: 2 !important; }
.newsletter-form > button,
.newsletter-form form > button,
.newsletter-section form[data-schema-resource="subscribers"] > button { order: 3 !important; }
.newsletter-form > [data-schema-form-message],
.newsletter-form form > [data-schema-form-message],
.newsletter-section form[data-schema-resource="subscribers"] > [data-schema-form-message] { order: 4 !important; }
/* Keep the promo heading + copy readable on the dark section background */
.newsletter-section h2, .newsletter-section p { color: #fff !important; }
</style>

<script>
// Expose backend URL + website id inside the iframe runtime
window.API_URL = ${JSON.stringify(editorApiBase)};
window.WEBSITE_ID = "${websiteId}";
window.CONTACT_RESOURCE = ${JSON.stringify(contactResource)};
window.WIDGET_BINDINGS = ${JSON.stringify(widgetBindings)};
window.WIDGET_CONFIGS = ${JSON.stringify(widgetConfigs)};
window.WIDGET_INSTANCES = ${JSON.stringify(widgetInstances)};
window.EDITOR_AUTH_TOKEN = ${JSON.stringify(token || '')};
window.SERVICES = ${JSON.stringify(liveServices)};
window.PRODUCTS = ${JSON.stringify(products)};
window.PRIMARY_COLOR = "${primaryColor}";
window.SECONDARY_COLOR = "${secondaryColor}";
window.CURRENCY_SYMBOL = "${currencySymbol}";
</script>

<script>
// Schema widget runtime
async function submitSchemaForm(formEl) {
  const messageEl = formEl.querySelector('[data-schema-form-message]');
  if (messageEl) {
    messageEl.textContent = '';
    messageEl.style.color = '#64748b';
  }

  const websiteId = window.WEBSITE_ID;
  const resource = formEl.getAttribute('data-schema-resource');

  const fd = new FormData(formEl);
  const data = {};
  for (const [k, v] of fd.entries()) {
    data[k] = v;
  }

  if (formEl.getAttribute('data-quiz-form') === 'true') {
    const answers = [1, 2, 3]
      .map((num) => data['answer_' + num] ? ('Q' + num + ': ' + data['answer_' + num]) : '')
      .filter(Boolean);
    data.message = answers.join(' | ');
    data.source = 'quiz';
    data.subject = 'Quiz submission';
  }

  if (!data.name && (data.first_name || data.last_name)) {
    data.name = [data.first_name, data.last_name].filter(Boolean).join(' ').trim();
    delete data.first_name;
    delete data.last_name;
  }

  if (resource === 'bookings' && !data.status) {
    data.status = 'pending';
  }
  if ((resource === 'inquiries' || resource === 'leads' || resource === 'messages') && !data.status) {
    data.status = 'new';
  }
  if (resource === 'subscribers') {
    if (!data.status) data.status = 'subscribed';
    if (!data.source) data.source = 'website_newsletter';
    if (!data.subscribed_at) data.subscribed_at = new Date().toISOString();
  }
  if (resource === 'comments') {
    if (!data.status) data.status = 'published';
    if (!data.source) data.source = 'comment_widget';
    if (data.message && !data.content) data.content = data.message;
  }
  if ((resource === 'inquiries' || resource === 'leads' || resource === 'messages' || resource === 'contacts') && !data.source) {
    data.source = 'website_contact_form';
  }

  try {
    const resp = await fetch(__apiSitesRoot() + websiteId + '/resources/' + resource, {
      method: 'POST',
      headers: Object.assign({ 'Content-Type': 'application/json' }, __authHeaders()),
      body: JSON.stringify(data),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(json.detail || 'Failed to submit');
    }

    formEl.reset();
    if (messageEl) {
      messageEl.textContent = 'Submitted successfully.';
      messageEl.style.color = '#16a34a';
    }
    (function notifyOperateSync(resourceName) {
      var payload = { type: 'operator-data-changed', resource: resourceName || '' };
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(payload, '*');
        }
      } catch (pe) {}
      try {
        if (typeof BroadcastChannel !== 'undefined') {
          var ch = new BroadcastChannel('snagcart-operate-sync');
          ch.postMessage(payload);
          ch.close();
        }
      } catch (pe) {}
    })(resource);
    if (resource === 'comments') {
      setTimeout(hydrateCommentWidgets, 400);
    }
  } catch (e) {
    if (messageEl) {
      messageEl.textContent = (e && e.message) ? e.message : 'Failed to submit.';
      messageEl.style.color = '#dc2626';
    }
  }
}

document.addEventListener('submit', (e) => {
  const form = e.target;
  if (form && form.matches && form.matches('form[data-schema-form="true"]')) {
    e.preventDefault();
    submitSchemaForm(form);
  }
});

function __escapeHtml(s) {
  return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function __apiSitesRoot() {
  var base = String(window.API_URL || '').replace(/\\/$/, '');
  if (!base || base === '/api' || base.indexOf('://') === -1) {
    try {
      if (window.parent && window.parent !== window && window.parent.location && window.parent.location.origin) {
        base = window.parent.location.origin + '/api';
      }
    } catch (e) {}
  }
  if (!base || base.indexOf('://') === -1) base = 'http://127.0.0.1:8002/api';
  if (base.indexOf('/api') === -1) base = base.replace(/\\/$/, '') + '/api';
  return base.replace(/\\/$/, '') + '/sites/';
}

function __authHeaders() {
  if (!window.EDITOR_AUTH_TOKEN) return {};
  return { Authorization: 'Bearer ' + window.EDITOR_AUTH_TOKEN };
}

function __resourceItemActive(item) {
  if (!item || typeof item !== 'object') return false;
  if (item.is_active === false || item.is_available === false) return false;
  var st = String(item.status || 'active').toLowerCase();
  return ['inactive', 'draft', 'archived', 'disabled', 'cancelled', 'removed'].indexOf(st) < 0;
}

function loadServices() {
  if (!window.SERVICES || !window.SERVICES.length) return;
  var activeServices = window.SERVICES.filter(__resourceItemActive);
  if (!activeServices.length) return;
  var selectors = '[data-services], .services-grid, section[data-section-name="Services"] .features-grid, section[data-section-name="Services"] .grid-3, section[data-widget-type="services"] [data-widget-content-grid], section[data-operate-widget="services"] [data-widget-content-grid]';
  document.querySelectorAll(selectors).forEach(function(container) {
    if (container.getAttribute('data-resource-list')) return;
    var section = container.closest ? container.closest('section') : null;
    if (section && section.getAttribute('data-operate-widget')) return;
    container.innerHTML = activeServices.map(function(s) {
      var price = Number(s.price);
      var priceLabel = Number.isFinite(price) ? (window.CURRENCY_SYMBOL || '$') + price.toFixed(2) : (s.price ? (window.CURRENCY_SYMBOL || '$') + s.price : '');
      var duration = s.duration ? String(s.duration) + ' min' : '';
      return '<div class="service-card feature-card" style="background: white; border-radius: 16px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.08);">' +
        '<div style="width: 60px; height: 60px; background: linear-gradient(135deg, ' + (window.PRIMARY_COLOR || '#8b5cf6') + ', ' + (window.SECONDARY_COLOR || '#10b981') + '); border-radius: 15px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px;"><span style="font-size: 1.5rem;">⏰</span></div>' +
        '<h3 style="font-size: 1.2rem; margin-bottom: 10px; font-weight: 600;">' + __escapeHtml(s.name) + '</h3>' +
        '<p style="color: #666; font-size: 0.9rem; margin-bottom: 15px;">' + __escapeHtml(s.description || '') + '</p>' +
        ((priceLabel || duration) ? '<div style="display:flex;justify-content:space-between;align-items:center;"><span style="color:#888;">' + __escapeHtml(duration) + '</span>' + (priceLabel ? '<span style="font-weight:bold;color:' + (window.PRIMARY_COLOR || '#8b5cf6') + ';">' + __escapeHtml(priceLabel) + '</span>' : '') + '</div>' : '') +
      '</div>';
    }).join('');
  });
}

function productImageUrl(p) {
  var img = p.images && p.images[0];
  if (typeof img === 'string') return img;
  if (img && img.url) return img.url;
  return p.image || p.image_url || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400';
}

function productsForContainer(container) {
  var list = Array.isArray(window.PRODUCTS) ? window.PRODUCTS.filter(function(p) {
    if (p.is_active === false) return false;
    var st = String(p.status || 'active').toLowerCase();
    return ['inactive', 'draft', 'archived', 'disabled', 'cancelled', 'removed'].indexOf(st) < 0;
  }) : [];
  if (!list.length) return [];
  var section = container.closest ? container.closest('section') : null;
  var isFeatured = container.id === 'featured-products'
    || (container.getAttribute('data-section-name') || '').toLowerCase().indexOf('featured') >= 0
    || (section && /featured/i.test(section.textContent || ''));
  if (isFeatured) {
    var featured = list.filter(function(p) { return p.is_featured; });
    return (featured.length ? featured : list).slice(0, 8);
  }
  return list;
}

function renderProductCard(p) {
  var sym = window.CURRENCY_SYMBOL || '$';
  var price = Number(p.sale_price || p.price || 0);
  var img = productImageUrl(p);
  var safeName = String(p.name || '').replace(/'/g, "\\\\'");
  return '<div class="product-card" style="background:white;border-radius:20px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.08);">' +
    '<div style="position:relative;overflow:hidden;">' +
    '<img src="' + __escapeHtml(img) + '" style="width:100%;height:220px;object-fit:cover;" alt="' + __escapeHtml(p.name || '') + '">' +
    '</div>' +
    '<div style="padding:20px;">' +
    '<h3 style="font-size:1.1rem;font-weight:600;margin-bottom:8px;color:#1e293b;">' + __escapeHtml(p.name || '') + '</h3>' +
    '<p style="color:#64748b;font-size:0.9rem;margin-bottom:15px;line-height:1.5;">' + __escapeHtml(String(p.description || p.short_description || '').substring(0, 80)) + '</p>' +
    '<div style="display:flex;justify-content:space-between;align-items:center;">' +
    '<span style="font-weight:700;font-size:1.3rem;color:' + (window.PRIMARY_COLOR || '#8b5cf6') + ';">' + sym + price.toFixed(2) + '</span>' +
    '<button type="button" onclick="addToCart(\\'' + p.id + '\\', \\'' + safeName + '\\', ' + price + ')" style="padding:10px 20px;background:linear-gradient(135deg,' + (window.PRIMARY_COLOR || '#8b5cf6') + ',' + (window.SECONDARY_COLOR || '#10b981') + ');color:white;border:none;border-radius:25px;cursor:pointer;font-weight:600;">Add to Cart</button>' +
    '</div></div></div>';
}

function loadProducts() {
  var selectors = '[data-products], .products-grid, #featured-products, #products-grid, [data-widget="products"]';
  document.querySelectorAll(selectors).forEach(function(container) {
    if (container.getAttribute('data-resource-list')) return;
    var items = productsForContainer(container);
    if (!items.length) {
      container.innerHTML = '<div style="grid-column:1/-1;text-align:center;padding:3rem;color:#64748b;">No products yet. Add products in the Store panel.</div>';
      return;
    }
    container.innerHTML = items.map(renderProductCard).join('');
  });
}

async function hydrateResourceLists(onlyResource) {
  var grids = document.querySelectorAll('[data-resource-list]');
  if (!grids.length || !window.WEBSITE_ID) return;
  var apiRoot = __apiSitesRoot();
  for (var i = 0; i < grids.length; i++) {
    var grid = grids[i];
    var resource = grid.getAttribute('data-resource-list');
    if (!resource) continue;
    if (onlyResource && resource !== onlyResource) continue;
    var titleF = grid.getAttribute('data-resource-title') || 'name';
    var bodyF = grid.getAttribute('data-resource-body') || 'description';
    var imageF = grid.getAttribute('data-resource-image');
    var metaF = grid.getAttribute('data-resource-meta');
    var metaPrefix = grid.getAttribute('data-resource-meta-prefix') || '';
    var layout = grid.getAttribute('data-resource-layout') || 'card';
    try {
      var resp = await fetch(apiRoot + window.WEBSITE_ID + '/resources/' + resource + '?limit=24', {
        headers: __authHeaders(),
      });
      if (!resp.ok) continue;
      var data = await resp.json();
      var items = (data.items || []).filter(__resourceItemActive);
      if (!items.length) continue;
      if (layout === 'menu') {
        grid.innerHTML = items.map(function(item) {
          var title = item[titleF] || item.title || item.name || '';
          var body = item[bodyF] || item.description || '';
          var meta = '';
          if (metaF && item[metaF] !== undefined && item[metaF] !== null && item[metaF] !== '') {
            meta = metaPrefix + item[metaF];
          }
          return '<div style="display:flex;justify-content:space-between;align-items:center;padding:20px;background:white;border-radius:12px;box-shadow:0 2px 8px rgba(0,0,0,0.04);">' +
            '<div><h3 style="font-weight:600;margin-bottom:6px;">' + __escapeHtml(title) + '</h3><p style="color:#64748b;font-size:0.9rem;">' + __escapeHtml(body) + '</p></div>' +
            (meta ? '<span style="font-weight:700;font-size:1.1rem;color:var(--primary-color,#8b5cf6);">' + __escapeHtml(String(meta)) + '</span>' : '') +
          '</div>';
        }).join('');
      } else if (layout === 'feature') {
        grid.style.display = 'grid';
        grid.style.gap = '30px';
        grid.innerHTML = items.map(function(item) {
          var title = item[titleF] || item.title || item.name || '';
          var body = item[bodyF] || item.description || item.highlights || '';
          var meta = '';
          if (metaF && item[metaF] !== undefined && item[metaF] !== null && item[metaF] !== '') {
            meta = metaPrefix + item[metaF];
          }
          return '<div style="background:white;border-radius:16px;padding:30px;box-shadow:0 4px 20px rgba(0,0,0,0.08);text-align:center;">'
            + '<div style="width:60px;height:60px;background:linear-gradient(135deg,var(--primary-color,#8b5cf6),var(--secondary-color,#10b981));border-radius:15px;display:flex;align-items:center;justify-content:center;margin:0 auto 20px;font-size:1.5rem;">⭐</div>'
            + '<h3 style="font-size:1.2rem;margin-bottom:10px;font-weight:600;color:#1e293b;">' + __escapeHtml(title) + '</h3>'
            + '<p style="color:#64748b;font-size:0.9rem;line-height:1.6;">' + __escapeHtml(body) + '</p>'
            + (meta ? '<p style="font-weight:700;color:var(--primary-color,#8b5cf6);margin-top:8px;">' + __escapeHtml(String(meta)) + '</p>' : '')
            + '</div>';
        }).join('');
      } else if (layout === 'service') {
        grid.style.display = 'grid';
        grid.style.gap = '30px';
        grid.innerHTML = items.map(function(item) {
          var title = item[titleF] || item.title || item.name || '';
          var body = item[bodyF] || item.description || '';
          var meta = '';
          if (metaF && item[metaF] !== undefined && item[metaF] !== null && item[metaF] !== '') {
            meta = metaPrefix + item[metaF];
          }
          var priceLabel = meta ? String(meta) : '';
          return '<div class="service-card feature-card" style="background:white;border-radius:16px;padding:30px;box-shadow:0 4px 20px rgba(0,0,0,0.08);">'
            + '<div style="width:60px;height:60px;background:linear-gradient(135deg,' + (window.PRIMARY_COLOR || '#8b5cf6') + ',' + (window.SECONDARY_COLOR || '#10b981') + ');border-radius:15px;display:flex;align-items:center;justify-content:center;margin-bottom:20px;"><span style="font-size:1.5rem;">⏰</span></div>'
            + '<h3 style="font-size:1.2rem;margin-bottom:10px;font-weight:600;">' + __escapeHtml(title) + '</h3>'
            + '<p style="color:#666;font-size:0.9rem;margin-bottom:15px;">' + __escapeHtml(body) + '</p>'
            + (priceLabel ? '<span style="font-weight:bold;color:' + (window.PRIMARY_COLOR || '#8b5cf6') + ';">' + __escapeHtml(priceLabel) + '</span>' : '')
            + '</div>';
        }).join('');
      } else if (layout === 'list') {
        grid.innerHTML = items.map(function(item) {
          var title = item[titleF] || item.title || item.name || '';
          var body = item[bodyF] || item.description || '';
          return '<div class="card" style="margin-top:16px;text-align:left;"><h3>' + __escapeHtml(title) + '</h3><p>' + __escapeHtml(body) + '</p></div>';
        }).join('');
      } else {
        grid.innerHTML = items.map(function(item) {
          var title = item[titleF] || item.title || item.name || '';
          var body = item[bodyF] || item.description || item.excerpt || item.content || '';
          if (body.length > 140) body = body.substring(0, 140) + '…';
          var img = (imageF && item[imageF]) ? item[imageF] : 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600';
          var meta = '';
          if (metaF && item[metaF] !== undefined && item[metaF] !== null && item[metaF] !== '') {
            meta = metaPrefix + item[metaF];
          }
          var metaHtml = meta ? '<p style="font-weight:700;color:var(--primary);margin-top:8px;">' + __escapeHtml(String(meta)) + '</p>' : '';
          return '<div class="card"><img src="' + img + '" alt=""><h3>' + __escapeHtml(title) + '</h3><p>' + __escapeHtml(body) + '</p>' + metaHtml + '</div>';
        }).join('');
      }
    } catch (e) {}
  }
}

function __quizDefaultConfig() {
  return {
    title: 'Quick Quiz',
    subtitle: 'Answer a few questions to get personalized recommendations',
    question_1: 'What best describes your business?',
    options_1: 'E-commerce / Online Store\\nService Provider\\nPortfolio / Personal\\nBlog / Content',
    question_2: 'What is your main goal?',
    options_2: 'Get more leads\\nSell online\\nTake bookings\\nBuild trust',
    question_3: 'How soon do you want to start?',
    options_3: 'Today\\nThis week\\nThis month\\nJust exploring',
    submit_label: 'Get My Result',
    success_message: 'Thanks! We received your answers.'
  };
}

function __splitQuizOptions(value) {
  return String(value || '').split(/\\n|\\|/).map(function(v) { return v.trim(); }).filter(Boolean);
}

async function hydrateQuizWidgets() {
  var sections = document.querySelectorAll('[data-quiz-widget="true"]');
  if (!sections.length || !window.WEBSITE_ID) return;
  var config = __quizDefaultConfig();
  var inlineConfigs = Array.isArray(window.WIDGET_CONFIGS) ? window.WIDGET_CONFIGS : [];
  var inlineQuizConfig = inlineConfigs.find(function(item) { return (item.widget_key || '').toLowerCase() === 'quiz' || (item.title || '').toLowerCase().indexOf('quiz') >= 0; });
  if (inlineQuizConfig) config = Object.assign(config, inlineQuizConfig);
  function render(configToRender) {
    sections.forEach(function(section) {
      var title = section.querySelector('[data-quiz-title]');
      var subtitle = section.querySelector('[data-quiz-subtitle]');
      var questionsEl = section.querySelector('[data-quiz-questions]');
      var submit = section.querySelector('[data-quiz-submit]');
      if (title) title.textContent = configToRender.title || 'Quick Quiz';
      if (subtitle) subtitle.textContent = configToRender.subtitle || '';
      if (submit) submit.textContent = configToRender.submit_label || 'Submit';
      if (!questionsEl) return;
      var html = '';
      [1,2,3].forEach(function(num) {
        var question = configToRender['question_' + num];
        var options = __splitQuizOptions(configToRender['options_' + num]);
        if (!question || !options.length) return;
        html += '<div data-quiz-question-block style="display:grid;gap:12px;">' +
          '<h3 style="font-size:1.1rem;font-weight:700;color:#4f46e5;">Question ' + num + ' of 3</h3>' +
          '<p style="font-weight:600;color:#334155;">' + __escapeHtml(question) + '</p>' +
          '<div style="display:grid;gap:10px;">' + options.map(function(opt, idx) {
            var field = 'answer_' + num;
            return '<label style="display:flex;align-items:center;gap:10px;padding:14px 16px;border:2px solid #e2e8f0;border-radius:10px;background:white;color:#1e293b;cursor:pointer;">' +
              '<input type="radio" name="' + field + '" value="' + __escapeHtml(opt) + '" ' + (idx === 0 ? 'required' : '') + '><span style="color:#1e293b;font-weight:600;">' + __escapeHtml(opt) + '</span>' +
            '</label>';
          }).join('') + '</div></div>';
      });
      questionsEl.innerHTML = html;
    });
  }
  render(config);
  try {
    var resp = await fetch(__apiSitesRoot() + window.WEBSITE_ID + '/resources/widget_configs?limit=50', { headers: __authHeaders() });
    if (resp.ok) {
      var data = await resp.json();
      var configs = data.items || [];
      var quizConfig = configs.find(function(item) { return (item.widget_key || '').toLowerCase() === 'quiz' || (item.title || '').toLowerCase().indexOf('quiz') >= 0; });
      if (quizConfig) config = Object.assign(config, quizConfig);
    }
  } catch (e) {}
  render(config);
}

async function loadEditorCategories() {
  var grids = document.querySelectorAll('#categories-grid, .categories-grid');
  if (!grids.length || !window.WEBSITE_ID) return;
  try {
    var resp = await fetch(__apiSitesRoot() + window.WEBSITE_ID + '/resources/categories?limit=50', {
      headers: __authHeaders(),
    });
    if (!resp.ok) return;
    var data = await resp.json();
    var categories = (data.items || []).filter(__resourceItemActive);
    if (!categories.length) return;
    var icons = ['🛍️', '📦', '✨', '🏷️', '🎯', '🧺', '🥤', '🍞'];
    grids.forEach(function(grid) {
      grid.innerHTML = categories.map(function(cat, idx) {
        var name = cat.name || '';
        if (!name) return '';
        var icon = icons[idx % icons.length];
        return '<a href="?page=products&cat=' + encodeURIComponent(name) + '" style="text-decoration:none;background:#fff;border-radius:12px;padding:1.5rem 1rem;text-align:center;border:1px solid #e2e8f0;transition:all .2s;display:block;" onmouseover="this.style.borderColor=\\'#8b5cf6\\';this.style.transform=\\'translateY(-2px)\\'" onmouseout="this.style.borderColor=\\'#e2e8f0\\';this.style.transform=\\'none\\'">' +
          '<div style="font-size:2rem;margin-bottom:.5rem;">' + icon + '</div>' +
          '<div style="font-weight:700;font-size:.9rem;color:#1e293b;">' + __escapeHtml(name) + '</div></a>';
      }).join('');
    });
  } catch (e) {}
}

async function hydrateOperateBackedContent() {
  wireLegacyContactForms();
  wireWidgetGrids();
  await hydrateQuizWidgets();
  await hydrateWidgetInstances();
  await hydrateResourceLists();
  await loadEditorCategories();
  await hydrateCommentWidgets();
  wireCommentFormReload();
  loadServices();
  loadProducts();
  initEditorPageNav();
}

// OPERATE_PREVIEW_SYNC_V1 — live refresh when Widget Dashboard edits records
async function refreshOperatePreviewFromSync(detail) {
  detail = detail || {};
  var resource = detail.resource || '';
  if (Array.isArray(detail.products)) window.PRODUCTS = detail.products;
  if (Array.isArray(detail.services)) window.SERVICES = detail.services;
  if (Array.isArray(detail.widgetInstances)) window.WIDGET_INSTANCES = detail.widgetInstances;
  if (detail.widgetConfigs) window.WIDGET_CONFIGS = detail.widgetConfigs;

  if (resource === 'widget_instances') {
    await hydrateWidgetInstances();
    return;
  }
  if (resource === 'comments' || resource === 'inquiries') {
    await hydrateCommentWidgets();
    return;
  }
  if (resource === 'widget_configs') {
    await hydrateQuizWidgets();
    return;
  }
  if (resource === 'products' || !resource) loadProducts();
  if (resource === 'services' || !resource) loadServices();
  if (resource === 'categories' || !resource) await loadEditorCategories();
  await hydrateResourceLists(resource || null);
  if (!resource) {
    await hydrateWidgetInstances();
    await hydrateCommentWidgets();
    await hydrateQuizWidgets();
  }
}
window.refreshOperatePreviewFromSync = refreshOperatePreviewFromSync;

window.addEventListener('message', function(event) {
  if (!event || !event.data || typeof event.data !== 'object') return;
  if (event.data.type === 'operate-preview-refresh') {
    refreshOperatePreviewFromSync(event.data);
  }
});

function __renderCommentList(items) {
  document.querySelectorAll('[data-comment-list]').forEach(function(listEl) {
    var comments = (items || []).filter(function(item) {
      var subj = String(item.subject || '').toLowerCase();
      return !subj || subj === 'comment';
    });
    if (!comments.length) {
      listEl.innerHTML = '<p style="color:#94a3b8;font-size:.9rem;">Comments from your site visitors will appear here.</p>';
      return;
    }
    listEl.innerHTML = comments.map(function(item) {
      var name = item.name || item.author_name || 'Anonymous';
      var msg = item.message || item.content || '';
      return '<div style="padding:16px 0;border-bottom:1px solid #e2e8f0;">'
        + '<div style="font-weight:700;margin-bottom:6px;">' + __escapeHtml(name) + '</div>'
        + '<p style="color:#475569;margin:0;line-height:1.5;">' + __escapeHtml(msg) + '</p></div>';
    }).join('');
  });
}

async function hydrateCommentWidgets() {
  if (!document.querySelector('[data-comment-widget]') || !window.WEBSITE_ID) return;
  var comments = [];
  try {
    var resp = await fetch(__apiSitesRoot() + window.WEBSITE_ID + '/resources/comments?limit=50', {
      headers: __authHeaders(),
    });
    if (resp.ok) {
      var data = await resp.json();
      comments = (data.items || []).filter(__resourceItemActive);
    }
  } catch (e) {}
  if (!comments.length) {
    try {
      var legacyResp = await fetch(__apiSitesRoot() + window.WEBSITE_ID + '/resources/inquiries?limit=50', {
        headers: __authHeaders(),
      });
      if (legacyResp.ok) {
        var legacyData = await legacyResp.json();
        comments = (legacyData.items || []).filter(function(item) {
          return String(item.subject || '').toLowerCase() === 'comment';
        }).filter(__resourceItemActive);
      }
    } catch (e) {}
  }
  __renderCommentList(comments);
}

function wireCommentFormReload() {
  document.querySelectorAll('form[data-comment-form="true"]').forEach(function(form) {
    if (form.dataset.commentReloadWired === 'true') return;
    form.dataset.commentReloadWired = 'true';
    form.addEventListener('submit', function() {
      setTimeout(hydrateCommentWidgets, 1200);
    });
  });
}

function __parseWidgetConfig(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(String(raw)); } catch (e) { return {}; }
}

function __normalizeVideoUrl(url) {
  var u = String(url || '').trim();
  if (!u) return '';
  if (u.indexOf('/embed') >= 0) return u;
  if (u.indexOf('youtube.com/watch') >= 0) {
    try {
      var vid = new URL(u).searchParams.get('v');
      if (vid) return 'https://www.youtube.com/embed/' + vid;
    } catch (e) {}
  }
  if (u.indexOf('youtu.be/') >= 0) {
    var parts = u.split('youtu.be/')[1];
    if (parts) return 'https://www.youtube.com/embed/' + parts.split('?')[0];
  }
  return u;
}

function __youtubeId(url) {
  var u = String(url || '');
  var m = u.match(/embed\\/([^?&/]+)/);
  if (m) return m[1];
  m = u.match(/[?&]v=([^&]+)/);
  if (m) return m[1];
  m = u.match(/youtu\\.be\\/([^?&/]+)/);
  if (m) return m[1];
  return '';
}

function __applyWidgetInstanceConfig(section, item) {
  if (!section || !item) return;
  var cfg = __parseWidgetConfig(item.config_json);
  var type = (item.widget_type || section.getAttribute('data-widget-type') || '').toLowerCase();
  if (!type) {
    var sid = section.getAttribute('data-section-id') || section.getAttribute('data-widget-instance') || '';
    if (sid.indexOf('gallery-') === 0) type = 'gallery';
    else if (sid.indexOf('slider-') === 0) type = 'slider';
    else if (sid.indexOf('playlist-') === 0) type = 'video-playlist';
  }
  if (type === 'video') {
    var url = __normalizeVideoUrl(cfg.url);
    var iframe = section.querySelector('iframe');
    if (iframe && url) iframe.setAttribute('src', url);
    return;
  }
  if (type === 'audio') {
    var src = cfg.src || cfg.url || '';
    var audio = section.querySelector('audio');
    if (audio && src) {
      var source = audio.querySelector('source');
      if (source) source.setAttribute('src', src);
      else audio.setAttribute('src', src);
      try { audio.load(); } catch (e) {}
    }
    var titleEl = section.querySelector('h3');
    if (titleEl && cfg.title) titleEl.textContent = cfg.title;
    return;
  }
  if (type === 'heading') {
    var heading = section.querySelector('h1,h2,h3,h4,h5,h6');
    if (heading && cfg.text) heading.textContent = cfg.text;
    return;
  }
  if (type === 'text') {
    var para = section.querySelector('p');
    if (para && cfg.content) para.textContent = cfg.content;
    return;
  }
  if (type === 'button') {
    var btn = section.querySelector('a,button');
    if (btn) {
      if (cfg.text) btn.textContent = cfg.text;
      if (cfg.url && btn.tagName === 'A') btn.setAttribute('href', cfg.url);
      if (cfg.bgColor) btn.style.background = cfg.bgColor;
      if (cfg.textColor) btn.style.color = cfg.textColor;
    }
    return;
  }
  if (type === 'image') {
    var img = section.querySelector('img');
    if (img && cfg.src) img.setAttribute('src', cfg.src);
    if (img && cfg.alt) img.setAttribute('alt', cfg.alt);
    return;
  }
  function __widgetContentGrid(sectionEl) {
    return sectionEl.querySelector('[data-widget-content-grid], .operate-resource-grid, [data-feature-grid]')
      || sectionEl.querySelector(':scope > div > div:last-child');
  }
  if (type === 'accordion') {
    var accItems = Array.isArray(cfg.items) ? cfg.items : [];
    var accHost = section.querySelector('[data-accordion-items]') || section.querySelector(':scope > div > div');
    if (accHost && accItems.length) {
      accHost.innerHTML = accItems.map(function(item) {
        return '<details style="background:#f8fafc;border-radius:12px;padding:20px;margin-bottom:15px;">'
          + '<summary style="font-weight:600;cursor:pointer;color:#1e293b;">' + __escapeHtml(item.title || 'Question') + '</summary>'
          + '<p style="margin-top:15px;color:#64748b;line-height:1.7;">' + __escapeHtml(item.content || '') + '</p>'
          + '</details>';
      }).join('');
    }
    return;
  }
  if (type === 'separator') {
    var hr = section.querySelector('hr');
    if (hr) {
      var lineColor = cfg.color || '#e2e8f0';
      var thickness = cfg.thickness || '2px';
      var lineWidth = cfg.width || '200px';
      var lineStyle = cfg.style || 'solid';
      hr.style.border = 'none';
      hr.style.height = thickness;
      hr.style.maxWidth = lineWidth;
      hr.style.margin = '0 auto';
      if (lineStyle === 'dashed') {
        hr.style.background = 'transparent';
        hr.style.borderTop = thickness + ' dashed ' + lineColor;
      } else if (lineStyle === 'dotted') {
        hr.style.background = 'transparent';
        hr.style.borderTop = thickness + ' dotted ' + lineColor;
      } else {
        hr.style.background = 'linear-gradient(90deg, transparent, ' + lineColor + ', transparent)';
      }
    }
    return;
  }
  if (type === 'spacer') {
    var spacerHeight = cfg.height || '50px';
    section.style.minHeight = spacerHeight;
    section.style.padding = (Math.max(parseInt(spacerHeight, 10) / 2, 20) || 25) + 'px 20px';
    return;
  }
  if (type === 'cta') {
    var ctaHeading = section.querySelector('h2');
    var ctaDesc = section.querySelector('p');
    var ctaLinks = section.querySelectorAll('a');
    if (ctaHeading && cfg.title) ctaHeading.textContent = cfg.title;
    if (ctaDesc && cfg.description) ctaDesc.textContent = cfg.description;
    if (cfg.bgColor) section.style.background = cfg.bgColor;
    if (cfg.textColor) {
      section.style.color = cfg.textColor;
      if (ctaHeading) ctaHeading.style.color = cfg.textColor;
      if (ctaDesc) ctaDesc.style.color = cfg.textColor;
    }
    if (ctaLinks[0]) {
      if (cfg.buttonText) ctaLinks[0].textContent = cfg.buttonText;
      if (cfg.buttonUrl) ctaLinks[0].setAttribute('href', cfg.buttonUrl);
    }
    return;
  }
  if (type === 'services') {
    var svcTitle = section.querySelector('h2');
    if (svcTitle && cfg.title) svcTitle.textContent = cfg.title;
    var svcItems = Array.isArray(cfg.items) ? cfg.items : [];
    var svcGrid = __widgetContentGrid(section);
    if (svcGrid && svcItems.length) {
      var svcCols = cfg.columns || '3';
      svcGrid.style.display = 'grid';
      svcGrid.style.gridTemplateColumns = 'repeat(' + svcCols + ', 1fr)';
      svcGrid.style.gap = '30px';
      svcGrid.innerHTML = svcItems.map(function(item) {
        var feats = Array.isArray(item.features) ? item.features : [];
        var featHtml = feats.map(function(f) {
          return '<li style="padding:8px 0;font-size:14px;color:#374151;display:flex;align-items:center;gap:8px;"><span style="color:#22c55e;font-weight:bold;">✓</span>' + __escapeHtml(f) + '</li>';
        }).join('');
        return '<div style="background:white;border-radius:16px;padding:30px;box-shadow:0 4px 20px rgba(0,0,0,0.08);text-align:left;">'
          + '<div style="font-size:2rem;margin-bottom:16px;">' + __escapeHtml(item.icon || '🔧') + '</div>'
          + '<h3 style="font-size:1.2rem;margin-bottom:10px;font-weight:600;color:#1e293b;">' + __escapeHtml(item.title || '') + '</h3>'
          + '<p style="color:#64748b;font-size:0.9rem;line-height:1.6;margin-bottom:16px;">' + __escapeHtml(item.description || '') + '</p>'
          + (item.price ? '<div style="font-size:1.4rem;font-weight:800;color:#1e293b;margin-bottom:12px;">' + __escapeHtml(item.price) + '</div>' : '')
          + (featHtml ? '<ul style="list-style:none;padding:0;margin:0;border-top:1px solid #f0f0f0;padding-top:12px;">' + featHtml + '</ul>' : '')
          + '</div>';
      }).join('');
    }
    return;
  }
  if (type === 'features-grid' || type === 'icon-section' || type === 'features') {
    var featTitle = section.querySelector('h2');
    if (featTitle && cfg.title) featTitle.textContent = cfg.title;
    var featItems = Array.isArray(cfg.items) ? cfg.items : [];
    var featGrid = __widgetContentGrid(section);
    if (featGrid && featItems.length) {
      var featCols = cfg.columns || (type === 'icon-section' ? '4' : '3');
      featGrid.style.display = 'grid';
      featGrid.style.gridTemplateColumns = 'repeat(' + featCols + ', 1fr)';
      featGrid.style.gap = '30px';
      featGrid.innerHTML = featItems.map(function(item) {
        return '<div style="background:white;border-radius:16px;padding:30px;box-shadow:0 4px 20px rgba(0,0,0,0.08);text-align:center;">'
          + '<div style="width:60px;height:60px;background:linear-gradient(135deg,var(--primary-color,#8b5cf6),var(--secondary-color,#10b981));border-radius:15px;display:flex;align-items:center;justify-content:center;margin:0 auto 20px;font-size:1.5rem;">' + __escapeHtml(item.icon || '⭐') + '</div>'
          + '<h3 style="font-size:1.2rem;margin-bottom:10px;font-weight:600;color:#1e293b;">' + __escapeHtml(item.title || '') + '</h3>'
          + '<p style="color:#64748b;font-size:0.9rem;line-height:1.6;">' + __escapeHtml(item.description || '') + '</p>'
          + '</div>';
      }).join('');
    }
    return;
  }
  if (type === 'gallery') {
    var galTitle = section.querySelector('[data-gallery-title], h2');
    var galSub = section.querySelector('[data-gallery-subtitle], [data-section-subtitle]');
    if (galTitle && cfg.title) galTitle.textContent = cfg.title;
    if (galSub && cfg.subtitle) { galSub.textContent = cfg.subtitle; galSub.style.display = ''; }
    var galItems = Array.isArray(cfg.items) ? cfg.items : [];
    if (!galItems.length) {
      var domImgs = section.querySelectorAll('[data-gallery-grid] img, [data-widget-content-grid] img, :scope > div[style*="grid"] img, :scope img');
      galItems = Array.prototype.map.call(domImgs, function(img, i) {
        return { image: img.getAttribute('src') || '', title: img.getAttribute('alt') || ('Image ' + (i + 1)), category: '' };
      }).filter(function(item) { return item.image; });
    }
    var galGrid = section.querySelector('[data-gallery-grid], [data-widget-content-grid]')
      || section.querySelector(':scope > div[style*="grid"], :scope > div > div[style*="grid"]');
    if (!galGrid) {
      var galWrap = section.querySelector(':scope > div') || section;
      galGrid = document.createElement('div');
      galGrid.setAttribute('data-gallery-grid', 'true');
      galGrid.setAttribute('data-widget-content-grid', 'true');
      galWrap.appendChild(galGrid);
    } else {
      galGrid.setAttribute('data-gallery-grid', 'true');
      galGrid.setAttribute('data-widget-content-grid', 'true');
    }
    if (galGrid && galItems.length) {
      var galCols = cfg.columns || '3';
      galGrid.style.display = 'grid';
      galGrid.style.gridTemplateColumns = 'repeat(' + galCols + ', 1fr)';
      galGrid.style.gap = '15px';
      galGrid.innerHTML = galItems.map(function(item, i) {
        var imgUrl = item.image || item.url || '';
        return '<div style="position:relative;overflow:hidden;border-radius:12px;aspect-ratio:4/3;background:#f0f0f0;">'
          + '<img src="' + __escapeHtml(imgUrl) + '" alt="' + __escapeHtml(item.title || '') + '" data-gallery-index="' + i + '" style="width:100%;height:100%;object-fit:cover;position:relative;z-index:1;">'
          + '<div style="position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,0.75) 0%,transparent 50%);display:flex;align-items:flex-end;padding:16px;pointer-events:none;z-index:2;">'
          + '<div><h4 style="color:#fff;font-size:16px;font-weight:700;margin:0 0 4px;">' + __escapeHtml(item.title || '') + '</h4>'
          + (item.category ? '<p style="color:rgba(255,255,255,0.75);font-size:12px;margin:0;">' + __escapeHtml(item.category) + '</p>' : '')
          + '</div></div></div>';
      }).join('');
    }
    return;
  }
  if (type === 'video-playlist') {
    var plTitle = section.querySelector('[data-playlist-title], h2');
    if (plTitle && cfg.title) plTitle.textContent = cfg.title;
    var plVideos = Array.isArray(cfg.videos) ? cfg.videos : [];
    var plGrid = section.querySelector('[data-video-playlist-grid], [data-widget-content-grid]') || __widgetContentGrid(section);
    if (plGrid && plVideos.length) {
      var plCols = cfg.columns || '3';
      plGrid.style.display = 'grid';
      plGrid.style.gridTemplateColumns = 'repeat(' + plCols + ', 1fr)';
      plGrid.style.gap = '25px';
      plGrid.innerHTML = plVideos.map(function(video) {
        var embedUrl = __normalizeVideoUrl(video.url || '');
        var ytId = __youtubeId(embedUrl);
        var thumb = ytId ? 'https://img.youtube.com/vi/' + ytId + '/hqdefault.jpg' : '';
        var watchUrl = ytId ? 'https://www.youtube.com/watch?v=' + ytId : embedUrl;
        return '<div style="background:white;border-radius:16px;overflow:hidden;box-shadow:0 4px 15px rgba(0,0,0,0.08);">'
          + '<a href="' + __escapeHtml(watchUrl) + '" target="_blank" rel="noopener noreferrer" style="display:block;aspect-ratio:16/9;position:relative;background:#1e293b;text-decoration:none;">'
          + (thumb ? '<img src="' + thumb + '" alt="' + __escapeHtml(video.title || 'Video') + '" style="width:100%;height:100%;object-fit:cover;display:block;" referrerpolicy="no-referrer" />' : '')
          + '<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.35);"><span style="font-size:3rem;">▶</span></div></a>'
          + '<div style="padding:20px;"><h3 style="font-weight:600;margin:0 0 6px;">' + __escapeHtml(video.title || 'Video') + '</h3>'
          + (video.duration ? '<p style="color:#64748b;font-size:0.9rem;margin:0;">' + __escapeHtml(video.duration) + '</p>' : '')
          + '</div></div>';
      }).join('');
    }
    return;
  }
  if (type === 'slider') {
    var sliderHeight = cfg.height || '500px';
    section.style.height = sliderHeight;
    section.style.position = 'relative';
    section.style.overflow = 'hidden';
    var sliderImages = Array.isArray(cfg.images) ? cfg.images : [];
    var track = section.querySelector('[data-slider-track]') || section.querySelector(':scope > div');
    if (track && sliderImages.length) {
      track.setAttribute('data-slider-track', 'true');
      track.style.display = 'flex';
      track.style.height = '100%';
      track.style.transition = 'transform 0.5s ease';
      track.style.width = '100%';
      track.innerHTML = sliderImages.map(function(slide, i) {
        var url = slide.url || slide.image || '';
        var caption = slide.caption || slide.title || '';
        return '<div style="min-width:100%;position:relative;flex-shrink:0;height:100%;">'
          + '<img src="' + __escapeHtml(url) + '" alt="' + __escapeHtml(slide.alt || ('Slide ' + (i + 1))) + '" data-slide-index="' + i + '" style="width:100%;height:100%;object-fit:cover;" />'
          + (caption ? '<div style="position:absolute;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;"><div style="text-align:center;color:white;padding:40px;"><h2 style="font-size:3rem;font-weight:800;margin-bottom:15px;">' + __escapeHtml(caption) + '</h2></div></div>' : '')
          + '</div>';
      }).join('');
      section.setAttribute('data-slider-autoplay', cfg.autoplay !== false ? 'true' : 'false');
      section.setAttribute('data-slider-interval', String(cfg.interval || '3000'));
      delete section.dataset.sliderInit;
    }
    return;
  }
  if (type === 'sale-banner' || type === 'sale_banner') {
    if (cfg.backgroundImage) {
      section.style.backgroundImage = "url('" + String(cfg.backgroundImage).replace(/'/g, "\\'") + "')";
    }
    var overlay = section.querySelector('[data-sale-overlay]');
    if (overlay && cfg.overlayColor) overlay.style.background = cfg.overlayColor;
    var badge = section.querySelector('[data-sale-badge]');
    if (badge && cfg.badge) badge.textContent = cfg.badge;
    var saleTitle = section.querySelector('[data-sale-title]');
    if (saleTitle && cfg.title) saleTitle.textContent = cfg.title;
    var saleSubtitle = section.querySelector('[data-sale-subtitle]');
    if (saleSubtitle && cfg.subtitle) saleSubtitle.textContent = cfg.subtitle;
    var saleBtn = section.querySelector('[data-sale-button]');
    if (saleBtn) {
      if (cfg.buttonText) saleBtn.textContent = cfg.buttonText;
      if (cfg.buttonUrl) saleBtn.setAttribute('href', cfg.buttonUrl);
    }
    return;
  }
  if (type === 'product-categories' || type === 'product_categories') {
    var catTitle = section.querySelector('[data-categories-title]');
    var catSub = section.querySelector('[data-categories-subtitle]');
    if (catTitle && cfg.title) catTitle.textContent = cfg.title;
    if (catSub && cfg.subtitle) catSub.textContent = cfg.subtitle;
    var catGrid = section.querySelector('#categories-grid, .categories-grid');
    if (catGrid && cfg.columns) catGrid.style.gridTemplateColumns = 'repeat(' + cfg.columns + ', 1fr)';
    return;
  }
}

function __initWidgetSliders(root) {
  (root || document).querySelectorAll('section[data-section-id^="slider-"], section[data-widget-type="slider"]').forEach(function(section) {
    if (section.dataset.sliderInit === 'true') return;
    var track = section.querySelector('[data-slider-track]');
    if (!track || track.children.length <= 1) return;
    section.dataset.sliderInit = 'true';
    var index = 0;
    var intervalMs = parseInt(section.getAttribute('data-slider-interval') || '3000', 10);
    var autoplay = section.getAttribute('data-slider-autoplay') !== 'false';
    function goTo(i) {
      var n = track.children.length;
      index = ((i % n) + n) % n;
      track.style.transform = 'translateX(-' + (index * 100) + '%)';
      section.querySelectorAll('[data-slider-dot]').forEach(function(dot, di) {
        dot.style.background = di === index ? '#fff' : 'rgba(255,255,255,0.5)';
      });
    }
    var dotsHost = section.querySelector('[data-slider-dots]');
    if (dotsHost) {
      dotsHost.innerHTML = Array.prototype.map.call(track.children, function(_, i) {
        return '<button type="button" data-slider-dot="' + i + '" style="width:10px;height:10px;border-radius:50%;border:none;cursor:pointer;background:' + (i === 0 ? '#fff' : 'rgba(255,255,255,0.5)') + ';"></button>';
      }).join('');
      dotsHost.querySelectorAll('[data-slider-dot]').forEach(function(btn) {
        btn.addEventListener('click', function() { goTo(parseInt(btn.getAttribute('data-slider-dot'), 10)); });
      });
    }
    var prev = section.querySelector('[data-slider-prev]');
    var next = section.querySelector('[data-slider-next]');
    if (prev) prev.addEventListener('click', function() { goTo(index - 1); });
    if (next) next.addEventListener('click', function() { goTo(index + 1); });
    if (autoplay) setInterval(function() { goTo(index + 1); }, intervalMs);
  });
}

async function hydrateWidgetInstances() {
  if (!window.WEBSITE_ID) return;
  var byInstance = {};
  (Array.isArray(window.WIDGET_INSTANCES) ? window.WIDGET_INSTANCES : []).forEach(function(item) {
    if (item && item.instance_id && (item.status || 'active') !== 'removed') byInstance[item.instance_id] = item;
  });
  try {
    var resp = await fetch(__apiSitesRoot() + window.WEBSITE_ID + '/resources/widget_instances?limit=200', { headers: __authHeaders() });
    if (resp.ok) {
      var data = await resp.json();
      (data.items || []).forEach(function(item) {
        if ((item.status || 'active') === 'removed') return;
        if (item.instance_id) byInstance[item.instance_id] = item;
      });
    }
  } catch (e) {}
  if (!Object.keys(byInstance).length) return;
  document.querySelectorAll('section[data-section-id], section[data-widget-instance]').forEach(function(section) {
    var id = section.getAttribute('data-widget-instance') || section.getAttribute('data-section-id');
    if (id && byInstance[id]) __applyWidgetInstanceConfig(section, byInstance[id]);
  });
  __initWidgetSliders(document);
}

function __stampOperateGrid(binding, grid) {
  if (!grid || !binding || !binding.resource || grid.getAttribute('data-resource-list')) return;
  grid.setAttribute('data-resource-list', binding.resource);
  grid.setAttribute('data-resource-title', binding.title || 'name');
  grid.setAttribute('data-resource-body', binding.body || 'description');
  grid.setAttribute('data-resource-layout', binding.layout || 'card');
  if (binding.image) grid.setAttribute('data-resource-image', binding.image);
  if (binding.meta) {
    grid.setAttribute('data-resource-meta', binding.meta);
    if (binding.meta_prefix) grid.setAttribute('data-resource-meta-prefix', binding.meta_prefix);
  }
}

function wireWidgetGrids() {
  var bindings = window.WIDGET_BINDINGS || {};
  Object.keys(bindings).forEach(function(widgetId) {
    var binding = bindings[widgetId];
    if (!binding || !binding.resource) return;
    var sectionSel = '[data-operate-widget="' + widgetId + '"]';
    document.querySelectorAll(sectionSel + ' .operate-resource-grid, ' + sectionSel + ' [data-widget-content-grid]').forEach(function(grid) {
      __stampOperateGrid(binding, grid);
    });
  });
  // Legacy sections dropped before data-operate-widget was added
  if (bindings.services) {
    document.querySelectorAll('section[data-section-name="Services"]:not([data-operate-widget]) [data-widget-content-grid]').forEach(function(grid) {
      __stampOperateGrid(bindings.services, grid);
    });
  }
}

function wireLegacyContactForms() {
  var resource = window.CONTACT_RESOURCE || 'inquiries';

  function inferName(input) {
    if (input.name) return input.name;
    if ((input.type || '').toLowerCase() === 'email') return 'email';
    var ph = (input.getAttribute('placeholder') || '').toLowerCase();
    if (ph.indexOf('email') >= 0) return 'email';
    if (ph.indexOf('subject') >= 0) return 'subject';
    if (ph.indexOf('message') >= 0 || input.tagName === 'TEXTAREA') return 'message';
    if (ph.indexOf('name') >= 0) return 'name';
    var group = input.closest('.form-group');
    if (group) {
      var lbl = group.querySelector('label');
      if (lbl) {
        var text = (lbl.textContent || '').trim().toLowerCase();
        if (text.indexOf('first') >= 0) return 'first_name';
        if (text.indexOf('last') >= 0) return 'last_name';
        if (text.indexOf('email') >= 0) return 'email';
        if (text.indexOf('subject') >= 0) return 'subject';
        if (text.indexOf('message') >= 0) return 'message';
        if (text.indexOf('name') >= 0) return 'name';
      }
    }
    return null;
  }

  document.querySelectorAll('div.contact-form').forEach(function(div) {
    if (div.closest('form[data-schema-form="true"]')) return;
    var form = document.createElement('form');
    form.className = div.className;
    form.setAttribute('data-schema-form', 'true');
    form.setAttribute('data-schema-resource', resource);
    while (div.firstChild) form.appendChild(div.firstChild);
    div.replaceWith(form);
    form.querySelectorAll('input, textarea, select').forEach(function(inp) {
      if (!inp.name) {
        var n = inferName(inp);
        if (n) inp.name = n;
      }
    });
    if (!form.querySelector('[data-schema-form-message]')) {
      var msg = document.createElement('div');
      msg.setAttribute('data-schema-form-message', '');
      msg.style.cssText = 'font-size:.9rem;color:#64748b;margin-top:8px;';
      var btn = form.querySelector('button[type="submit"], button');
      if (btn) form.insertBefore(msg, btn);
      else form.appendChild(msg);
    }
  });

  // Convert .newsletter-form divs to schema forms (match backend _wire_newsletter_forms).
  // If hydrate already nested a <form> inside the div, promote that form instead of nesting again.
  document.querySelectorAll('div.newsletter-form').forEach(function(div) {
    if (div.closest('form[data-schema-form="true"]')) return;
    var nested = null;
    for (var i = 0; i < div.children.length; i++) {
      var child = div.children[i];
      if (child.tagName === 'FORM' && child.getAttribute('data-schema-form') === 'true') {
        nested = child;
        break;
      }
    }
    if (nested) {
      var mergedClass = ((nested.className || '') + ' ' + (div.className || '')).trim();
      nested.className = mergedClass;
      if (!nested.getAttribute('data-schema-resource')) {
        nested.setAttribute('data-schema-resource', 'subscribers');
      }
      div.replaceWith(nested);
      return;
    }
    var nlForm = document.createElement('form');
    nlForm.className = div.className;
    nlForm.setAttribute('data-schema-form', 'true');
    nlForm.setAttribute('data-schema-resource', 'subscribers');
    while (div.firstChild) nlForm.appendChild(div.firstChild);
    div.replaceWith(nlForm);
    nlForm.querySelectorAll('input, textarea, select').forEach(function(inp) {
      if (!inp.name) {
        var n = inferName(inp);
        if (n) inp.name = n;
      }
    });
    var nlBtn = nlForm.querySelector('button');
    if (nlBtn && !nlBtn.getAttribute('type')) nlBtn.type = 'submit';
    if (!nlForm.querySelector('[data-schema-form-message]')) {
      var nlMsg = document.createElement('div');
      nlMsg.setAttribute('data-schema-form-message', '');
      nlMsg.style.cssText = 'font-size:.9rem;color:#64748b;margin-top:8px;width:100%;';
      if (nlBtn) nlForm.insertBefore(nlMsg, nlBtn);
      else nlForm.appendChild(nlMsg);
    }
  });

  // Newsletter signup: keep phone input, remove visible "Phone" label text
  document.querySelectorAll('form.newsletter-form, .newsletter-section form[data-schema-resource="subscribers"], form[data-schema-resource="subscribers"]').forEach(function(form) {
    form.querySelectorAll('label').forEach(function(label) {
      var text = (label.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (text === 'phone' || text === 'phone number') label.remove();
    });
    var phoneInp = form.querySelector('input[name="phone"], input[type="tel"]');
    if (phoneInp && !phoneInp.getAttribute('aria-label')) phoneInp.setAttribute('aria-label', 'Phone');
  });

  // Bind existing <form> elements that don't have data-schema-form yet
  function inferResource(form) {
    var fields = Array.from(form.querySelectorAll('input, select, textarea'));
    var names = fields.map(function(f) { return (f.name || f.getAttribute('placeholder') || '').toLowerCase(); }).join(' ');
    var html = (form.innerHTML || '').toLowerCase();
    if (names.indexOf('party_size') >= 0 || names.indexOf('guest') >= 0 || names.indexOf('reserv') >= 0 || html.indexOf('reserv') >= 0 || html.indexOf('book') >= 0 || names.indexOf('table') >= 0 || names.indexOf('time') >= 0 && names.indexOf('date') >= 0) return 'bookings';
    if (fields.length === 1 && fields[0].type === 'email' || names.indexOf('subscri') >= 0 || html.indexOf('subscri') >= 0 || html.indexOf('newsletter') >= 0 || html.indexOf('mailing') >= 0) return 'subscribers';
    return resource;
  }

  document.querySelectorAll('form').forEach(function(form) {
    if (form.getAttribute('data-schema-form') === 'true') return;
    if (form.closest('[data-quiz-form]')) return;
    var inputs = form.querySelectorAll('input:not([type="hidden"]), textarea, select');
    if (!inputs.length) return;
    var res = inferResource(form);
    form.setAttribute('data-schema-form', 'true');
    form.setAttribute('data-schema-resource', res);
    inputs.forEach(function(inp) {
      if (!inp.name) {
        var n = inferName(inp);
        if (n) inp.name = n;
      }
    });
    if (!form.querySelector('[data-schema-form-message]')) {
      var msg = document.createElement('div');
      msg.setAttribute('data-schema-form-message', '');
      msg.style.cssText = 'font-size:.9rem;color:#64748b;margin-top:8px;';
      var btn = form.querySelector('button[type="submit"], button');
      if (btn) form.insertBefore(msg, btn);
      else form.appendChild(msg);
    }
  });

  // Wrap standalone email+subscribe patterns (not inside a form) into a schema form
  document.querySelectorAll('section, div, footer').forEach(function(container) {
    if (container.closest('form')) return;
    // Newsletter/contact wrappers are converted above — never nest a second form inside them.
    if (container.classList && (container.classList.contains('newsletter-form') || container.classList.contains('contact-form'))) return;
    var emailInput = container.querySelector('input[type="email"]');
    if (!emailInput) return;
    if (emailInput.closest('form')) return;
    if (emailInput.closest('.newsletter-form, .contact-form')) return;
    var btn = container.querySelector('button');
    if (!btn) return;
    var text = (container.textContent || '').toLowerCase();
    if (text.indexOf('subscri') < 0 && text.indexOf('newsletter') < 0 && text.indexOf('mailing') < 0 && text.indexOf('join') < 0) return;
    var form = document.createElement('form');
    form.setAttribute('data-schema-form', 'true');
    form.setAttribute('data-schema-resource', 'subscribers');
    form.style.cssText = emailInput.parentElement.style ? emailInput.parentElement.style.cssText : '';
    emailInput.name = emailInput.name || 'email';
    var parent = emailInput.parentElement;
    var elementsToWrap = [emailInput];
    if (btn.parentElement === parent || btn.parentElement === emailInput.parentElement) elementsToWrap.push(btn);
    elementsToWrap.forEach(function(el) { form.appendChild(el); });
    var msgDiv = document.createElement('div');
    msgDiv.setAttribute('data-schema-form-message', '');
    msgDiv.style.cssText = 'font-size:.9rem;color:#64748b;margin-top:8px;width:100%;';
    form.appendChild(msgDiv);
    btn.type = 'submit';
    parent.appendChild(form);
  });
}

function initEditorPageNav() {
  function bindEditorNavLink(link) {
    if (link.dataset.editorNavBound === 'true') return;
    var slug = link.getAttribute('data-editor-page') || link.getAttribute('data-page');
    if (!slug) {
      var href = link.getAttribute('href') || '';
      var match = href.match(/[?&]page=([^&]+)/);
      if (match) slug = match[1];
    }
    if (!slug) return;
    link.setAttribute('data-editor-page', slug);
    link.dataset.editorNavBound = 'true';
    link.addEventListener('click', function(e) {
      if (link.getAttribute('data-snagcart-proceed-checkout') !== null || slug === 'checkout') {
        e.preventDefault();
        if (typeof snagcartProceedToCheckout === 'function') {
          snagcartProceedToCheckout();
          return;
        }
      }
      e.preventDefault();
      window.parent.postMessage({ type: 'switchEditorPage', slug: slug }, '*');
    });
  }
  document.querySelectorAll('[data-editor-page], .header-nav a, nav .nav-link, a[href*="?page="], [data-sale-button], .cta-primary, .cta-secondary, a.btn-primary, a.btn-secondary, .pricing-btn').forEach(bindEditorNavLink);
}
window.initEditorPageNav = initEditorPageNav;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrateOperateBackedContent);
} else {
  hydrateOperateBackedContent();
}
</script>

</head><body>
${!hasHeaderSection ? `<!-- Unified Navigation (shown when AI site has no Header section) -->
<nav style="background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); color: white; padding: 15px 30px; display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 1000; box-shadow: 0 4px 20px rgba(0,0,0,0.15);">
  <div style="display:flex;align-items:center;gap:14px;min-width:0;">${previewBrandContent}</div>
  <div style="display: flex; align-items: center; gap: 5px;">${navLinks}${ecommerceLinks}</div>
</nav>` : ''}

<!-- Cart Overlay -->
<div class="cart-overlay" onclick="toggleCart()"></div>

<!-- Cart Sidebar -->
<div class="cart-sidebar" id="cart-sidebar">
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px;">
    <h2 style="font-size: 1.5rem; font-weight: 700;">Shopping Cart</h2>
    <button onclick="toggleCart()" style="background: none; border: none; font-size: 1.5rem; cursor: pointer;">✕</button>
  </div>
  <div id="cart-items" style="margin-bottom: 20px;"></div>
  <div id="cart-total" style="font-size: 1.3rem; font-weight: 700; padding-top: 20px; border-top: 2px solid #eee;"></div>
  <button id="cart-sidebar-checkout-btn" type="button" data-snagcart-checkout onclick="snagcartStartCheckout()" style="width: 100%; padding: 15px; background: ${primaryColor}; color: white; border: none; border-radius: 10px; font-weight: 600; margin-top: 20px; cursor: pointer;">Checkout</button>
</div>

<div id="editor-page-content" data-editor-page-root="true">
${processedContent}
</div>

${website?.custom_body_code || ''}

${website?.js_code ? `<script>
/* Website JS from AI builder */
${website.js_code}
</script>` : ''}

<script>
// Cart functionality
(function migrateLegacyCart() {
  var key = 'cart_${websiteId}';
  try {
    var legacy = localStorage.getItem('snagcart_${websiteId}');
    if (legacy && !localStorage.getItem(key)) localStorage.setItem(key, legacy);
  } catch (e) {}
})();
window.cart = JSON.parse(localStorage.getItem('cart_${websiteId}') || '[]');
window.currencySymbol = '${currencySymbol}';
window.WEBSITE_NAME = ${JSON.stringify(website?.name || 'Store')};
updateCartUI();

function cartStorageKey() { return 'cart_${websiteId}'; }

function reloadEditorCart() {
  try {
    window.cart = JSON.parse(localStorage.getItem(cartStorageKey()) || '[]');
  } catch (e) {
    window.cart = [];
  }
  if (!Array.isArray(window.cart)) window.cart = [];
  window.cart = window.cart.map(function(item) {
    var qty = Number(item.qty || item.quantity || 1);
    var pid = String(item.product_id || item.id || '');
    return Object.assign({}, item, { id: pid, product_id: pid, qty: qty, quantity: qty });
  });
}

function updateCartQty(productId, newQty) {
  if (typeof snagcartCartPageUpdateQty === 'function') {
    snagcartCartPageUpdateQty(productId, newQty);
    return;
  }
  if (newQty <= 0) {
    removeFromCart(productId);
    return;
  }
  window.cart = window.cart.map(function(item) {
    if (String(item.id || item.product_id) === String(productId)) {
      return Object.assign({}, item, { qty: newQty, quantity: newQty });
    }
    return item;
  });
  localStorage.setItem(cartStorageKey(), JSON.stringify(window.cart));
  updateCartUI();
}

function renderEditorCartRow(item) {
  var qty = item.qty || item.quantity || 1;
  var pid = String(item.product_id || item.id || '');
  var lineTotal = (Number(item.price || 0) * qty).toFixed(2);
  var price = Number(item.price || 0).toFixed(2);
  return '<div style="display:flex;align-items:center;gap:1rem;padding:1rem 1.5rem;border-bottom:1px solid #f1f5f9;">' +
    '<div style="flex:1;min-width:0;"><div style="font-weight:700;font-size:.9rem;">' + (item.name || '') + '</div>' +
    '<div style="color:#64748b;font-size:.8rem;">' + window.currencySymbol + price + ' each</div></div>' +
    '<div style="display:flex;align-items:center;gap:.5rem;flex-shrink:0;">' +
    '<button type="button" onclick="updateCartQty(\\'' + pid + '\\',' + (qty - 1) + ')" style="width:28px;height:28px;border:1.5px solid #e2e8f0;border-radius:6px;background:#f8fafc;cursor:pointer;font-weight:700;">−</button>' +
    '<span style="font-weight:700;min-width:24px;text-align:center;">' + qty + '</span>' +
    '<button type="button" onclick="updateCartQty(\\'' + pid + '\\',' + (qty + 1) + ')" style="width:28px;height:28px;border:1.5px solid #e2e8f0;border-radius:6px;background:#f8fafc;cursor:pointer;font-weight:700;">+</button>' +
    '</div>' +
    '<div style="font-weight:800;color:#16a34a;min-width:60px;text-align:right;">' + window.currencySymbol + lineTotal + '</div>' +
    '<button type="button" onclick="removeFromCart(\\'' + pid + '\\')" style="background:none;border:none;color:#ef4444;cursor:pointer;font-size:1.1rem;">✕</button>' +
    '</div>';
}

function renderEditorSidebarCartRow(item) {
  var qty = item.qty || item.quantity || 1;
  var pid = String(item.product_id || item.id || '');
  return '<div style="display:flex;justify-content:space-between;align-items:center;padding:15px 0;border-bottom:1px solid #eee;gap:10px;">' +
    '<div style="flex:1;min-width:0;"><div style="font-weight:600;">' + (item.name || '') + '</div>' +
    '<div style="color:#666;font-size:.9rem;">' + window.currencySymbol + Number(item.price || 0).toFixed(2) + ' each</div></div>' +
    '<div style="display:flex;align-items:center;gap:.35rem;flex-shrink:0;">' +
    '<button type="button" onclick="updateCartQty(\\'' + pid + '\\',' + (qty - 1) + ')" style="width:24px;height:24px;border:1px solid #ddd;border-radius:6px;background:#fff;cursor:pointer;">−</button>' +
    '<span style="min-width:18px;text-align:center;font-weight:600;">' + qty + '</span>' +
    '<button type="button" onclick="updateCartQty(\\'' + pid + '\\',' + (qty + 1) + ')" style="width:24px;height:24px;border:1px solid #ddd;border-radius:6px;background:#fff;cursor:pointer;">+</button>' +
    '</div>' +
    '<button type="button" onclick="removeFromCart(\\'' + pid + '\\')" style="background:#fee;color:#e55;border:none;padding:5px 8px;border-radius:5px;cursor:pointer;flex-shrink:0;">✕</button>' +
    '</div>';
}

function toggleCart() {
  document.getElementById('cart-sidebar').classList.toggle('open');
  document.querySelector('.cart-overlay').classList.toggle('open');
}

function addToCart(productId, name, price) {
  const existing = window.cart.find(item => item.id === productId);

  if (existing) {
    existing.qty = (existing.qty || existing.quantity || 0) + 1;
    existing.quantity = existing.qty;
  } else {
    window.cart.push({ id: productId, product_id: productId, name, price, qty: 1, quantity: 1 });
  }
  localStorage.setItem(cartStorageKey(), JSON.stringify(window.cart));
  updateCartUI();
  window.parent.postMessage({type:'addToCart', productId},'*');
}

function removeFromCart(productId) {
  if (typeof snagcartCartPageRemoveItem === 'function') {
    snagcartCartPageRemoveItem(productId);
    return;
  }
  window.cart = window.cart.filter(item => String(item.id || item.product_id) !== String(productId));
  localStorage.setItem(cartStorageKey(), JSON.stringify(window.cart));
  updateCartUI();
}

function updateCartUI() {
  reloadEditorCart();
  const countEl = document.getElementById('cart-count');
  const navCountEl = document.getElementById('nav-cart-count');
  const itemsEl = document.getElementById('cart-items');
  const totalEl = document.getElementById('cart-total');
  
  const totalQty = window.cart.reduce((sum, item) => sum + (item.qty || item.quantity || 0), 0);
  const totalPrice = window.cart.reduce((sum, item) => sum + (item.price * (item.qty || item.quantity || 0)), 0);
  const shipping = 9.99;
  const grandTotal = totalPrice + (window.cart.length > 0 ? shipping : 0);
  
  if (countEl) countEl.textContent = totalQty;
  if (navCountEl) {
    navCountEl.textContent = totalQty;
    navCountEl.style.display = totalQty > 0 ? 'inline' : 'none';
  }
  
  // Update sidebar cart
  if (itemsEl) {
    itemsEl.innerHTML = window.cart.length ? window.cart.map(item => renderEditorSidebarCartRow(item)).join('') : '<p style="color: #999; text-align: center; padding: 40px 0;">Your cart is empty</p>';
  }
  
  if (totalEl) {
    totalEl.innerHTML = window.cart.length ? \`Total: <span style="color: ${primaryColor};">\${window.currencySymbol}\${grandTotal.toFixed(2)}</span>\` : '';
  }
  
  // Update cart widget on page
  const cartItemsDisplay = document.querySelector('.cart-items-display');
  if (cartItemsDisplay) {
    cartItemsDisplay.innerHTML = window.cart.length ? window.cart.map(item => \`
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 15px; border-bottom: 1px solid #e2e8f0;">
        <div style="display: flex; align-items: center; gap: 15px;">
          <div style="width: 60px; height: 60px; background: #f1f5f9; border-radius: 10px; display: flex; align-items: center; justify-content: center;">🛍️</div>
          <div><div style="font-weight: 600;">\${item.name}</div><div style="color: #64748b; font-size: 0.9rem;">Qty: \${item.qty}</div></div>
        </div>
        <div style="text-align: right;">
          <div style="font-weight: 600;">\${window.currencySymbol}\${(item.price * item.qty).toFixed(2)}</div>
          <button onclick="removeFromCart('\${item.id}')" style="color: #ef4444; background: none; border: none; cursor: pointer; font-size: 0.85rem;">Remove</button>
        </div>
      </div>
    \`).join('') : '<p style="color: #94a3b8; text-align: center; padding: 40px;">Your cart is empty. Start shopping!</p>';
  }
  
  // Update cart subtotals and totals on page
  document.querySelectorAll('.cart-subtotal-display').forEach(el => { el.textContent = \`\${window.currencySymbol}\${totalPrice.toFixed(2)}\`; });
  document.querySelectorAll('.cart-total-display').forEach(el => { el.textContent = \`\${window.currencySymbol}\${grandTotal.toFixed(2)}\`; });
  
  // Update checkout widget
  const checkoutItems = document.querySelector('.checkout-items-display');
  if (checkoutItems) {
    checkoutItems.innerHTML = window.cart.map(item => \`
      <div style="display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #e2e8f0;">
        <span>\${item.name} × \${item.qty}</span>
        <span style="font-weight: 600;">\${window.currencySymbol}\${(item.price * item.qty).toFixed(2)}</span>
      </div>
    \`).join('');
  }
  document.querySelectorAll('.checkout-subtotal').forEach(el => { el.textContent = \`\${window.currencySymbol}\${totalPrice.toFixed(2)}\`; });
  document.querySelectorAll('.checkout-total').forEach(el => { el.textContent = \`\${window.currencySymbol}\${grandTotal.toFixed(2)}\`; });

  if (typeof snagcartInitCartPage === 'function' && document.getElementById('cart-items-list')) {
    snagcartInitCartPage();
  } else if (document.getElementById('cart-items-list')) {
    const list = document.getElementById('cart-items-list');
    const loading = document.getElementById('cart-loading');
    const empty = document.getElementById('cart-empty');
    const content = document.getElementById('cart-content');
    if (loading) loading.style.display = 'none';
    if (!window.cart.length) {
      if (content) content.style.display = 'none';
      if (empty) empty.style.display = 'block';
    } else {
      if (empty) empty.style.display = 'none';
      if (content) content.style.display = 'grid';
      list.innerHTML = window.cart.map(item => renderEditorCartRow(item)).join('');
      const subtotal = totalPrice;
      const tax = subtotal * 0.08;
      const delivery = subtotal >= 50 ? 0 : (subtotal > 0 ? 4.99 : 0);
      const total = subtotal + tax + delivery;
      const set = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = window.currencySymbol + val.toFixed(2); };
      set('subtotal-val', subtotal);
      set('tax-val', tax);
      set('total-val', total);
      const delEl = document.getElementById('delivery-val');
      if (delEl) delEl.textContent = delivery === 0 && subtotal > 0 ? 'Free' : (subtotal === 0 ? 'Free' : window.currencySymbol + delivery.toFixed(2));
      const cntEl = document.getElementById('item-count');
      if (cntEl) cntEl.textContent = totalQty;
    }
  }

  if (typeof snagcartInitCheckoutPage === 'function' && document.getElementById('checkout-items')) {
    snagcartInitCheckoutPage();
  }
}

function openCheckout() {
  if (typeof snagcartProceedToCheckout === 'function') {
    snagcartProceedToCheckout();
    return;
  }
  if (typeof snagcartStartCheckout === 'function') {
    snagcartStartCheckout();
    return;
  }
  if (window.cart.length === 0) {
    alert('Your cart is empty!');
    return;
  }
  window.parent.postMessage({type:'goToCheckout'},'*');
}

// Make text editable and notify parent about selection
(function() {
  var editorIdCounter = 0;

  function markEditableElements() {
    function markOne(el, opts) {
      if (!el || el.closest('nav')) return;
      if (el.closest('.footer-social')) return;
      if (el.querySelector && el.querySelector('a') && !el.closest('footer') && !(opts && opts.allowNestedLinks)) return;
      el.setAttribute('data-editable', 'true');
      el.setAttribute('contenteditable', 'true');
      if (!el.dataset.editorId) {
        editorIdCounter += 1;
        el.dataset.editorId = 'ed-' + editorIdCounter;
      }
      el.addEventListener('focus', handleFocus);
      el.addEventListener('click', opts && opts.preventNav ? function(e) { e.preventDefault(); handleFocus(e); } : handleFocus);
    }

    document.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,li,label,strong,small,td,th,figcaption').forEach(function(el) { markOne(el); });
    document.querySelectorAll('.section-eyebrow,.section-title,.section-subtitle,.hero-eyebrow,.hero-title,.hero-subtitle,.testimonial-name,.testimonial-role,.testimonial-text,.price-amount,.header-cta,.feature-card h3,.feature-card p,.pricing-card h3,.pricing-card p,.about-content li,.step-title,.step-desc').forEach(function(el) { markOne(el); });
    document.querySelectorAll('a.btn-primary,a.btn-secondary,button.header-cta,a.header-cta,.pricing-btn,.cta-primary,.cta-secondary').forEach(function(el) { markOne(el, { preventNav: true }); });
    document.querySelectorAll('.widget-hero a, .widget-hero button, section[data-section-name="Hero"] a, section[data-section-name="Hero"] button, .hero-actions a, .hero-actions button, .btn-group a, .btn-group button').forEach(function(el) {
      if (el.closest('nav')) return;
      markOne(el, { preventNav: true });
    });
    document.querySelectorAll('section a, section button').forEach(function(el) {
      if (el.closest('nav, form, footer')) return;
      if (el.type === 'submit') return;
      if (el.hasAttribute('data-editable')) return;
      var cls = (el.className || '').toLowerCase();
      var isCta = el.tagName === 'BUTTON' || cls.indexOf('btn') >= 0 || cls.indexOf('cta') >= 0;
      if (!isCta && el.tagName === 'A') isCta = !!el.closest('.btn-group,.hero-actions,.widget-button,[data-widget-type="button"]');
      if (isCta) markOne(el, { preventNav: true });
    });
    document.querySelectorAll('footer a').forEach(function(el) { markOne(el, { preventNav: true }); });
    document.querySelectorAll('footer .footer-bottom, footer [data-footer-copyright]').forEach(function(el) { markOne(el); });
    document.querySelectorAll('footer span, footer p').forEach(function(el) {
      if (/©|&copy;|all rights reserved/i.test(el.textContent || '')) markOne(el);
    });
  }

  function handleFocus(e) {
    var el = e.target;
    if (!el.hasAttribute('data-editable')) return;
    var rect = el.getBoundingClientRect();
    var styles = window.getComputedStyle(el);
    window.parent.postMessage({
      type: 'textElementSelected',
      editorId: el.dataset.editorId || '',
      tagName: el.tagName,
      href: el.tagName === 'A' ? (el.getAttribute('href') || '') : '',
      rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      styles: {
        fontWeight: styles.fontWeight,
        fontStyle: styles.fontStyle,
        textDecoration: styles.textDecoration,
        color: styles.color,
        backgroundColor: styles.backgroundColor,
        fontSize: styles.fontSize,
        fontFamily: styles.fontFamily,
        textAlign: styles.textAlign
      }
    }, '*');
  }

  document.addEventListener('DOMContentLoaded', markEditableElements);
  markEditableElements();

  document.addEventListener('input', function(e) {
    if (e.target.hasAttribute('data-editable')) {
      window.parent.postMessage({ type: 'contentChanged' }, '*');
    }
  });

  document.addEventListener('click', function(e) {
    var target = e.target;

    if (target.tagName !== 'IMG') {
      var galleryImg = target.closest('[data-gallery-grid] div, [data-widget-content-grid] div');
      if (galleryImg) {
        var nestedImg = galleryImg.querySelector('img[data-gallery-index], img');
        if (nestedImg) target = nestedImg;
      }
    }

    // Case 1: Direct <img> click
    if (target.tagName === 'IMG') {
      e.preventDefault();
      // Site logo / brand mark — use brand controls, not Replace Image
      var isSiteLogo = target.getAttribute('data-site-logo') === '1'
        || !!(target.closest && target.closest('.header-logo, .site-logo, [data-site-logo="1"]'));
      if (isSiteLogo) {
        window.parent.postMessage({ type: 'logoClick', src: target.src || '' }, '*');
        return;
      }
      if (!target.dataset.imageEditId) {
        target.dataset.imageEditId = 'img-edit-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
      }
      window.parent.postMessage({
        type: 'imageClick',
        src: target.src,
        srcAttr: target.getAttribute('src') || '',
        mode: 'img',
        imageEditId: target.dataset.imageEditId,
        galleryIndex: target.dataset.galleryIndex || '',
        slideIndex: target.dataset.slideIndex || '',
        sectionId: (target.closest('[data-section-id]') || {}).getAttribute?.('data-section-id') || '',
      }, '*');
      return;
    }

    // Case 2: Click on element with background-image (e.g. hero/section images)
    var el = target;
    while (el && el !== document.body) {
      var style = window.getComputedStyle(el);
      var bg = style.backgroundImage || '';

      if (bg && bg !== 'none' && bg.indexOf('url(') !== -1) {
        // Extract URL from background-image: url("...")
        var match = bg.match(/url\(["']?(.*?)["']?\)/);
        var bgUrl = match && match[1] ? match[1] : '';
        if (bgUrl) {
          e.preventDefault();
          if (!el.dataset.imageEditId) {
            el.dataset.imageEditId = 'img-edit-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
          }
          window.parent.postMessage({
            type: 'imageClick',
            src: bgUrl,
            srcAttr: bgUrl,
            mode: 'background',
            imageEditId: el.dataset.imageEditId,
            selector: el.getAttribute('data-section-id') || el.className || el.tagName
          }, '*');
          return;
        }
      }
      el = el.parentElement;
    }
  });
})();

// Fix broken stock photos and keep external images loading inside the preview iframe.
(function() {
  document.querySelectorAll('img').forEach(function(img) {
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', function() {
      if (img.dataset.fallbackApplied) return;
      img.dataset.fallbackApplied = '1';
      img.src = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=600&fit=crop&auto=format';
    });
  });
})();

// Apply design system (colors & font) to headings and buttons
// Only applies when elements have NO class-based styling (legacy templates)
(function() {
  try {
    var primary = '${primaryColor}';
    var secondary = '${secondaryColor}';
    var font = '${fontFamily}';
    
    // Only apply font if body has no font-family set via CSS
    var bodyFont = window.getComputedStyle(document.body).fontFamily;
    var hasAiFont = bodyFont && bodyFont !== '' && !bodyFont.includes('Times') && !bodyFont.includes('serif') && bodyFont !== 'normal';
    if (!hasAiFont && document.body) {
      document.body.style.fontFamily = font + ", -apple-system, system-ui, sans-serif";
    }

    function shouldApplyThemeColor(el) {
      if (el.getAttribute('data-custom-color') === 'true') return false;
      if (el.style && el.style.color) return false;
      return true;
    }

    // Apply design color tokens only where the user has not customized inline colors.
    document.querySelectorAll('h1, h2, h3, h4, h5, h6, .section-title, .section-eyebrow, [data-design-primary="true"]').forEach(function(el) {
      if (el.closest('nav')) return;
      if (!shouldApplyThemeColor(el)) return;
      el.style.color = primary;
    });
    document.querySelectorAll('p, li, .section-subtitle, .lead, [data-design-secondary="true"]').forEach(function(el) {
      if (el.closest('nav') || el.closest('button')) return;
      if (!shouldApplyThemeColor(el)) return;
      var current = window.getComputedStyle(el).color || '';
      if (current === 'rgb(0, 0, 0)' || current === 'rgb(17, 24, 39)') {
        el.style.color = '#475569';
      }
    });

    // Secondary color for buttons ONLY if they have no class-based styling
    document.querySelectorAll('button, a').forEach(function(el) {
      if (el.closest('nav')) return;
      var isButton = el.tagName === 'BUTTON' || el.getAttribute('role') === 'button' || (el.className || '').toLowerCase().includes('btn');
      if (!isButton) return;
      if (el.getAttribute('data-custom-color') === 'true') return;
      if (el.style && el.style.backgroundColor && el.style.backgroundColor !== 'transparent') return;
      if (!el.style.backgroundImage) {
        el.style.backgroundColor = secondary;
      }
      if (!el.style.borderColor) {
        el.style.borderColor = secondary;
      }
      if (!el.style.color || el.style.color === 'black') {
        el.style.color = '#ffffff';
      }
    });
  } catch (e) {
    console.error('Design system apply error', e);
  }
})();

</script>
<script>
${siteAuthRuntime}
</script>
<script>
// Auto-resize: notify parent of full content height so iframe shows entire page
(function() {
  function sendHeight() {
    var h = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
    window.parent.postMessage({ type: 'iframeHeight', height: h }, '*');
  }
  // Send on load
  window.addEventListener('load', sendHeight);
  document.addEventListener('DOMContentLoaded', sendHeight);
  // Send after a short delay to catch lazy-loaded images
  setTimeout(sendHeight, 500);
  setTimeout(sendHeight, 1500);
  // Observe DOM mutations (widgets added by SiteBrain)
  var resizeObserver = new MutationObserver(function() { setTimeout(sendHeight, 100); });
  resizeObserver.observe(document.body, { childList: true, subtree: true, attributes: true });
})();

document.addEventListener('click', function (event) {
  var trigger = event.target.closest('.editor-brand-chip button');
  if (trigger) {
    event.preventDefault();
    event.stopPropagation();
  }
});
</script>
<script>
${snagcartCheckoutRuntime}
</script>
<script>
${orderConfirmationRuntime}
</script></body></html>`;
  }, [website, htmlContent, cssContent, pages, currentPage, products, liveServices, widgetConfigs, widgetInstances, sections, primaryColor, secondaryColor, fontFamily, websiteId, token, getCurrencySymbol, ecomSettings, renderSchemaWidgetsIntoHtml, brandName, contactResource, widgetBindings, snagcartCheckoutRuntime, orderConfirmationRuntime, siteAuthRuntime, editorApiBase]);

  const getPreviewHtml = useCallback(() => {
    const liveHtml = htmlContentRef.current || htmlContent;
    if (currentPage && STANDALONE_DASHBOARD_SLUGS.has(currentPage.slug)) {
      return prepareStandaloneDashboardPreview(liveHtml, websiteId);
    }
    return getFullHtml();
  }, [currentPage, htmlContent, websiteId, getFullHtml]);

  getPreviewHtmlRef.current = getPreviewHtml;

  const loadPreviewIframe = useCallback(() => {
    if (loading || !currentPage?.id) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    const storedHtml = htmlContentRef.current || htmlContent;
    if (!storedHtml || !String(storedHtml).trim()) return;
    try {
      const nextHtml = getPreviewHtmlRef.current();
      if (!nextHtml || !String(nextHtml).trim()) return;
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = '';
      }
      const blob = new Blob([nextHtml], { type: 'text/html;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      previewBlobUrlRef.current = blobUrl;
      iframe.removeAttribute('srcdoc');
      iframe.src = blobUrl;
    } catch (error) {
      console.error('Failed to load editor preview iframe', error);
    }
  }, [loading, currentPage?.id, htmlContent, website?.logo_url, brandName]);

  useEffect(() => () => {
    if (previewBlobUrlRef.current) {
      URL.revokeObjectURL(previewBlobUrlRef.current);
      previewBlobUrlRef.current = '';
    }
  }, []);

  // Reload iframe when page data arrives, page switches, nav visibility changes, or refresh is requested.
  const pagesNavSignature = useMemo(
    () => (pages || []).map((p) => `${p.id}:${p.nav_visible ? 1 : 0}:${p.slug || ''}`).join('|'),
    [pages],
  );
  useEffect(() => {
    loadPreviewIframe();
    const frameId = requestAnimationFrame(() => loadPreviewIframe());
    return () => cancelAnimationFrame(frameId);
  }, [loadPreviewIframe, previewRefreshKey, pagesNavSignature, widgetInstances, products, liveServices, widgetConfigs, website?.logo_url, brandName]);

  const resolveEditableElement = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    const editorId = selectedEditorIdRef.current;
    if (!doc || !editorId) return null;
    return doc.querySelector(`[data-editor-id="${editorId}"]`);
  }, []);

  const applyEditableStyle = useCallback((el, prop, value) => {
    if (!el) return;
    if (prop === 'font-family') {
      const formatted = formatToolbarFontFamily(value);
      el.setAttribute('data-custom-font-family', 'true');
      el.style.setProperty('--editor-custom-font-family', formatted);
      el.style.setProperty('font-family', formatted, 'important');
      return;
    }
    if (prop === 'font-size') {
      el.setAttribute('data-custom-font-size', 'true');
      el.style.setProperty('--editor-custom-font-size', value);
      el.style.setProperty('font-size', value, 'important');
      return;
    }
    if (prop === 'color') {
      el.setAttribute('data-custom-color', 'true');
      // Override template logo effects (gradient-text, stroke outlines) so the
      // picker color is what actually shows and what gets saved.
      el.classList.remove('gradient-text');
      el.style.setProperty('color', value, 'important');
      el.style.setProperty('-webkit-text-fill-color', value, 'important');
      el.style.setProperty('-webkit-text-stroke', '0px transparent', 'important');
      el.style.setProperty('text-shadow', 'none', 'important');
      el.style.setProperty('background', 'none', 'important');
      el.style.setProperty('background-image', 'none', 'important');
      el.style.setProperty('-webkit-background-clip', 'border-box', 'important');
      el.style.setProperty('background-clip', 'border-box', 'important');
      return;
    }
    el.style.setProperty(prop, value, 'important');
  }, []);

  const ensurePreviewFontLoaded = useCallback((fontName) => {
    const doc = iframeRef.current?.contentDocument;
    const trimmed = String(fontName || '').trim();
    if (!doc || !trimmed) return;
    const slug = trimmed.replace(/\s+/g, '-').toLowerCase();
    const id = `editor-preview-font-${slug}`;
    if (doc.getElementById(id)) return;
    const link = doc.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(trimmed).replace(/%20/g, '+')}:wght@400;600;700&display=swap`;
    doc.head.appendChild(link);
  }, []);

  // Message handler
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'contentChanged') {
        updateHtmlFromIframe();
      } else if (event.data.type === 'logoClick') {
        // Logo is edited via brand card / AI Logo — not the Replace Image modal
        setShowImageModal(false);
        setEditingImage(null);
        setActivePanel('pages');
        setShowBrandPopover(true);
        toast.info('Use AI Logo in the brand card (left) to change your logo');
      } else if (event.data.type === 'imageClick') {
        // Extra guard: never open Replace Image for brand logos
        const src = String(event.data.src || event.data.srcAttr || '');
        const logoUrl = String(website?.logo_url || '');
        const looksLikeLogo = Boolean(
          logoUrl && (src === logoUrl || src.includes(logoUrl) || logoUrl.includes(src.split('?')[0]))
        ) || /ui-avatars\.com\/api\/\?name=/i.test(src);
        if (looksLikeLogo) {
          setShowImageModal(false);
          setActivePanel('pages');
          setShowBrandPopover(true);
          toast.info('Use AI Logo in the brand card (left) to change your logo');
          return;
        }
        setEditingImage(event.data);
        setImageUrl(event.data.src);
        setShowImageModal(true);
      } else if (event.data.type === 'addToCart') {
        toast.success('Product added to cart! 🛒');
      } else if (event.data.type === 'goToCheckout') {
        const checkoutPage = pages.find((p) => p.slug === 'checkout');
        if (checkoutPage) switchPage(checkoutPage);
        else navigate(`/store/${websiteId}/checkout`);
      } else if (event.data.type === 'checkoutNotice') {
        const msg = event.data.message || 'Checkout update';
        if (msg.toLowerCase().includes('successful')) toast.success(msg);
        else toast.info(msg);
      } else if (event.data.type === 'iframeHeight') {
        if (event.data.height && event.data.height > 400) {
          setIframeHeight(Math.min(event.data.height + 40, 15000));
        }
      } else if (event.data.type === 'textElementSelected') {
        if (!iframeRef.current) return;
        const doc = iframeRef.current.contentDocument;
        if (!doc) return;
        const el = doc.querySelector(`[data-editor-id="${event.data.editorId}"]`);
        if (!el) return;
        selectedEditorIdRef.current = event.data.editorId || '';
        setSelectedEditorId(event.data.editorId || '');
        setSelectedTextElement(el);
        const tag = String(event.data.tagName || el.tagName || '').toUpperCase();
        const isButtonLike = tag === 'A' || tag === 'BUTTON';
        setToolbarIsButton(isButtonLike);
        if (isButtonLike) {
          const href = event.data.href || (tag === 'A' ? el.getAttribute('href') || '' : '');
          setToolbarLinkUrl(href);
          setToolbarPageSlug(slugFromPageHref(href));
          const doc = iframeRef.current?.contentDocument;
          const bg = doc?.defaultView?.getComputedStyle(el)?.backgroundColor || event.data.styles?.backgroundColor || '#8b5cf6';
          setToolbarBgColor(cssColorToHex(bg));
        }
        setToolbarColor(cssColorToHex(event.data.styles?.color));
        setToolbarFontSize(normalizeToolbarFontSize(event.data.styles?.fontSize));
        setToolbarFontFamily(matchToolbarFontFamily(event.data.styles?.fontFamily));

        const { rect } = event.data;
        if (!iframeRef.current) return;
        const iframeRect = iframeRef.current.getBoundingClientRect();
        // Adjust coordinates to account for iframe position within the window
        const adjustedTop = iframeRect.top + rect.top - 48;
        const adjustedLeft = iframeRect.left + rect.left + rect.width / 2;
        // Keep toolbar within viewport bounds
        const top = Math.max(Math.min(adjustedTop, window.innerHeight - 100), 16);
        const left = Math.max(Math.min(adjustedLeft, window.innerWidth - 200), 80);
        setToolbarPosition({
          visible: true,
          top,
          left,
        });
      } else if (event.data.type === 'brandSettingsOpen') {
        setBrandDraftName(brandName);
        setShowBrandPopover(true);
        setActivePanel('pages');
      } else if (event.data.type === 'switchEditorPage') {
        const slug = event.data.slug;
        const target = pages.find((p) => p.slug === slug);
        if (target) {
          switchPage(target);
          return;
        }
        if (slug === 'checkout' || slug === 'order-confirmation' || slug === 'cart') {
          fetch(`${API_URL}/api/websites/${websiteId}/pages`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then((r) => (r.ok ? r.json() : []))
            .then((freshPages) => {
              if (!Array.isArray(freshPages) || !freshPages.length) {
                toast.error('Checkout page is missing. Refresh the editor and try again.');
                iframeRef.current?.contentWindow?.postMessage({ type: 'snagcartOpenQuickCheckout' }, '*');
                return;
              }
              setPages(freshPages);
              const resolved = freshPages.find((p) => p.slug === slug);
              if (resolved) {
                switchPage(resolved);
                toast.success(slug === 'checkout' ? 'Opening checkout…' : `Opening ${resolved.name}…`);
              } else {
                toast.error(`"${slug}" page not found on this store.`);
                if (slug === 'checkout') {
                  iframeRef.current?.contentWindow?.postMessage({ type: 'snagcartOpenQuickCheckout' }, '*');
                }
              }
            })
            .catch(() => {
              toast.error('Could not open checkout. Try refreshing the editor.');
            });
          return;
        }
        toast.error(`Page "${slug}" not found`);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [brandName, navigate, websiteId, pages, switchPage, API_URL, token, website?.logo_url]);

  const updateHtmlFromIframe = () => {
    if (iframeRef.current) {
      try {
        syncToRef();
        if (htmlHistoryTimerRef.current) clearTimeout(htmlHistoryTimerRef.current);
        htmlHistoryTimerRef.current = setTimeout(() => {
          saveToHistory(htmlContentRef.current, cssContent);
        }, 400);
      } catch (e) { console.error(e); }
    }
  };

  // Sync toolbar changes to ref WITHOUT reloading iframe (keeps selectedTextElement alive)
  const syncToRef = useCallback(() => {
    if (!iframeRef.current) return;
    try {
      htmlContentRef.current = getCurrentPageHtml();
    } catch (e) { console.error(e); }
  }, [getCurrentPageHtml]);

  const runToolbarEdit = useCallback((mutator) => {
    const el = resolveEditableElement();
    if (!el) {
      toast.error('Click the text again to keep editing');
      return;
    }
    mutator(el);
    setSelectedTextElement(el);

    const href = el.tagName === 'A' ? (el.getAttribute('href') || '') : '';
    if (href) {
      const doc = el.ownerDocument;
      const slugMatch = href.match(/[?&]page=([^&]+)/);
      if (slugMatch && doc) {
        el.setAttribute('data-editor-page', slugMatch[1]);
        el.dataset.editorNavBound = 'false';
        if (typeof doc.defaultView?.initEditorPageNav === 'function') {
          doc.defaultView.initEditorPageNav();
        }
      }
      const section = el.closest('[data-section-id]');
      const instanceId = section?.getAttribute('data-widget-instance') || section?.getAttribute('data-section-id');
      const widgetType = section?.getAttribute('data-widget-type')
        || (section ? inferWidgetTypeFromSectionHtml(section.outerHTML) : '');
      if (instanceId && widgetType && isConfigWidget(widgetType)) {
        patchWidgetInstanceConfig(
          API_URL,
          token,
          websiteId,
          instanceId,
          { buttonUrl: href, url: href },
          widgetInstances,
        ).then((item) => {
          if (item) {
            setWidgetInstances((prev) => prev.map((row) => (row.id === item.id ? item : row)));
          }
        }).catch(() => {});
      }
    }

    syncToRef();
  }, [resolveEditableElement, syncToRef, API_URL, token, websiteId, widgetInstances]);

  // Save website
  const handleSave = async () => {
    setSaving(true);
    const latestHtml = getCurrentPageHtml();
    commitPageHtml(latestHtml);
    try {
      await saveCurrentPage();
      const patchedCss = patchThemeTokensInCss(cssContent, primaryColor, secondaryColor, fontFamily);
      if (patchedCss !== cssContent) setCssContent(patchedCss);
      // Note: saveCurrentPage already syncs html_code to website
      // Update additional website settings (PUT only updates non-null fields)
      await fetch(`${API_URL}/api/websites/${websiteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
          seo_title: seoTitle, 
          seo_description: seoDescription,
          primary_color: primaryColor,
          secondary_color: secondaryColor,
          font_family: fontFamily,
          css_code: patchedCss,
        })
      });
      syncWidgetInstancesFromPages(API_URL, token, websiteId).then((items) => {
        if (items?.length) setWidgetInstances(items);
      });
      toast.success('Saved!');
    } catch (error) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  // Publish website
  const openPublishModal = async () => {
    const defaultSlug = website?.slug || slugifyWebsiteName(website?.name) || '';
    setCustomSubdomain(defaultSlug);
    setCustomDomain(website?.custom_domain || customDomain || '');
    setShowPublishModal(true);
    setDomainsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/my/domains`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setAvailableDomains(await response.json());
      }
    } catch (error) {
      console.error('Failed to load domains', error);
    } finally {
      setDomainsLoading(false);
    }
  };

  const websiteDomains = useMemo(() => {
    const rows = Array.isArray(availableDomains) ? availableDomains : [];
    return rows.filter((row) => {
      const attachedId = row.website_id || row.attached_website_id || '';
      return !attachedId || attachedId === websiteId;
    });
  }, [availableDomains, websiteId]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await handleSave();
      const publishData = {
        seo_title: seoTitle,
        seo_description: seoDescription,
        slug: customSubdomain || undefined,
        custom_domain: customDomain || undefined
      };
      
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(publishData)
      });
      const data = await response.json();
      if (response.ok) {
        setWebsite(prev => ({ ...prev, ...data.website }));
        setCustomSubdomain(data.website?.slug || '');
        const liveUrl = data.slug_url || data.public_url || (data.website?.slug ? `${API_URL}/api/sites/${data.website.slug}/html` : '');
        const parts = ['Published!'];
        if (liveUrl) parts.push(`Live: ${liveUrl}`);
        if (data.custom_domain_url) parts.push(`Domain: ${data.custom_domain_url} (${data.domain_status || 'pending'})`);
        toast.success(parts.join(' · '));
        setShowPublishModal(false);
        if (liveUrl) window.open(liveUrl.startsWith('http') ? liveUrl : `${API_URL}${liveUrl}`, '_blank');
      } else {
        toast.error(data.detail || 'Publish failed');
      }
    } catch (error) { toast.error('Publish failed'); }
    finally { setPublishing(false); }
  };

  // Image update — patch live iframe first, then sync stored HTML + widget instance config
  const handleImageUpdate = async (newUrl) => {
    const finalUrl = resolveMediaUrl(newUrl || imageUrl, API_URL);
    if (!editingImage) {
      toast.error('Click an image in the preview first');
      return;
    }
    if (!finalUrl) {
      toast.error('Choose or upload an image first');
      return;
    }

    const matchSrc = editingImage.srcAttr || editingImage.src;
    let changed = false;
    const liveDoc = iframeRef.current?.contentDocument;

    const applyToLiveTarget = (target) => {
      if (!target) return false;
      if (editingImage.mode === 'background') {
        const style = target.getAttribute('style') || '';
        const nextStyle = style.includes('url(')
          ? style.replace(/url\(['"]?[^'")]+['"]?\)/gi, `url('${finalUrl}')`)
          : `${style}${style.trim().endsWith(';') || !style ? '' : ';'}background-image:url('${finalUrl}');background-size:cover;background-position:center;`;
        target.setAttribute('style', nextStyle);
        return true;
      }
      if (target.tagName === 'IMG') {
        target.setAttribute('src', finalUrl);
        target.referrerPolicy = 'no-referrer';
        return true;
      }
      return false;
    };

    if (liveDoc && editingImage.imageEditId) {
      changed = applyToLiveTarget(liveDoc.querySelector(`[data-image-edit-id="${editingImage.imageEditId}"]`));
    }

    if (!changed && liveDoc) {
      if (editingImage.mode === 'background') {
        liveDoc.querySelectorAll('[style*="background"], [style*="url("]').forEach((el) => {
          const style = el.getAttribute('style') || '';
          if (imageUrlMatches(style, matchSrc) || imageUrlMatches(style, editingImage.src)) {
            changed = applyToLiveTarget(el) || changed;
          }
        });
      } else {
        liveDoc.querySelectorAll('img').forEach((img) => {
          const attr = img.getAttribute('src') || '';
          if (imageUrlMatches(attr, matchSrc) || imageUrlMatches(img.src, editingImage.src)) {
            img.setAttribute('src', finalUrl);
            img.referrerPolicy = 'no-referrer';
            changed = true;
          }
        });
      }
    }

    if (!changed) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${htmlContentRef.current || htmlContent}</div>`, 'text/html');
      if (editingImage.mode === 'background') {
        doc.querySelectorAll('[style]').forEach((el) => {
          const style = el.getAttribute('style') || '';
          if (imageUrlMatches(style, matchSrc) || imageUrlMatches(style, editingImage.src)) {
            const nextStyle = style.replace(/url\(['"]?[^'")]+['"]?\)/gi, `url('${finalUrl}')`);
            el.setAttribute('style', nextStyle);
            changed = true;
          }
        });
      } else {
        doc.querySelectorAll('img').forEach((img) => {
          const attr = img.getAttribute('src') || '';
          if (imageUrlMatches(attr, matchSrc) || imageUrlMatches(img.src, editingImage.src)) {
            img.setAttribute('src', finalUrl);
            changed = true;
          }
        });
      }
      if (changed) {
        const parsedHtml = repairBrokenUnsplashUrls(doc.body.firstChild.innerHTML);
        htmlContentRef.current = parsedHtml;
      }
    }

    if (!changed) {
      toast.error('Could not find that image in the page HTML');
      return;
    }

    const baseHtml = htmlContentRef.current || htmlContent;
    const { sections: currentSections, envelope } = resolveSectionsForEdit(baseHtml, sections, htmlEnvelopeRef);
    htmlEnvelopeRef.current = envelope;

    let sectionIndex = -1;
    let nextSectionHtml = null;

    if (liveDoc && editingImage.imageEditId) {
      const target = liveDoc.querySelector(`[data-image-edit-id="${editingImage.imageEditId}"]`);
      const sectionEl = target?.closest('[data-section-id]');
      const sectionId = sectionEl?.getAttribute('data-section-id');
      if (sectionId) {
        sectionIndex = currentSections.findIndex((s) => s.id === sectionId);
        if (sectionIndex !== -1 && sectionEl) {
          nextSectionHtml = cleanSectionHtmlForStorage(sectionEl.outerHTML, widgetInstances);
        }
      }
    }

    let newSections;
    if (sectionIndex !== -1 && nextSectionHtml) {
      newSections = [...currentSections];
      newSections[sectionIndex] = { ...newSections[sectionIndex], html: nextSectionHtml };
    } else {
      let replacedOne = false;
      newSections = currentSections.map((section) => {
        if (replacedOne) return section;
        const hasMatch = editingImage.mode === 'background'
          ? /url\(['"]?[^'")]+['"]?\)/i.test(section.html)
            && (imageUrlMatches(section.html, matchSrc) || imageUrlMatches(section.html, editingImage.src))
          : imageUrlMatches(section.html, matchSrc) || imageUrlMatches(section.html, editingImage.src);
        if (!hasMatch) return section;
        replacedOne = true;
        const patched = applyImageToSectionHtml(section.html, finalUrl, {
          matchSrc,
          imageEditId: editingImage.imageEditId || '',
          mode: editingImage.mode || 'img',
        });
        return {
          ...section,
          html: patched.html,
        };
      });
    }

    const updatedSection = sectionIndex !== -1 ? newSections[sectionIndex] : newSections.find((section) => {
      if (editingImage.mode === 'background') {
        return /url\(['"]?[^'")]+['"]?\)/i.test(section.html)
          && (imageUrlMatches(section.html, finalUrl) || imageUrlMatches(section.html, matchSrc));
      }
      return imageUrlMatches(section.html, finalUrl);
    });
    const instanceId = extractInstanceIdFromHtml(updatedSection?.html || '')
      || editingImage.sectionId
      || (liveDoc && editingImage.imageEditId
        ? liveDoc.querySelector(`[data-image-edit-id="${editingImage.imageEditId}"]`)?.closest('[data-section-id]')?.getAttribute('data-section-id')
        : null);
    const widgetRow = instanceId ? widgetInstances.find((row) => row.instance_id === instanceId) : null;
    const widgetType = (
      widgetRow?.widget_type
      || inferWidgetTypeFromSectionHtml(updatedSection?.html || '')
      || (instanceId?.startsWith('gallery-') ? 'gallery' : '')
      || (instanceId?.startsWith('slider-') ? 'slider' : '')
      || ''
    ).toLowerCase();
    const liveTarget = liveDoc && editingImage.imageEditId
      ? liveDoc.querySelector(`[data-image-edit-id="${editingImage.imageEditId}"]`)
      : null;
    const isImageWidget = instanceId && widgetType === 'image';

    let configPatch = null;
    if (instanceId && widgetType === 'gallery') {
      const cfg = parseWidgetInstanceConfig(widgetRow?.config_json);
      const sectionEl = liveTarget?.closest('[data-section-id]');
      const bootstrapItems = sectionEl && liveDoc ? extractGalleryItemsFromDom(sectionEl) : [];
      let items = Array.isArray(cfg.items) && cfg.items.length ? cfg.items : bootstrapItems;
      const idx = findGalleryItemIndex(items, matchSrc, liveTarget?.getAttribute('data-gallery-index') || editingImage.galleryIndex);
      items = items.map((item) => ({ ...item }));
      if (!items.length) items.push({ image: finalUrl, title: '', category: '' });
      items[idx] = { ...(items[idx] || {}), image: finalUrl };
      configPatch = { items };
    } else if (instanceId && widgetType === 'slider') {
      const cfg = parseWidgetInstanceConfig(widgetRow?.config_json);
      const idx = findSliderImageIndex(cfg.images, matchSrc, liveTarget?.getAttribute('data-slide-index') || editingImage.slideIndex);
      configPatch = patchSliderSlideImage(cfg, idx, finalUrl);
    }

    if (configPatch && instanceId) {
      const pageSlug = pages.find((p) => p.id === currentPage)?.slug || '';
      const registerPayload = {
        instance_id: instanceId,
        widget_type: widgetType,
        widget_name: updatedSection?.name || widgetType,
        page_slug: pageSlug,
      };
      const item = await patchWidgetInstanceConfig(
        API_URL, token, websiteId, instanceId, configPatch, widgetInstances, registerPayload,
      );
      if (item) {
        setWidgetInstances((prev) => {
          const nextRow = { ...item, config_json: item.config_json || JSON.stringify(configPatch) };
          return [nextRow, ...prev.filter((row) => row.instance_id !== instanceId)];
        });
      } else {
        setWidgetInstances((prev) => prev.map((row) => {
          if (row.instance_id !== instanceId) return row;
          const merged = { ...parseWidgetInstanceConfig(row.config_json), ...configPatch };
          return { ...row, config_json: JSON.stringify(merged) };
        }));
      }
    } else if (isImageWidget && editingImage.mode !== 'background') {
      const patch = { src: finalUrl };
      try {
        const doc = new DOMParser().parseFromString(updatedSection.html, 'text/html');
        const alt = doc.querySelector('img')?.getAttribute('alt');
        if (alt) patch.alt = alt;
      } catch { /* ignore */ }
      const item = await patchWidgetInstanceConfig(API_URL, token, websiteId, instanceId, patch, widgetInstances);
      setWidgetInstances((prev) => prev.map((row) => {
        if (row.instance_id !== instanceId) return row;
        const cfgJson = item?.config_json || JSON.stringify({ ...parseWidgetInstanceConfig(row.config_json), ...patch });
        return { ...row, config_json: cfgJson };
      }));
    }

    commitPageHtml(composeSectionHtml(newSections), { saveHistory: true, refreshPreview: true });
    setShowImageModal(false);
    setShowBannerModal(false);
    setImageFile(null);
    setEditingImage(null);
    setImageUrl('');
    try {
      await saveCurrentPage();
      toast.success('Image updated and saved');
    } catch {
      toast.success('Image updated — click Save if it does not persist');
    }
  };
  handleImageUpdateRef.current = handleImageUpdate;

  // Group pages by category — for e-commerce, mirror header nav in the left panel
  const isEcommerceSite = editorSurfaces?.genre_id === 'ecommerce'
    || website?.category === 'ecommerce'
    || website?.genre_id === 'ecommerce';

  const linkablePages = useMemo(() => {
    const hidden = new Set(['admin-dashboard']);
    return (pages || [])
      .filter((p) => p?.slug && !hidden.has(p.slug))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [pages]);

  const sortablePages = useMemo(
    () => linkablePages.filter((p) => !ECOMMERCE_UTILITY_PAGE_SLUGS.has(p.slug)),
    [linkablePages],
  );

  const handlePageDragEnd = async (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;
    const page = sortablePages[sourceIndex];
    if (!page?.id) return;
    try {
      const response = await fetch(
        `${API_URL}/api/websites/${websiteId}/pages/${page.id}/reorder?new_order=${destIndex}`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) {
        toast.error('Could not reorder pages');
        return;
      }
      const updated = await response.json();
      setPages(repairPagesHeaderNav(Array.isArray(updated) ? updated : pages));
      toast.success('Page order updated');
    } catch {
      toast.error('Could not reorder pages');
    }
  };

  const pagesByCategory = useMemo(() => {
    if (!isEcommerceSite) {
      return PAGE_CATEGORIES.map((cat) => ({
        ...cat,
        pages: pages.filter((p) => {
          const normalizedCategory = (p.category || (['sign-in', 'my-account', 'account'].includes(p.slug) ? 'account' : 'main'));
          if (normalizedCategory !== cat.id && !(cat.id === 'account' && ['sign-in', 'my-account', 'account'].includes(p.slug))) return false;
          if (cat.id === 'main' && ECOMMERCE_UTILITY_PAGE_SLUGS.has(p.slug)) return false;
          return true;
        }),
      })).filter((cat) => cat.pages.length > 0);
    }

    const navigationPages = ECOMMERCE_NAV_ITEMS.map(({ slug, label, fallbacks }) => {
      const page = resolvePageBySlugs(pages, [slug, ...fallbacks]);
      if (slug === 'sign-in' && page?.nav_visible === false) return null;
      return page ? { ...page, name: label, _navSlug: slug } : null;
    }).filter(Boolean);

    const navigation = {
      id: 'navigation',
      name: 'Navigation',
      icon: Navigation,
      color: 'text-emerald-400',
      pages: navigationPages,
    };

    const storeFlows = {
      id: 'store-flows',
      name: 'Checkout & product',
      icon: ShoppingCart,
      color: 'text-cyan-400',
      pages: pages
        .filter((p) => ECOMMERCE_STORE_FLOW_SLUGS.has(p.slug))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    };

    const navSlugs = new Set(
      ECOMMERCE_NAV_ITEMS.flatMap(({ slug, fallbacks }) => [slug, ...(fallbacks || [])]),
    );
    const contentPages = pages
      .filter((p) => {
        if (!p?.slug || ECOMMERCE_UTILITY_PAGE_SLUGS.has(p.slug)) return false;
        if (navSlugs.has(p.slug) || ECOMMERCE_STORE_FLOW_SLUGS.has(p.slug)) return false;
        const cat = p.category || 'main';
        return ['main', 'blog'].includes(cat);
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const content = contentPages.length
      ? {
          id: 'content',
          name: 'Content pages',
          icon: Newspaper,
          color: 'text-sky-400',
          pages: contentPages,
        }
      : null;

    const secondary = PAGE_CATEGORIES
      .filter((cat) => ['admin', 'legal', 'system', 'account'].includes(cat.id))
      .map((cat) => ({
        ...cat,
        pages: pages.filter(
          (p) => (p.category || 'main') === cat.id
            || (cat.id === 'admin' && p.slug === 'admin-dashboard')
            || (cat.id === 'account' && ['sign-in', 'my-account', 'account'].includes(p.slug)),
        ),
      }))
      .filter((cat) => cat.pages.length > 0);

    return [navigation, content, storeFlows, ...secondary].filter((cat) => cat && cat.pages.length > 0);
  }, [pages, isEcommerceSite]);

  const operatorDashboardPage = pages.find(p => p.slug === 'admin-dashboard');

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => 
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  // Get unique banner categories
  const bannerCategories = ['all', ...new Set(BANNER_IMAGES.map(b => b.category))];

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-zinc-950 flex flex-col overflow-hidden" data-testid="template-editor-page">
      {/* Toolbar */}
      <div className="bg-zinc-900 border-b border-white/10 px-3 py-2 flex items-center justify-between flex-shrink-0 min-w-0">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <div className="h-6 w-px bg-zinc-700" />
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <img src={website?.logo_url || brandLogoFallback} alt={brandName} className="w-7 h-7 rounded-lg object-cover" />
              <h1 className="text-sm font-semibold text-white">{brandName}</h1>
            </div>
            {currentPage && <span className="text-sm text-zinc-400">/ {currentPage.name}</span>}
          </div>
          {/* Brand settings moved to left sidebar */}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button onClick={handleUndo} disabled={historyIndex <= 0} variant="ghost" size="sm" className="text-zinc-400 px-2"><Undo className="w-4 h-4" /></Button>
          <Button onClick={handleRedo} disabled={historyIndex >= history.length - 1} variant="ghost" size="sm" className="text-zinc-400 px-2"><Redo className="w-4 h-4" /></Button>
          <div className="h-5 w-px bg-zinc-700 mx-1" />
          <div className="flex bg-zinc-800 rounded-lg p-0.5">
            {['desktop', 'tablet', 'mobile'].map(mode => (
              <button key={mode} onClick={() => setPreviewMode(mode)} className={`p-1.5 rounded ${previewMode === mode ? 'bg-violet-500 text-white' : 'text-zinc-400'}`} title={mode}>
                {mode === 'desktop' && <Monitor className="w-3.5 h-3.5" />}
                {mode === 'tablet' && <Tablet className="w-3.5 h-3.5" />}
                {mode === 'mobile' && <Smartphone className="w-3.5 h-3.5" />}
              </button>
            ))}
          </div>
          <div className="h-5 w-px bg-zinc-700 mx-1" />
          {isPanelVisible(editorSurfaces, 'design') && (
            <Button onClick={() => setShowDesignPanel(true)} variant="ghost" size="sm" className="text-zinc-400 px-2 flex items-center gap-1.5" title="Design — pages, colors, and brand"><Palette className="w-4 h-4" /><span className="hidden lg:inline text-xs">Design</span></Button>
          )}
          {isPanelVisible(editorSurfaces, 'settings') && (
            <Button onClick={() => navigate(`/edit/${websiteId}/settings`)} variant="ghost" size="sm" className="text-zinc-400 px-2 flex items-center gap-1.5" title="Settings — SEO, domain, integrations"><Settings className="w-4 h-4" /><span className="hidden lg:inline text-xs">Settings</span></Button>
          )}
          {isPanelVisible(editorSurfaces, 'store') && (
            <Button onClick={() => navigate(`/ecommerce/${websiteId}`)} variant="ghost" size="sm" className="text-zinc-400 px-2 flex items-center gap-1.5" title="Store — synced with Operate products & orders"><ShoppingCart className="w-4 h-4" /><span className="hidden lg:inline text-xs">Store</span></Button>
          )}
          {isPanelVisible(editorSurfaces, 'booking') && (
            <Button onClick={() => navigate(`/booking/${websiteId}`)} variant="ghost" size="sm" className="text-zinc-400 px-2 flex items-center gap-1.5" title="Booking — synced with Operate bookings"><Calendar className="w-4 h-4" /><span className="hidden lg:inline text-xs">Booking</span></Button>
          )}
          {isPanelVisible(editorSurfaces, 'crm') && (
            <Button
              onClick={() => navigate(crmUrl(websiteId))}
              variant="ghost"
              size="sm"
              className="text-sky-400 px-2 flex items-center gap-1.5"
              title="CRM — Marketing Suite leads from website forms"
            >
              <UserPlus className="w-4 h-4" /><span className="hidden lg:inline text-xs">CRM</span>
            </Button>
          )}
          {isPanelVisible(editorSurfaces, 'operate') && (
            <Button
              onClick={() => navigate(operateUrl(websiteId, editorSurfaces?.panels?.find((p) => p.id === 'operate')?.operate_section))}
              variant="ghost"
              size="sm"
              className="text-rose-400 px-2 flex items-center gap-1.5"
              title={`Operate — ${editorSurfaces?.genre_label || 'genre'} admin cockpit`}
            >
              <LayoutDashboard className="w-4 h-4" /><span className="hidden lg:inline text-xs">Operate</span>
            </Button>
          )}
          <Button onClick={() => navigate('/marketing-suite')} variant="ghost" size="sm" className="text-pink-400 px-2 flex items-center gap-1.5" title="Marketing Suite">
            <Mail className="w-4 h-4" /><span className="hidden lg:inline text-xs">Marketing Suite</span>
          </Button>
          <Button onClick={() => navigate(`/ai-tools/${websiteId}`)} variant="ghost" size="sm" className="text-violet-400 px-2 flex items-center gap-1.5" title="AI Tools"><Sparkles className="w-4 h-4" /><span className="hidden lg:inline text-xs">AI Tools</span></Button>
          <div className="h-5 w-px bg-zinc-700 mx-1" />
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-zinc-700 text-xs px-3"><Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving...' : 'Save'}</Button>
          <Button onClick={openPublishModal} size="sm" className="bg-violet-500 text-xs px-3"><Rocket className="w-3.5 h-3.5 mr-1" />Publish</Button>
          {website?.is_published && (
            <Button onClick={async () => { await handleSave(); window.open(`${API_URL}/api/sites/${website.slug}/html`, '_blank'); }} size="sm" variant="outline" className="border-emerald-500 text-emerald-400 text-xs px-2" title="View Live"><ExternalLink className="w-3.5 h-3.5" /></Button>
          )}
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left Panel */}
        <div className="w-64 bg-zinc-900 border-r border-white/10 flex flex-col flex-shrink-0 overflow-hidden">
          {/* Sidebar Tab Rail — icon + label, no overflow */}
          <div className="flex flex-col border-b border-white/10">
            {[
              { id: 'pages',     label: 'Pages',     Icon: FolderOpen },
              { id: 'widgets',   label: 'Widgets',   Icon: Layout },
              { id: 'sections',  label: 'Sections',  Icon: Layers },
              ...(isPanelVisible(editorSurfaces, 'sitebrain') ? [{ id: 'sitebrain', label: 'SiteBrain', Icon: Sparkles }] : []),
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActivePanel(id)}
                className={`flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium transition-colors border-l-2 ${
                  activePanel === id
                    ? 'text-white border-violet-500 bg-violet-500/10'
                    : 'text-zinc-400 border-transparent hover:text-zinc-200 hover:bg-white/5'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${activePanel === id ? 'text-violet-400' : ''}`} />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">
            {activePanel === 'pages' ? (
              <div className="p-2">
                {/* Brand identity card */}
                <div className="mb-3 rounded-xl border border-white/10 bg-zinc-800/60 p-3">
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        setBrandDraftName(brandName);
                        setShowBrandPopover(true);
                      }}
                      disabled={logoGenerating}
                      className="relative flex-shrink-0 group"
                      title={logoGenerating ? 'Generating...' : 'Open brand settings'}
                      aria-label="Open brand settings"
                    >
                      <img src={website?.logo_url || brandLogoFallback} alt={brandName} className="w-10 h-10 rounded-xl object-cover border border-white/10 group-hover:ring-2 group-hover:ring-violet-500/50 transition-all" />
                      {logoGenerating && <div className="absolute inset-0 rounded-xl bg-black/40 flex items-center justify-center"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>}
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Sparkles className="w-2.5 h-2.5 text-white" />
                      </div>
                    </button>
                    <div className="flex-1 min-w-0">
                      {showBrandPopover ? (
                        <input
                          value={brandDraftName}
                          onChange={(e) => setBrandDraftName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { handleSaveBrandSettings(); } if (e.key === 'Escape') setShowBrandPopover(false); }}
                          className="w-full rounded-lg border border-violet-500/50 bg-zinc-900 px-2 py-1 text-sm text-white outline-none focus:border-violet-500"
                          placeholder="Brand name"
                          autoFocus
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setBrandDraftName(brandName); setShowBrandPopover(true); }}
                          className="w-full text-left group"
                        >
                          <p className="text-sm font-semibold text-white truncate group-hover:text-violet-300 transition-colors">{brandName}</p>
                          <p className="text-[10px] text-zinc-500">Click to edit name</p>
                        </button>
                      )}
                    </div>
                  </div>
                  {showBrandPopover && (
                    <div className="flex gap-1.5 mt-1">
                      <Button onClick={handleSaveBrandSettings} disabled={brandSaving} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white text-[10px] px-2 py-1 h-6">
                        {brandSaving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button onClick={handleGenerateLogo} disabled={logoGenerating} size="sm" variant="outline" className="border-zinc-700 text-zinc-300 text-[10px] px-2 py-1 h-6">
                        <Sparkles className="w-2.5 h-2.5 mr-1" />AI Logo
                      </Button>
                      {website?.logo_url && (
                        <Button onClick={handleRemoveLogo} disabled={brandSaving} size="sm" variant="outline" className="border-zinc-700 text-zinc-300 text-[10px] px-2 py-1 h-6">
                          Remove
                        </Button>
                      )}
                      <Button onClick={() => setShowBrandPopover(false)} size="sm" variant="ghost" className="text-zinc-500 text-[10px] px-1 py-1 h-6 ml-auto">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {isPanelVisible(editorSurfaces, 'operate') && (
                  <div className="px-2 pb-2 space-y-1">
                    <Button
                      onClick={() => navigate(operateUrl(websiteId))}
                      size="sm"
                      className="w-full bg-rose-600/90 hover:bg-rose-500 text-white text-xs justify-start"
                    >
                      <LayoutDashboard className="w-3.5 h-3.5 mr-2" />
                      Open operator dashboard
                    </Button>
                    {(editorSurfaces?.genre_id === 'ecommerce' || website?.category === 'ecommerce' || isPanelVisible(editorSurfaces, 'operate')) && (
                      <>
                        <Button
                          onClick={() => navigate(operateUrl(websiteId, 'currency', 'commerce'))}
                          size="sm"
                          variant="outline"
                          className="w-full border-zinc-700 text-zinc-300 text-xs justify-start hover:border-violet-500 hover:text-violet-300"
                        >
                          <Globe className="w-3.5 h-3.5 mr-2" />
                          Commerce+ settings
                        </Button>
                        <Button
                          onClick={() => navigate(operateUrl(websiteId, 'delivery', 'commerce'))}
                          size="sm"
                          variant="outline"
                          className="w-full border-zinc-700 text-zinc-300 text-xs justify-start hover:border-cyan-600 hover:text-cyan-300"
                        >
                          <Navigation className="w-3.5 h-3.5 mr-2" />
                          Delivery &amp; routes
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {sortablePages.length > 1 && (
                  <div className="mb-3 px-2">
                    <p className="px-1 mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Page order</p>
                    <DragDropContext onDragEnd={handlePageDragEnd}>
                      <Droppable droppableId="pages-order">
                        {(provided) => (
                          <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-1">
                            {sortablePages.map((page, index) => (
                              <Draggable key={page.id} draggableId={`page-order-${page.id}`} index={index}>
                                {(dragProvided, snapshot) => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    onClick={() => switchPage(page)}
                                    className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 cursor-pointer ${
                                      currentPage?.id === page.id
                                        ? 'border-violet-500/50 bg-violet-500/20'
                                        : snapshot.isDragging
                                          ? 'border-violet-500 bg-violet-500/10'
                                          : 'border-white/10 bg-zinc-800/60 hover:bg-zinc-800'
                                    }`}
                                  >
                                    <div
                                      {...dragProvided.dragHandleProps}
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex h-7 w-7 cursor-grab items-center justify-center rounded-md bg-zinc-900/70 text-zinc-400"
                                    >
                                      <GripVertical className="w-3.5 h-3.5" />
                                    </div>
                                    <span className="truncate text-xs text-zinc-200">{page.name}</span>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                )}

                {pagesByCategory.map(category => {
                  const Icon = category.icon;
                  const isExpanded = expandedCategories.includes(category.id);
                  return (
                    <div key={category.id} className="mb-2">
                      <button onClick={() => toggleCategory(category.id)} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-zinc-800 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${category.color}`} />
                          <span className="font-medium">{category.name}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {isExpanded && (
                        <div className="ml-2 space-y-1 mt-1">
                          {category.pages.map(page => {
                            const navActive = category.id === 'navigation'
                              && normalizeEcommerceNavSlug(currentPage?.slug) === (page._navSlug || page.slug);
                            const isActive = category.id === 'navigation'
                              ? navActive
                              : currentPage?.id === page.id;
                            const NavIcon = category.id === 'navigation'
                              ? (page._navSlug === 'home' ? Home
                                : page._navSlug === 'products' ? ShoppingBag
                                : page._navSlug === 'sign-in' ? User
                                : page._navSlug === 'cart' ? ShoppingCart
                                : FileText)
                              : (page.is_home ? Home : FileText);
                            return (
                            <div key={`${category.id}-${page._navSlug || page.slug}-${page.id}`} onClick={() => switchPage(page)} className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer group ${isActive ? 'bg-violet-500/20 border border-violet-500/50' : 'hover:bg-zinc-800'}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <NavIcon className={`w-4 h-4 flex-shrink-0 ${category.id === 'navigation' ? 'text-emerald-400' : page.is_home ? 'text-amber-400' : 'text-zinc-400'}`} />
                                <span className="truncate text-sm">{page.name}</span>
                                {category.id !== 'navigation' && page.nav_visible === false && <EyeOff className="w-3 h-3 text-zinc-500" />}
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                <button onClick={(e) => { e.stopPropagation(); openPageSettings(page); }} className="p-1 hover:bg-zinc-700 rounded"><Edit2 className="w-3 h-3 text-zinc-400" /></button>
                                <button onClick={(e) => { e.stopPropagation(); deletePage(page.id); }} className="p-1 hover:bg-red-500/20 rounded"><Trash2 className="w-3 h-3 text-zinc-400 hover:text-red-400" /></button>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="px-2 pt-2">
                  {!showAddPage && (
                    <Button
                      onClick={() => setShowAddPage(true)}
                      variant="outline"
                      size="sm"
                      className="w-full border-dashed border-zinc-700 text-zinc-300 hover:border-violet-500 hover:text-white"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Page
                    </Button>
                  )}
                </div>

                {showAddPage ? (
                  <div className="p-3 bg-zinc-800 rounded-lg space-y-3 mx-2 mt-3">
                    <input type="text" value={newPageName} onChange={(e) => setNewPageName(e.target.value)} placeholder="Page Name" className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded text-white text-sm" autoFocus />
                    <input type="text" value={newPageSlug} onChange={(e) => setNewPageSlug(e.target.value)} placeholder="page-url (optional)" className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded text-white text-sm" />
                    <select value={newPageCategory} onChange={(e) => setNewPageCategory(e.target.value)} className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded text-white text-sm">
                      {PAGE_CATEGORIES.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </select>
                    <div className="flex gap-2">
                      <Button onClick={() => setShowAddPage(false)} variant="outline" size="sm" className="flex-1 border-zinc-600">Cancel</Button>
                      <Button onClick={createPage} size="sm" className="flex-1 bg-violet-500">Create</Button>
                    </div>
                  </div>
                ) : null}

              </div>
            ) : activePanel === 'widgets' ? (
              <div className="p-3">
                <div className="flex flex-wrap gap-1 mb-4">
                  <button onClick={() => setWidgetFilter('all')} className={`px-3 py-1 rounded-full text-xs font-medium ${widgetFilter === 'all' ? 'bg-violet-500 text-white' : 'bg-zinc-800 text-zinc-400'}`}>All</button>
                  {WIDGET_CATEGORIES.map(cat => (
                    <button key={cat.id} onClick={() => setWidgetFilter(cat.id)} className={`px-3 py-1 rounded-full text-xs font-medium ${widgetFilter === cat.id ? 'bg-violet-500 text-white' : 'bg-zinc-800 text-zinc-400'}`}>{cat.icon} {cat.name}</button>
                  ))}
                </div>
                <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto">
                  {ALL_WIDGETS.filter(w => widgetFilter === 'all' || w.category === widgetFilter).map(widget => (
                    <button key={widget.id} onClick={() => addWidget(widget)} className="w-full p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 text-left transition-colors">
                      <span className="text-xl flex-shrink-0">{widget.icon}</span>
                      <div className="min-w-0">
                        <span className="font-medium block text-sm">{widget.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : activePanel === 'sections' ? (
              <div className="p-3">
                <button
                  onClick={() => setShowAddSectionModal(true)}
                  className="w-full mb-4 p-3 bg-violet-600 hover:bg-violet-500 rounded-lg flex items-center justify-center gap-2 text-white font-medium transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add Section
                </button>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="sections">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                        {sections.map((section, index) => (
                          <Draggable key={section.id} draggableId={section.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`rounded-xl border p-3.5 transition-all ${
                                  selectedSectionId === section.id
                                    ? 'border-violet-500/70 bg-gradient-to-br from-violet-500/18 to-cyan-500/10 shadow-[0_0_0_1px_rgba(139,92,246,0.2)]'
                                    : 'border-white/8 bg-zinc-800/85 hover:bg-zinc-800 hover:border-zinc-600/80'
                                } ${snapshot.isDragging ? 'ring-2 ring-violet-500 scale-[1.01]' : ''}`}
                                onClick={() => selectSectionById(section.id)}
                              >
                                <div className="flex items-start gap-3">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="mt-1 flex h-8 w-8 cursor-grab items-center justify-center rounded-lg bg-zinc-950/60 text-zinc-400 border border-white/10"
                                  >
                                    <GripVertical className="w-4 h-4" />
                                  </div>

                                  <div className="flex-1 min-w-0 space-y-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="min-w-0 flex-1">
                                        <input
                                          className="w-full rounded-md border border-transparent bg-zinc-950/50 px-2.5 py-1.5 text-sm font-semibold text-white outline-none transition focus:border-violet-500/60 focus:bg-zinc-950"
                                          value={section.name}
                                          onChange={(e) => updateSectionDisplayName(index, e.target.value)}
                                          onBlur={() => commitSectionRename(index)}
                                          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </div>
                                      <span className="shrink-0 rounded-full border border-white/10 bg-black/20 px-2 py-1 text-[10px] font-semibold tracking-wide text-zinc-400">
                                        {String(index + 1).padStart(2, '0')}
                                      </span>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${getSectionBadgeMeta(section).classes}`}>
                                        {getSectionBadgeMeta(section).label}
                                      </span>
                                      <span className="rounded-full border border-white/10 bg-zinc-950/40 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.12em] text-zinc-400">
                                        {section.type}
                                      </span>
                                      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-medium ${section.showInNav ? 'border-violet-400/20 bg-violet-500/15 text-violet-300' : 'border-white/10 bg-zinc-900/60 text-zinc-500'}`}>
                                        {section.showInNav ? 'In nav' : 'Hidden'}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-1.5 pt-1">
                                      <button onClick={() => toggleSectionNav(index)} className={`p-1.5 rounded-lg transition ${section.showInNav ? 'bg-violet-500/20 text-violet-300' : 'bg-zinc-900/60 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'}`} title={section.showInNav ? 'Visible in nav' : 'Hidden from nav'}>
                                        {section.showInNav ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                      </button>
                                      {getSectionConfigWidgetType(section) && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); openWidgetDashboardForSection(section); }}
                                          className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition"
                                          title="Configure in Widget Dashboard"
                                        >
                                          <Settings className="w-4 h-4" />
                                        </button>
                                      )}
                                      {getSectionEditType(section) && (
                                        <button onClick={(e) => { e.stopPropagation(); setEditingSectionIdx(index); setShowSectionEditModal(true); }} className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition" title="Edit content"><Edit2 className="w-4 h-4" /></button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingImageSection(index);
                                          setImageAiPrompt(`${section.name} banner image for ${brandName}`);
                                          setShowBannerModal(true);
                                        }}
                                        className="p-1.5 rounded-lg bg-zinc-900/60 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 transition"
                                        title="Change image"
                                      >
                                        <Image className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => moveSection(index, 'up')} disabled={index === 0} className="p-1.5 rounded-lg bg-zinc-900/60 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 transition disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                                      <button onClick={() => moveSection(index, 'down')} disabled={index === sections.length - 1} className="p-1.5 rounded-lg bg-zinc-900/60 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 transition disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                                      <button onClick={() => deleteSection(index)} className="ml-auto p-1.5 rounded-lg bg-red-500/10 text-zinc-400 hover:bg-red-500/20 hover:text-red-300 transition"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}


                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                {sections.length === 0 && (
                  <div className="text-center py-8 text-zinc-500">
                    <p>No sections yet.</p>
                  </div>
                )}
              </div>
            ) : activePanel === 'assistant' ? (
              <div className="p-3 h-full flex flex-col">
                <AIPageAssistant
                  websiteId={websiteId}
                  onApplied={handleAIApply}
                  currentPageId={currentPage?.id}
                  currentCode={htmlContent}
                  onCodeApply={async (newCode) => {
                    commitPageHtml(newCode || '', { saveHistory: true, refreshPreview: true });
                    await saveCurrentPage();
                  }}
                />
              </div>
            ) : activePanel === 'data' ? (
              <div className="h-full flex flex-col">
                <ClassSchedulePanel
                  websiteId={websiteId}
                  token={localStorage.getItem('token')}
                  onSchedulesChanged={() => {
                    setPreviewRefreshKey((key) => key + 1);
                  }}
                />
              </div>
            ) : activePanel === 'sitebrain' && (
              <div className="p-3 h-full flex flex-col">
                <SiteBrainPanel 
                  websiteId={websiteId} 
                  currentPageId={currentPage?.id}
                  currentPageSlug={currentPage?.slug}
                  onApply={async () => {
                  // Refresh pages list, app schema, and website metadata
                  try {
                    await fetchAppSchema();
                    await fetchPages();
                    // Also refresh website name/metadata
                    const wResp = await fetch(`${API_URL}/api/websites/${websiteId}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (wResp.ok) {
                      const wData = await wResp.json();
                      if (wData?.name) {
                        setWebsite(prev => (prev ? { ...prev, ...wData } : wData));
                      }
                    }
                  } catch (e) {
                    console.warn('SiteBrain onApply refresh error:', e);
                  }
                  // Reload current page's HTML from backend so preview updates
                  if (currentPage?.id) {
                    try {
                      const resp = await fetch(`${API_URL}/api/websites/${websiteId}/pages/${currentPage.id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (resp.ok) {
                        const updated = await resp.json();
                        commitPageHtml(repairBrokenUnsplashUrls(updated.html_code || ''), { refreshPreview: true });
                        setPages(prev => prev.map(p => p.id === currentPage.id ? { ...p, html_code: updated.html_code } : p));
                        setPreviewRefreshKey((key) => key + 1);
                      }
                    } catch (e) {
                      console.warn('SiteBrain page reload error:', e);
                    }
                  } else {
                    setPreviewRefreshKey((key) => key + 1);
                  }
                }} />
              </div>
            )}
          </div>
        </div>
        {/* Preview */}
        <div className="flex-1 bg-zinc-950 p-4 flex items-start justify-center overflow-auto min-h-0">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden transition-all flex-shrink-0" style={{ width: previewWidths[previewMode], minWidth: 0 }}>
            <iframe ref={iframeRef} className="w-full border-0" style={{ height: `${iframeHeight}px`, minHeight: '500px', display: 'block' }} title="Preview" />
          </div>
        </div>

        {/* App Schema Panel */}
        {showSchemaPanel && (
          <div className="w-80 bg-zinc-900 border-l border-white/10 flex flex-col flex-shrink-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-violet-400" />
                <span className="text-sm font-semibold text-white">App Schema</span>
                <span className="text-xs text-zinc-500">(read-only)</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={fetchAppSchema}
                  disabled={schemaLoading}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700"
                  data-testid="schema-refresh"
                >
                  {schemaLoading ? 'Loading…' : 'Refresh'}
                </Button>
                <Button
                  onClick={() => setShowSchemaPanel(false)}
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-white"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {schemaError && (
                <div className="mb-3 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  {schemaError}
                </div>
              )}

              <pre className="text-xs text-zinc-200 whitespace-pre-wrap break-words bg-zinc-950 border border-white/10 rounded-lg p-3">
                {appSchema ? JSON.stringify(appSchema, null, 2) : schemaLoading ? 'Loading…' : 'No schema loaded.'}
              </pre>
            </div>
          </div>
        )}

      </div>


      {/* Inline Text Toolbar over preview */}
      {toolbarPosition.visible && selectedEditorId && (
        <div
          className="fixed z-40 flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-white/10 rounded-full shadow-lg text-xs text-white pointer-events-auto"
          style={{
            top: toolbarPosition.top,
            left: toolbarPosition.left,
            transform: 'translateX(-50%)',
          }}
          onMouseDown={(e) => {
            const tag = e.target?.tagName;
            if (tag === 'SELECT' || tag === 'OPTION' || tag === 'INPUT') return;
            e.preventDefault();
          }}
        >
          <button
            type="button"
            className="px-2 py-1 rounded hover:bg-zinc-700"
            onClick={() => {
              runToolbarEdit((el) => {
                const doc = el.ownerDocument;
                const weight = doc?.defaultView?.getComputedStyle(el)?.fontWeight || el.style.fontWeight || '400';
                const isBold = parseInt(weight, 10) >= 600;
                applyEditableStyle(el, 'font-weight', isBold ? '400' : '700');
              });
            }}
          >
            B
          </button>
          <button
            type="button"
            className="px-2 py-1 rounded hover:bg-zinc-700 italic"
            onClick={() => {
              runToolbarEdit((el) => {
                const doc = el.ownerDocument;
                const isItalic = (doc?.defaultView?.getComputedStyle(el)?.fontStyle || el.style.fontStyle) === 'italic';
                applyEditableStyle(el, 'font-style', isItalic ? 'normal' : 'italic');
              });
            }}
          >
            I
          </button>
          <button
            type="button"
            className="px-2 py-1 rounded hover:bg-zinc-700 underline"
            onClick={() => {
              runToolbarEdit((el) => {
                const doc = el.ownerDocument;
                const decoration = doc?.defaultView?.getComputedStyle(el)?.textDecoration || el.style.textDecoration || '';
                applyEditableStyle(el, 'text-decoration', decoration.includes('underline') ? 'none' : 'underline');
              });
            }}
          >
            U
          </button>
          <input
            type="color"
            value={toolbarColor}
            className="w-7 h-7 rounded-full border border-zinc-700 bg-transparent cursor-pointer"
            title="Text color"
            onChange={(e) => {
              setToolbarColor(e.target.value);
              runToolbarEdit((el) => applyEditableStyle(el, 'color', e.target.value));
            }}
          />
          {toolbarIsButton && (
            <>
              <select
                className="bg-zinc-900 border border-zinc-700 rounded px-1 py-0.5 text-xs max-w-[110px]"
                value={toolbarPageSlug}
                title="Link to page"
                onChange={(e) => {
                  const slug = e.target.value;
                  setToolbarPageSlug(slug);
                  if (!slug) return;
                  const href = `?page=${slug}`;
                  setToolbarLinkUrl(href);
                  runToolbarEdit((el) => {
                    if (el.tagName === 'A') el.setAttribute('href', href);
                  });
                }}
              >
                <option value="">Page…</option>
                {linkablePages.map((p) => (
                  <option key={p.id || p.slug} value={p.slug}>
                    {p.name || p.title || p.slug}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="bg-zinc-900 border border-zinc-700 rounded px-1 py-0.5 text-xs w-28"
                value={toolbarLinkUrl}
                placeholder="Link URL"
                title="Button link URL"
                onChange={(e) => {
                  const href = e.target.value;
                  setToolbarLinkUrl(href);
                  setToolbarPageSlug(slugFromPageHref(href));
                  runToolbarEdit((el) => {
                    if (el.tagName === 'A') {
                      el.setAttribute('href', href);
                      return;
                    }
                    if (el.tagName === 'BUTTON' && href) {
                      const doc = el.ownerDocument;
                      const anchor = doc.createElement('a');
                      anchor.innerHTML = el.innerHTML;
                      anchor.className = el.className;
                      anchor.setAttribute('style', el.getAttribute('style') || '');
                      anchor.setAttribute('href', href);
                      el.replaceWith(anchor);
                    }
                  });
                }}
              />
              <input
                type="color"
                value={toolbarBgColor}
                className="w-7 h-7 rounded-full border border-zinc-700 bg-transparent cursor-pointer"
                title="Button background"
                onChange={(e) => {
                  setToolbarBgColor(e.target.value);
                  runToolbarEdit((el) => {
                    el.setAttribute('data-custom-color', 'true');
                    el.style.setProperty('background', e.target.value, 'important');
                    el.style.setProperty('background-color', e.target.value, 'important');
                  });
                }}
              />
            </>
          )}
          <select
            className="bg-zinc-900 border border-zinc-700 rounded px-1 py-0.5 text-xs"
            value={toolbarFontSize}
            onChange={(e) => {
              const next = e.target.value;
              if (!next) return;
              setToolbarFontSize(next);
              runToolbarEdit((el) => applyEditableStyle(el, 'font-size', next));
            }}
          >
            <option value="" disabled>
              Size
            </option>
            <option value="14px">14</option>
            <option value="16px">16</option>
            <option value="18px">18</option>
            <option value="20px">20</option>
            <option value="24px">24</option>
            <option value="28px">28</option>
            <option value="32px">32</option>
            <option value="36px">36</option>
            <option value="48px">48</option>
          </select>
          <select
            className="bg-zinc-900 border border-zinc-700 rounded px-1 py-0.5 text-xs max-w-[120px]"
            value={toolbarFontFamily}
            onChange={(e) => {
              const next = e.target.value;
              if (!next) return;
              setToolbarFontFamily(next);
              ensurePreviewFontLoaded(next);
              runToolbarEdit((el) => applyEditableStyle(el, 'font-family', next));
            }}
          >
            <option value="" disabled>
              Font
            </option>
            {FONT_FAMILIES.map((font) => (
              <option key={font.value} value={font.value}>
                {font.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="px-2 py-1 rounded hover:bg-zinc-700"
            onClick={() => runToolbarEdit((el) => applyEditableStyle(el, 'text-align', 'left'))}
          >
            L
          </button>
          <button
            type="button"
            className="px-2 py-1 rounded hover:bg-zinc-700"
            onClick={() => runToolbarEdit((el) => applyEditableStyle(el, 'text-align', 'center'))}
          >
            C
          </button>
          <button
            type="button"
            className="px-2 py-1 rounded hover:bg-zinc-700"
            onClick={() => runToolbarEdit((el) => applyEditableStyle(el, 'text-align', 'right'))}
          >
            R
          </button>
          <button
            type="button"
            className="px-2 py-1 rounded hover:bg-zinc-700"
            onClick={() => runToolbarEdit((el) => applyEditableStyle(el, 'text-align', 'justify'))}
          >
            J
          </button>
          <button
            type="button"
            className="px-2 py-1 rounded bg-violet-600 hover:bg-violet-500"
            onClick={() => setShowAITextModal(true)}
          >
            AI
          </button>
          <button
            type="button"
            className="ml-1 px-2 py-1 rounded-full hover:bg-zinc-800 text-zinc-400"
            onClick={() => {
              setToolbarPosition({ visible: false, top: 0, left: 0 });
              setSelectedTextElement(null);
              setSelectedEditorId('');
              selectedEditorIdRef.current = '';
              setToolbarIsButton(false);
              setToolbarLinkUrl('');
              setToolbarPageSlug('');
            }}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Design Panel Modal */}
      {showDesignPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Palette className="w-6 h-6 text-violet-400" />
                <h3 className="text-xl font-bold">Design Settings</h3>
              </div>
              <button onClick={() => setShowDesignPanel(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-6">
              {/* Colors */}
              <div>
                <label className="block text-sm font-medium mb-3">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-12 h-12 rounded-lg cursor-pointer border-2 border-zinc-700" />
                  <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="flex-1 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="w-12 h-12 rounded-lg cursor-pointer border-2 border-zinc-700" />
                  <input type="text" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="flex-1 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white" />
                </div>
              </div>
              
              {/* Font */}
              <div>
                <label className="block text-sm font-medium mb-3">Font Family</label>
                <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
                  {FONT_FAMILIES.map(font => <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.name}</option>)}
                </select>
              </div>
              
              
              {/* Color Presets */}
              <div>
                <label className="block text-sm font-medium mb-3">Color Presets</label>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { primary: '#8b5cf6', secondary: '#10b981' },
                    { primary: '#3b82f6', secondary: '#f59e0b' },
                    { primary: '#ef4444', secondary: '#8b5cf6' },
                    { primary: '#10b981', secondary: '#3b82f6' },
                    { primary: '#f59e0b', secondary: '#ef4444' },
                    { primary: '#ec4899', secondary: '#8b5cf6' },
                    { primary: '#6366f1', secondary: '#14b8a6' },
                    { primary: '#84cc16', secondary: '#0ea5e9' },
                    { primary: '#f97316', secondary: '#6366f1' },
                    { primary: '#0ea5e9', secondary: '#f97316' },
                  ].map((preset, i) => (
                    <button key={i} onClick={() => { setPrimaryColor(preset.primary); setSecondaryColor(preset.secondary); }} className="h-10 rounded-lg overflow-hidden flex hover:ring-2 hover:ring-white/50">
                      <div style={{ background: preset.primary }} className="flex-1" />
                      <div style={{ background: preset.secondary }} className="flex-1" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={() => { handleSave(); setShowDesignPanel(false); }} className="w-full bg-violet-500">Apply Changes</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner Image Modal */}
      {showBannerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-4xl p-6 border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Image className="w-6 h-6 text-violet-400" />
                <h3 className="text-xl font-bold">Select Banner Image</h3>
              </div>
              <button onClick={() => { setShowBannerModal(false); setEditingImageSection(null); }} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              {bannerCategories.map(cat => (
                <button key={cat} onClick={() => setBannerCategory(cat)} className={`px-4 py-2 rounded-full text-sm font-medium ${bannerCategory === cat ? 'bg-violet-500 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>
            
            {/* Image Grid */}
            <div className="grid grid-cols-4 gap-4">
              {BANNER_IMAGES.filter(b => bannerCategory === 'all' || b.category === bannerCategory).map(banner => (
                <button key={banner.id} onClick={() => selectBannerImage(banner.url)} className="aspect-video rounded-lg overflow-hidden hover:ring-2 hover:ring-violet-500 transition-all">
                  <img src={banner.url} alt={banner.category} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            
            {/* Custom URL */}
            <div className="mt-6 p-4 bg-zinc-800 rounded-lg">
              <label className="block text-sm font-medium mb-2">Use image path / URL</label>
              <div className="flex gap-2">
                <input type="url" placeholder="https://..." value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="flex-1 px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white" />
                <Button onClick={() => { if (imageUrl) selectBannerImage(imageUrl); }} className="bg-violet-500">Apply</Button>
              </div>
            </div>

            <div className="mt-4 p-4 bg-zinc-800 rounded-lg">
              <label className="block text-sm font-medium mb-2">Upload image</label>
              <input
                ref={imageUploadRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadBannerImage(e.target.files?.[0])}
              />
              <Button type="button" variant="outline" className="border-zinc-600" onClick={() => imageUploadRef.current?.click()}>
                Upload from device
              </Button>
              <p className="mt-2 text-xs text-zinc-500">Uploaded images are embedded in the current template preview.</p>
            </div>

            <div className="mt-4 p-4 bg-zinc-800 rounded-lg border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <label className="text-sm font-medium">Generate with AI</label>
              </div>
              <textarea
                placeholder="e.g. Fresh food hero banner with organic groceries on a modern market counter"
                value={imageAiPrompt}
                onChange={(e) => setImageAiPrompt(e.target.value)}
                className="w-full h-24 px-4 py-3 bg-zinc-700 border border-zinc-600 rounded-lg text-white resize-none"
              />
              <div className="mt-3 flex flex-wrap gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="border-zinc-600"
                  onClick={() => setImageAiPrompt(sections[editingImageSection]?.name ? `${sections[editingImageSection].name} banner image for ${brandName}` : `Banner image for ${brandName}`)}
                >
                  Use section name
                </Button>
                <Button
                  type="button"
                  className="bg-violet-500"
                  disabled={imageAiGenerating || !imageAiPrompt.trim()}
                  onClick={async () => {
                    try {
                      setImageAiGenerating(true);
                      const res = await fetch(`${API_URL}/api/ai/generate-image`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ prompt: imageAiPrompt, size: '1536x1024' }),
                      });
                      if (!res.ok) {
                        let msg = 'AI image generation failed';
                        try {
                          const err = await res.json();
                          msg = err.detail || err.message || msg;
                        } catch { /* ignore */ }
                        toast.error(msg);
                        return;
                      }
                      const data = await res.json();
                      const generatedUrl = data.image_url;
                      if (!generatedUrl) {
                        toast.error('No AI image returned');
                        return;
                      }
                      setImageUrl(generatedUrl);
                      await selectBannerImage(generatedUrl);
                    } catch (err) {
                      console.error(err);
                      toast.error('AI image error');
                    } finally {
                      setImageAiGenerating(false);
                    }
                  }}
                >
                  {imageAiGenerating ? 'Generating…' : 'Generate AI image'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Edit Modal (FAQ, Video, Audio, Slider, Footer) */}
      {showSectionEditModal && editingSectionIdx != null && sections[editingSectionIdx] && (() => {
        const editSection = sections[editingSectionIdx];
        const editType = getSectionEditType(editSection);
        const parser = new DOMParser();
        const doc = parser.parseFromString(editSection.html, 'text/html');
        const root = doc.body.firstElementChild || doc.body;

        if (editType === 'faq') {
          const details = Array.from(root.querySelectorAll('details'));
          const faqItems = details.map((d) => ({
            question: (d.querySelector('summary') || {}).textContent || '',
            answer: (d.querySelector('p') || {}).textContent || '',
          }));
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
              <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl p-6 border border-white/10 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Edit FAQ Items</h3>
                  <button onClick={() => setShowSectionEditModal(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div id="faq-edit-items" className="space-y-4">
                  {faqItems.map((item, i) => (
                    <div key={i} className="p-4 bg-zinc-800 rounded-xl border border-white/10">
                      <label className="text-xs text-zinc-400 mb-1 block">Question {i + 1}</label>
                      <input defaultValue={item.question} data-faq-q={i} className="w-full mb-2 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                      <label className="text-xs text-zinc-400 mb-1 block">Answer</label>
                      <textarea defaultValue={item.answer} data-faq-a={i} rows={3} className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm resize-none" />
                      <button type="button" onClick={() => {
                        details[i]?.remove();
                        const newHtml = root.outerHTML;
                        updateSectionContent(editingSectionIdx, newHtml);
                        setShowSectionEditModal(false);
                        setTimeout(() => { setEditingSectionIdx(editingSectionIdx); setShowSectionEditModal(true); }, 50);
                      }} className="mt-2 text-xs text-red-400 hover:text-red-300">Remove this item</button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="border-zinc-600 text-zinc-200" onClick={() => {
                    const container = root.querySelector('div[style]') || root;
                    const newDetail = doc.createElement('details');
                    newDetail.setAttribute('style', 'background: #f8fafc; border-radius: 12px; padding: 20px; margin-bottom: 15px;');
                    newDetail.innerHTML = '<summary style="font-weight: 600; cursor: pointer; color: #1e293b;">New Question</summary><p style="margin-top: 15px; color: #64748b; line-height: 1.7;">Answer goes here...</p>';
                    const inner = container.querySelector('div') || container;
                    inner.appendChild(newDetail);
                    updateSectionContent(editingSectionIdx, root.outerHTML);
                    setShowSectionEditModal(false);
                    setTimeout(() => { setEditingSectionIdx(editingSectionIdx); setShowSectionEditModal(true); }, 50);
                  }}>
                    <Plus className="w-4 h-4 mr-1" /> Add FAQ Item
                  </Button>
                  <Button className="bg-violet-500 ml-auto" onClick={() => {
                    const modalEl = document.getElementById('faq-edit-items');
                    if (modalEl) {
                      const qInputs = modalEl.querySelectorAll('[data-faq-q]');
                      const aInputs = modalEl.querySelectorAll('[data-faq-a]');
                      qInputs.forEach((inp, i) => {
                        if (details[i]) {
                          const summary = details[i].querySelector('summary');
                          const p = details[i].querySelector('p');
                          if (summary) summary.textContent = inp.value;
                          if (p) p.textContent = inp.value ? (aInputs[i]?.value || '') : '';
                        }
                      });
                    }
                    updateSectionContent(editingSectionIdx, root.outerHTML);
                    setShowSectionEditModal(false);
                    toast.success('FAQ updated');
                  }}>Save Changes</Button>
                </div>
              </div>
            </div>
          );
        }

        if (editType === 'video' || editType === 'video-gallery') {
          const iframe = root.querySelector('iframe');
          const currentSrc = iframe ? iframe.getAttribute('src') : '';
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
              <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Edit Video</h3>
                  <button onClick={() => setShowSectionEditModal(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <p className="text-sm text-zinc-400 mb-4">Paste a YouTube or Vimeo embed URL, or a direct video URL.</p>
                <input id="video-url-input" defaultValue={currentSrc} placeholder="https://www.youtube.com/embed/..." className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white mb-4" />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" className="border-zinc-600 text-zinc-200" onClick={() => setShowSectionEditModal(false)}>Cancel</Button>
                  <Button className="bg-violet-500" onClick={() => {
                    const input = document.getElementById('video-url-input');
                    let url = input?.value?.trim() || '';
                    if (url && !url.includes('/embed') && url.includes('youtube.com/watch')) {
                      const vid = new URL(url).searchParams.get('v');
                      if (vid) url = `https://www.youtube.com/embed/${vid}`;
                    }
                    if (url && url.includes('youtu.be/')) {
                      const vid = url.split('youtu.be/')[1]?.split('?')[0];
                      if (vid) url = `https://www.youtube.com/embed/${vid}`;
                    }
                    if (iframe && url) {
                      iframe.setAttribute('src', url);
                      updateSectionContent(editingSectionIdx, root.outerHTML);
                      toast.success('Video updated');
                    } else if (!iframe && url) {
                      const container = root.querySelector('div') || root;
                      container.innerHTML = `<div style="max-width: 900px; margin: 0 auto; position: relative; aspect-ratio: 16/9; border-radius: 20px; overflow: hidden; box-shadow: 0 25px 80px rgba(0,0,0,0.5);"><iframe width="100%" height="100%" src="${url}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position: absolute; top: 0; left: 0;"></iframe></div>`;
                      updateSectionContent(editingSectionIdx, root.outerHTML);
                      toast.success('Video added');
                    }
                    setShowSectionEditModal(false);
                  }}>Apply</Button>
                </div>
              </div>
            </div>
          );
        }

        if (editType === 'audio') {
          const audioEl = root.querySelector('audio');
          const source = audioEl?.querySelector('source');
          const currentSrc = source?.getAttribute('src') || audioEl?.getAttribute('src') || '';
          const titleEl = root.querySelector('h3');
          const artistEl = root.querySelector('p');
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
              <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Edit Audio Player</h3>
                  <button onClick={() => setShowSectionEditModal(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Track Title</label>
                    <input id="audio-title-input" defaultValue={titleEl?.textContent || ''} placeholder="Track Title" className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Artist Name</label>
                    <input id="audio-artist-input" defaultValue={artistEl?.textContent || ''} placeholder="Artist Name" className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Audio URL (mp3, wav, ogg)</label>
                    <input id="audio-url-input" defaultValue={currentSrc} placeholder="https://example.com/track.mp3" className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white" />
                  </div>
                </div>
                <div className="flex gap-2 justify-end mt-4">
                  <Button variant="outline" className="border-zinc-600 text-zinc-200" onClick={() => setShowSectionEditModal(false)}>Cancel</Button>
                  <Button className="bg-violet-500" onClick={() => {
                    const url = document.getElementById('audio-url-input')?.value?.trim() || '';
                    const title = document.getElementById('audio-title-input')?.value?.trim() || 'Track Title';
                    const artist = document.getElementById('audio-artist-input')?.value?.trim() || 'Artist';
                    if (titleEl) titleEl.textContent = title;
                    if (artistEl) artistEl.textContent = artist;
                    if (source) source.setAttribute('src', url);
                    else if (audioEl) audioEl.setAttribute('src', url);
                    updateSectionContent(editingSectionIdx, root.outerHTML);
                    setShowSectionEditModal(false);
                    toast.success('Audio player updated');
                  }}>Apply</Button>
                </div>
              </div>
            </div>
          );
        }

        if (editType === 'gallery') {
          const galleryInstanceId = extractInstanceIdFromHtml(editSection.html);
          const galleryInst = widgetInstances.find((row) => row.instance_id === galleryInstanceId);
          const galleryCfg = parseWidgetInstanceConfig(galleryInst?.config_json);
          const galleryRegisterPayload = {
            instance_id: galleryInstanceId,
            widget_type: 'gallery',
            widget_name: editSection.name || 'Gallery',
            page_slug: pages.find((p) => p.id === currentPage)?.slug || '',
          };
          const galleryItems = Array.isArray(galleryCfg.items) && galleryCfg.items.length
            ? galleryCfg.items
            : Array.from(root.querySelectorAll('[data-gallery-grid] img, [data-widget-content-grid] img, img')).map((img, i) => ({
              image: img.getAttribute('src') || '',
              title: img.getAttribute('alt') || `Image ${i + 1}`,
              category: '',
            })).filter((item) => item.image);
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
              <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl p-6 border border-white/10 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Edit Gallery</h3>
                  <button onClick={() => setShowSectionEditModal(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div id="gallery-edit-items" className="space-y-4">
                  {galleryItems.map((item, i) => (
                    <div key={i} className="p-4 bg-zinc-800 rounded-xl border border-white/10">
                      <label className="text-xs text-zinc-400 mb-1 block">Image {i + 1} URL</label>
                      <input defaultValue={item.image || item.url || ''} data-gallery-img={i} className="w-full mb-2 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                      <label className="text-xs text-zinc-400 mb-1 block">Title</label>
                      <input defaultValue={item.title || ''} data-gallery-title={i} className="w-full mb-2 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                      <label className="text-xs text-zinc-400 mb-1 block">Category</label>
                      <input defaultValue={item.category || ''} data-gallery-cat={i} className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="border-zinc-600 text-zinc-200" onClick={() => {
                    const nextItems = [...galleryItems, { image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800', title: 'New Item', category: '' }];
                    patchWidgetInstanceConfig(
                      API_URL, token, websiteId, galleryInstanceId, { items: nextItems }, widgetInstances, galleryRegisterPayload,
                    ).then((item) => {
                      setWidgetInstances((prev) => prev.map((row) => (
                        row.instance_id === galleryInstanceId
                          ? { ...row, config_json: item?.config_json || JSON.stringify({ ...galleryCfg, items: nextItems }) }
                          : row
                      )));
                      setShowSectionEditModal(false);
                      setTimeout(() => { setEditingSectionIdx(editingSectionIdx); setShowSectionEditModal(true); }, 50);
                      setPreviewRefreshKey((key) => key + 1);
                    });
                  }}>
                    <Plus className="w-4 h-4 mr-1" /> Add Image
                  </Button>
                  <Button className="bg-violet-500 ml-auto" onClick={async () => {
                    const modalEl = document.getElementById('gallery-edit-items');
                    const nextItems = galleryItems.map((item, i) => {
                      const imgInput = modalEl?.querySelector(`[data-gallery-img="${i}"]`);
                      const titleInput = modalEl?.querySelector(`[data-gallery-title="${i}"]`);
                      const catInput = modalEl?.querySelector(`[data-gallery-cat="${i}"]`);
                      return {
                        ...item,
                        image: imgInput?.value?.trim() || item.image || item.url || '',
                        title: titleInput?.value?.trim() || item.title || '',
                        category: catInput?.value?.trim() || item.category || '',
                      };
                    });
                    const item = await patchWidgetInstanceConfig(
                      API_URL, token, websiteId, galleryInstanceId, { items: nextItems }, widgetInstances, galleryRegisterPayload,
                    );
                    setWidgetInstances((prev) => prev.map((row) => (
                      row.instance_id === galleryInstanceId
                        ? { ...row, config_json: item?.config_json || JSON.stringify({ ...galleryCfg, items: nextItems }) }
                        : row
                    )));
                    setShowSectionEditModal(false);
                    setPreviewRefreshKey((key) => key + 1);
                    toast.success('Gallery updated');
                  }}>Save Changes</Button>
                </div>
              </div>
            </div>
          );
        }

        if (editType === 'slider') {
          const sliderInstanceId = extractInstanceIdFromHtml(editSection.html);
          const sliderInst = widgetInstances.find((row) => row.instance_id === sliderInstanceId);
          const sliderCfg = parseWidgetInstanceConfig(sliderInst?.config_json);
          const track = root.querySelector('[data-slider-track]');
          const domSlides = track ? Array.from(track.children) : [];
          const slideData = Array.isArray(sliderCfg.images) && sliderCfg.images.length
            ? sliderCfg.images.map((slide) => ({
              image: slide.url || slide.image || '',
              title: slide.caption || slide.title || '',
              subtitle: slide.alt || '',
            }))
            : domSlides.map((s) => {
              const img = s.querySelector('img');
              const h2 = s.querySelector('h2');
              const p = s.querySelector('p');
              return { image: img?.getAttribute('src') || '', title: h2?.textContent || '', subtitle: p?.textContent || '' };
            });
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
              <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl p-6 border border-white/10 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Edit Slider</h3>
                  <button onClick={() => setShowSectionEditModal(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div id="slider-edit-items" className="space-y-4">
                  {slideData.map((slide, i) => (
                    <div key={i} className="p-4 bg-zinc-800 rounded-xl border border-white/10">
                      <label className="text-xs text-zinc-400 mb-1 block">Slide {i + 1} Image URL</label>
                      <input defaultValue={slide.image} data-slide-img={i} className="w-full mb-2 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                      <label className="text-xs text-zinc-400 mb-1 block">Title</label>
                      <input defaultValue={slide.title} data-slide-title={i} className="w-full mb-2 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                      <label className="text-xs text-zinc-400 mb-1 block">Subtitle</label>
                      <input defaultValue={slide.subtitle} data-slide-sub={i} className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="border-zinc-600 text-zinc-200" onClick={() => {
                    const nextImages = [...(sliderCfg.images || slideData.map((s) => ({ url: s.image, caption: s.title, alt: s.subtitle }))), {
                      url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920',
                      caption: 'New Slide',
                      alt: 'New Slide',
                    }];
                    patchWidgetInstanceConfig(API_URL, token, websiteId, sliderInstanceId, { images: nextImages }, widgetInstances).then((item) => {
                      setWidgetInstances((prev) => prev.map((row) => (
                        row.instance_id === sliderInstanceId
                          ? { ...row, config_json: item?.config_json || JSON.stringify({ ...sliderCfg, images: nextImages }) }
                          : row
                      )));
                      setShowSectionEditModal(false);
                      setTimeout(() => { setEditingSectionIdx(editingSectionIdx); setShowSectionEditModal(true); }, 50);
                      setPreviewRefreshKey((key) => key + 1);
                    });
                  }}>
                    <Plus className="w-4 h-4 mr-1" /> Add Slide
                  </Button>
                  <Button className="bg-violet-500 ml-auto" onClick={async () => {
                    const modalEl = document.getElementById('slider-edit-items');
                    const nextImages = slideData.map((slide, i) => {
                      const imgInput = modalEl?.querySelector(`[data-slide-img="${i}"]`);
                      const titleInput = modalEl?.querySelector(`[data-slide-title="${i}"]`);
                      const subInput = modalEl?.querySelector(`[data-slide-sub="${i}"]`);
                      return {
                        url: imgInput?.value?.trim() || slide.image || '',
                        caption: titleInput?.value?.trim() || slide.title || '',
                        alt: subInput?.value?.trim() || slide.subtitle || slide.title || '',
                      };
                    });
                    const item = await patchWidgetInstanceConfig(API_URL, token, websiteId, sliderInstanceId, { images: nextImages }, widgetInstances);
                    setWidgetInstances((prev) => prev.map((row) => (
                      row.instance_id === sliderInstanceId
                        ? { ...row, config_json: item?.config_json || JSON.stringify({ ...sliderCfg, images: nextImages }) }
                        : row
                    )));
                    setShowSectionEditModal(false);
                    setPreviewRefreshKey((key) => key + 1);
                    toast.success('Slider updated');
                  }}>Save Changes</Button>
                </div>
              </div>
            </div>
          );
        }

        if (editType === 'footer') {
          const brandBlock = root.querySelector('.footer-brand') || root.querySelector('[style*="grid"] > div:first-child, .footer-grid > div:first-child');
          const brandTitleEl = brandBlock?.querySelector('h3') || brandBlock?.querySelector('div[style*="font-size"]');
          const brandDescEl = brandBlock?.querySelector('p');
          const brandTitle = brandTitleEl?.textContent?.trim() || '';
          const brandDesc = brandDescEl?.textContent?.trim() || '';

          let colEls = Array.from(root.querySelectorAll('.footer-col'));
          if (!colEls.length) {
            const grid = root.querySelector('.footer-grid, [style*="grid-template-columns"]');
            if (grid) colEls = Array.from(grid.children).slice(1);
          }

          const columns = colEls.map((col) => {
            const titleEl = col.querySelector('h4, h3') || col.querySelector('div[style*="font-weight"]');
            const title = titleEl?.textContent?.trim() || '';
            const items = [];
            col.querySelectorAll('ul li').forEach((li) => {
              const a = li.querySelector('a');
              if (a) {
                items.push({ label: a.textContent.trim(), href: a.getAttribute('href') || '#', isLink: true, el: a, liEl: li });
              } else {
                items.push({ label: li.textContent.trim(), href: '', isLink: false, el: li, liEl: li });
              }
            });
            if (!items.length) {
              col.querySelectorAll('a').forEach((a) => {
                items.push({ label: a.textContent.trim(), href: a.getAttribute('href') || '#', isLink: true, el: a, liEl: null });
              });
            }
            return { title, titleEl, items, colEl: col };
          });

          const copyrightEl = findFooterCopyrightEl(root);
          const copyright = copyrightEl?.textContent?.trim() || '© 2025 Snagcart. All rights reserved.';

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
              <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl p-6 border border-white/10 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Edit Footer</h3>
                  <button onClick={() => setShowSectionEditModal(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div id="footer-edit-form" className="space-y-5">
                  <div className="p-4 bg-zinc-800 rounded-xl border border-white/10 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Brand</p>
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">Site name</label>
                      <input id="footer-brand-title" defaultValue={brandTitle} className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">Description</label>
                      <textarea id="footer-brand-desc" defaultValue={brandDesc} rows={2} className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm resize-none" />
                    </div>
                  </div>

                  {columns.map((col, colIdx) => (
                    <div key={colIdx} className="p-4 bg-zinc-800 rounded-xl border border-white/10">
                      <label className="text-xs text-zinc-400 mb-1 block">Column {colIdx + 1} title</label>
                      <input defaultValue={col.title} data-footer-col-title={colIdx} className="w-full mb-3 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                      <div className="space-y-2">
                        {col.items.map((item, linkIdx) => (
                          <div key={linkIdx} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input defaultValue={item.label} data-footer-link-label={`${colIdx}-${linkIdx}`} placeholder="Link label" className="px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                            {item.isLink ? (
                              <input defaultValue={item.href} data-footer-link-href={`${colIdx}-${linkIdx}`} placeholder="URL" className="px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                            ) : (
                              <input disabled value="Text only (contact info)" className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-500 text-sm" />
                            )}
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={() => {
                        const ul = col.colEl.querySelector('ul');
                        if (ul) {
                          const li = doc.createElement('li');
                          const a = doc.createElement('a');
                          a.setAttribute('href', '#');
                          a.textContent = 'New Link';
                          li.appendChild(a);
                          ul.appendChild(li);
                        } else {
                          const linkWrap = col.colEl.querySelector('div[style*="flex"]') || col.colEl;
                          const sample = linkWrap.querySelector('a');
                          const a = doc.createElement('a');
                          a.setAttribute('href', '#');
                          a.textContent = 'New Link';
                          if (sample?.getAttribute('style')) a.setAttribute('style', sample.getAttribute('style'));
                          linkWrap.appendChild(a);
                        }
                        updateSectionContent(editingSectionIdx, root.outerHTML);
                        setShowSectionEditModal(false);
                        setTimeout(() => { setEditingSectionIdx(editingSectionIdx); setShowSectionEditModal(true); }, 50);
                      }} className="mt-3 text-xs text-violet-300 hover:text-violet-200">
                        <Plus className="w-3 h-3 inline mr-1" /> Add link
                      </button>
                    </div>
                  ))}

                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Copyright line</label>
                    <input id="footer-copyright" defaultValue={copyright} placeholder="© 2025 Snagcart. All rights reserved." className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white text-sm" />
                  </div>
                </div>
                <div className="flex gap-2 mt-4 justify-end">
                  <Button variant="outline" className="border-zinc-600 text-zinc-200" onClick={() => setShowSectionEditModal(false)}>Cancel</Button>
                  <Button className="bg-violet-500" onClick={() => {
                    const modalEl = document.getElementById('footer-edit-form');
                    if (brandTitleEl && modalEl) {
                      const titleInput = document.getElementById('footer-brand-title');
                      if (titleInput) brandTitleEl.textContent = titleInput.value;
                    }
                    if (brandDescEl && modalEl) {
                      const descInput = document.getElementById('footer-brand-desc');
                      if (descInput) brandDescEl.textContent = descInput.value;
                    }
                    columns.forEach((col, colIdx) => {
                      const titleInput = modalEl?.querySelector(`[data-footer-col-title="${colIdx}"]`);
                      if (col.titleEl && titleInput) col.titleEl.textContent = titleInput.value;
                      col.items.forEach((item, linkIdx) => {
                        const labelInput = modalEl?.querySelector(`[data-footer-link-label="${colIdx}-${linkIdx}"]`);
                        const hrefInput = modalEl?.querySelector(`[data-footer-link-href="${colIdx}-${linkIdx}"]`);
                        if (!labelInput) return;
                        if (item.isLink && item.el) {
                          item.el.textContent = labelInput.value;
                          if (hrefInput) item.el.setAttribute('href', hrefInput.value || '#');
                        } else if (item.el) {
                          item.el.textContent = labelInput.value;
                        }
                      });
                    });
                    const copyInput = document.getElementById('footer-copyright');
                    if (copyInput) {
                      let copyEl = findFooterCopyrightEl(root);
                      if (copyEl) {
                        copyEl.textContent = copyInput.value;
                        copyEl.setAttribute('data-footer-copyright', 'true');
                      } else {
                        const bottom = doc.createElement('div');
                        bottom.className = 'footer-bottom';
                        bottom.style.cssText = 'border-top:1px solid #1e293b;margin-top:40px;padding-top:25px;text-align:center;color:#64748b;';
                        const line = doc.createElement('span');
                        line.setAttribute('data-footer-copyright', 'true');
                        line.textContent = copyInput.value;
                        bottom.appendChild(line);
                        root.appendChild(bottom);
                      }
                    }
                    updateSectionContent(editingSectionIdx, root.outerHTML);
                    setShowSectionEditModal(false);
                    toast.success('Footer updated');
                  }}>Save Changes</Button>
                </div>
              </div>
            </div>
          );
        }

        return null;
      })()}

      {/* AI Text Modal */}
      {showAITextModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-md p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-400" />
                AI Write
              </h3>
              <button
                type="button"
                onClick={() => setShowAITextModal(false)}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-zinc-400 mb-3">
              Describe what you want this text to say. We&apos;ll generate a suggestion.
            </p>
            <textarea
              className="w-full h-28 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white mb-4 resize-none"
              placeholder="e.g. A catchy headline about our AI website builder"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
            />
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-zinc-700"
                onClick={() => setShowAITextModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-violet-500"
                disabled={aiGenerating || !aiPrompt.trim() || !selectedEditorId}
                onClick={async () => {
                  const el = resolveEditableElement();
                  if (!aiPrompt.trim() || !el) return;
                  try {
                    setAiGenerating(true);
                    const res = await fetch(`${API_URL}/api/ai/generate-text`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({ prompt: aiPrompt, type: 'description', tone: 'friendly' }),
                    });
                    if (!res.ok) {
                      toast.error('AI text generation failed');
                    } else {
                      const data = await res.json();
                      el.textContent = data.text || el.textContent;
                      updateHtmlFromIframe();
                      setShowAITextModal(false);
                      setAiPrompt('');
                    }
                  } catch (err) {
                    console.error(err);
                    toast.error('AI text error');
                  } finally {
                    setAiGenerating(false);
                  }
                }}
              >
                {aiGenerating ? 'Generating...' : 'Generate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Page Settings Modal */}
      {showPageSettings && editingPage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-md p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-violet-400" />
                <h3 className="text-xl font-bold">Page Settings</h3>
              </div>
              <button onClick={() => setShowPageSettings(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Page Name</label>
                <input type="text" value={editPageName} onChange={(e) => setEditPageName(e.target.value)} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">URL Slug</label>
                <input type="text" value={editPageSlug} onChange={(e) => setEditPageSlug(e.target.value)} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select value={editPageCategory} onChange={(e) => setEditPageCategory(e.target.value)} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
                  {PAGE_CATEGORIES.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Navigation className="w-5 h-5 text-zinc-400" />
                  <div>
                    <p className="font-medium">Show in Navigation</p>
                    <p className="text-xs text-zinc-500">Display in website menu</p>
                  </div>
                </div>
                <button onClick={() => setEditPageNavVisible(!editPageNavVisible)} className={`w-12 h-6 rounded-full transition-colors ${editPageNavVisible ? 'bg-violet-500' : 'bg-zinc-600'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${editPageNavVisible ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {!editingPage.is_home && (
                <button onClick={() => { setAsHomePage(editingPage.id); setShowPageSettings(false); }} className="w-full p-3 bg-amber-500/20 text-amber-400 rounded-lg flex items-center justify-center gap-2 hover:bg-amber-500/30">
                  <Home className="w-4 h-4" />Set as Home Page
                </button>
              )}
              <div className="pt-4 flex gap-3">
                <Button onClick={() => setShowPageSettings(false)} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={savePageSettings} className="flex-1 bg-violet-500">Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Rocket className="w-6 h-6 text-violet-400" />
                <h3 className="text-xl font-bold">Publish Website</h3>
              </div>
              <button onClick={() => setShowPublishModal(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Subdomain</label>
                <div className="flex items-center">
                  <input type="text" value={customSubdomain} onChange={(e) => setCustomSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="my-site" className="flex-1 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-l-lg text-white" />
                  <span className="px-4 py-3 bg-zinc-700 border border-zinc-600 rounded-r-lg text-zinc-400">.snagcart.app</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Custom Domain (Optional)</label>
                <select
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white mb-2"
                >
                  <option value="">{domainsLoading ? 'Loading domains…' : 'Select a domain from your portfolio'}</option>
                  {websiteDomains.map((row) => {
                    const domainName = row.domain_name || row.domain || row.name || '';
                    if (!domainName) return null;
                    const attached = row.website_id || row.attached_website_id;
                    const suffix = attached === websiteId
                      ? ' (attached to this site)'
                      : attached
                        ? ` (attached to ${row.website_name || 'another site'})`
                        : ' (available)';
                    return (
                      <option key={domainName} value={domainName}>
                        {domainName}{suffix}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                  placeholder="Or enter a domain manually"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                />
                <div className="flex items-center justify-between mt-2 gap-3">
                  <p className="text-xs text-zinc-500">
                    Domains load from your portfolio. Attached domains for this site appear first.
                  </p>
                  <a
                    href={`/domains-hosting?website=${websiteId}`}
                    className="text-xs text-violet-400 hover:text-violet-300 whitespace-nowrap"
                  >
                    Attach domain →
                  </a>
                </div>
                {website?.domain_status && website.domain_status !== 'none' && (
                  <p className="text-xs text-emerald-400 mt-2">Domain status: {website.domain_status}</p>
                )}
              </div>
              <div className="h-px bg-zinc-700 my-4" />
              <div>
                <label className="block text-sm font-medium mb-2">SEO Title</label>
                <input type="text" value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">SEO Description</label>
                <textarea value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} rows={3} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none" />
              </div>
              <div className="pt-4 flex flex-col gap-3">
                <div className="flex gap-3">
                  <Button onClick={() => setShowPublishModal(false)} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                  <Button onClick={handlePublish} disabled={publishing} className="flex-1 bg-violet-500">{publishing ? 'Publishing...' : 'Publish Now'}</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Edit Modal */}
      {showImageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-md p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Replace Image</h3>
              <button onClick={() => setShowImageModal(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              />
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Upload from your computer</label>
                <input
                  type="file"
                  accept="image/*"
                  className="w-full text-xs text-zinc-400"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setImageFile(file);
                  }}
                />
              </div>
              <div className="space-y-2 p-3 bg-zinc-900/60 rounded-lg border border-zinc-800/80">
                <p className="text-xs text-zinc-400">Or create an image with AI</p>
                <textarea
                  className="w-full h-16 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-white resize-none"
                  placeholder="e.g. Product shot on a clean white background"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  className="w-full bg-violet-600 hover:bg-violet-500"
                  disabled={aiGenerating || !aiPrompt.trim()}
                  onClick={async () => {
                    try {
                      setAiGenerating(true);
                      const res = await fetch(`${API_URL}/api/ai/generate-image`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ prompt: aiPrompt, size: '1024x1024' }),
                      });
                      if (!res.ok) {
                        toast.error('AI image generation failed');
                      } else {
                        const data = await res.json();
                        const url = resolveMediaUrl(data.image_url, API_URL);
                        if (url) {
                          setImageUrl(url);
                          await handleImageUpdate(url);
                          setAiPrompt('');
                        }
                      }
                    } catch (err) {
                      console.error(err);
                      toast.error('AI image error');
                    } finally {
                      setAiGenerating(false);
                    }
                  }}
                >
                  {aiGenerating ? 'Generating…' : 'AI Image'}
                </Button>
              </div>
              <Button
                onClick={() => {
                  setShowBannerModal(true);
                  setShowImageModal(false);
                }}
                variant="outline"
                className="w-full border-zinc-700"
              >
                Browse Image Library
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowImageModal(false)}
                  variant="outline"
                  className="flex-1 border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      if (imageFile) {
                        const formData = new FormData();
                        formData.append('file', imageFile);
                        formData.append('name', imageFile.name);
                        const res = await fetch(`${API_URL}/api/websites/${websiteId}/media/upload`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData,
                        });
                        if (!res.ok) {
                          toast.error('Image upload failed');
                          return;
                        }
                        const data = await res.json();
                        const uploadedUrl = resolveMediaUrl(data.url, API_URL);
                        await handleImageUpdate(uploadedUrl);
                      } else {
                        await handleImageUpdate(resolveMediaUrl(imageUrl, API_URL));
                      }
                    } catch (err) {
                      console.error(err);
                      toast.error('Image update failed');
                    }
                  }}
                  className="flex-1 bg-violet-500"
                >
                  Update
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Section Modal */}
      {showAddSectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Add New Section</h3>
              <button onClick={() => setShowAddSectionModal(false)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              {[
                { name: 'Hero Section', desc: 'Full-width hero with background image and CTA' },
                { name: 'Features Section', desc: 'Grid of feature cards with icons' },
                { name: 'Testimonials', desc: 'Customer testimonials and reviews' },
                { name: 'Pricing Section', desc: 'Pricing plans or packages' },
                { name: 'FAQ Section', desc: 'Frequently asked questions' },
                { name: 'Team Section', desc: 'Team members or staff profiles' },
                { name: 'Gallery Section', desc: 'Image gallery or portfolio' },
                { name: 'Stats Section', desc: 'Numbers and statistics highlight' },
                { name: 'CTA Section', desc: 'Call to action with button' },
                { name: 'Video Section', desc: 'Video embed section' },
                { name: 'Map Section', desc: 'Location or map embed' },
                { name: 'Custom Section', desc: 'Empty section to customize' },
              ].map((sectionType) => (
                <button
                  key={sectionType.name}
                  onClick={() => addBlankSection(sectionType)}
                  className="w-full p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-left transition-colors"
                >
                  <div className="font-medium text-white">{sectionType.name}</div>
                  <div className="text-sm text-zinc-400">{sectionType.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditor;
